import { useState, useEffect, useRef } from "react";
import "@/App.css";
import axios from "axios";
import { MagnifyingGlass, CheckCircle, XCircle, Truck, PlugsConnected, Shield, WarningCircle, ArrowsClockwise, LockKey, CircleNotch, Info, Paperclip, Sparkle } from "@phosphor-icons/react";
import { format, parseISO } from "date-fns";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { StatusText } from "@/components/StatusText";
import { DateField } from "@/components/DateField";
import { Switch } from "@/components/ui/switch";
import { toast } from "@/components/ui/sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { NavTabs } from "@/components/NavTabs";
import { Progress } from "@/components/ui/progress";
import { SapConnectionStatus } from "@/components/SapConnectionStatus";
import { InvoicePreviewPanel } from "@/components/InvoicePreviewPanel";
import { ErpConnectionStatus } from "@/components/ErpConnectionStatus";
import { useAuth } from "@/contexts/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_BADGE = {
  in_transit: { label: "In Transit", tone: "warning" },
  discrepancy: { label: "Discrepancy", tone: "danger" },
  approved: { label: "Received", tone: "success" },
  rejected: { label: "Rejected", tone: "danger" },
  cancelled: { label: "Cancelled", tone: "danger" },
};



// Warehouse-move item/movement-ID breakdown popover (user's explicit
// ask, Sep 2026) - same pattern already used on the STO pages, applied
// here to sap_movement_result.per_item (product_id + the real SAP
// Goods Movement external_id/error per line).
// Oct 2026, user's explicit ask ("can this be shorter/more readable") -
// raw SAP Goods Movement IDs are 35-digit zero-padded (e.g.
// "00000000000000000000000000000288491") - strips the leading zeros
// for display; the full raw ID is still available via the `title`
// tooltip for anyone who needs to look it up in SAP directly.
const shortMovementId = (id) => {
  if (!id) return id;
  const trimmed = id.replace(/^0+(?=\d)/, "");
  return trimmed || id;
};

const MovementPopover = ({ items, warehouseLabel, docCode, children }) => (
  <Popover>
    <PopoverTrigger asChild>
      <button className="underline-offset-2 hover:underline text-left" onClick={(e) => e.stopPropagation()} data-testid={`grn-movement-popover-trigger-${docCode}`}>
        {children}
      </button>
    </PopoverTrigger>
    <PopoverContent className="w-72 p-3" align="start" data-testid={`grn-movement-popover-${docCode}`}>
      <p className="text-xs font-semibold text-[#101828] mb-2">Warehouse Move{warehouseLabel ? ` — ${warehouseLabel}` : ""}</p>
      <table className="w-full text-[11px]">
        <thead>
          <tr className="text-[#667085]">
            <th className="text-left font-medium py-1">Item</th>
            <th className="text-right font-medium py-1">Movement</th>
          </tr>
        </thead>
        <tbody>
          {items.map((it, i) => (
            <tr key={`${it.product_id}-${i}`} className="border-t border-[#EAECF0]">
              <td className="py-1.5 pr-2 font-mono text-[#344054]">{it.product_id}</td>
              <td className="py-1.5 text-right font-mono">
                {it.ok !== false && it.external_id ? <span className="text-[#027A48]" title={it.external_id}>GM {shortMovementId(it.external_id)}</span> : it.skipped ? <span className="text-[#667085]">{it.note || "Skipped"}</span> : it.ok === false ? <span className="text-[#B42318]" title={it.error_detail || ""}>{it.error || "Failed"}</span> : <span className="text-[#175CD3]">In progress...</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </PopoverContent>
  </Popover>
);

// Sep 10 2026, user's explicit ask: "do not show status received until
// the SAP inbound number is received" - internal `status` becomes
// "approved" the instant staff physically match goods (independent of
// SAP), so the top badge must NOT say "Received" (implying fully done)
// until sap_sync_status actually confirms SAP posted the Goods Receipt.
const resolveStatusBadge = (shipment) => {
  // Feb 2026, real incident (S000029/PO 27480) - a per-PO `release_stuck`
  // flag can be correctly True for weeks while `sap_sync_status` itself
  // never flips to "put_away_failed" (see the long comment on
  // `effectivePutAwayFailed` in the main component below for why) -
  // this list-level badge needs the same override as the detail view's
  // banner, or a shipment stuck exactly like S000029 would list as a
  // plain green "Approved"/"Posted" badge here too.
  const releaseStuck = (shipment.sap_gr_result?.per_po || []).some((p) => p.release_stuck);
  if (releaseStuck && shipment.sap_sync_status !== "put_away_failed") {
    return { label: "Receipt Not Completed In SAP", tone: "danger" };
  }
  if (shipment.status === "approved" && shipment.sap_sync_status !== "posted" && shipment.sap_sync_status !== "partial") {
    // Sep 17 2026, Manual GRN feature - distinct badges for the two new
    // no-Playwright states so the pending/confirmed lists read correctly
    // without needing to open the shipment.
    if (shipment.sap_sync_status === "awaiting_manual_gr") {
      return { label: "Awaiting Manual GR In SAP", tone: "info" };
    }
    if (shipment.sap_sync_status === "manual_mismatch") {
      return { label: "Qty Mismatch - Blocked", tone: "danger" };
    }
    if (shipment.sap_sync_status === "put_away_failed") {
      return { label: "Receipt Not Completed In SAP", tone: "danger" };
    }
    return shipment.sap_sync_status === "failed"
      ? { label: "SAP Sync Failed", tone: "danger" }
      : { label: "Pending SAP Sync", tone: "warning" };
  }
  if (shipment.status === "approved" && shipment.sap_sync_status === "partial") {
    return { label: "Partially Posted", tone: "success" };
  }
  return STATUS_BADGE[shipment.status];
};

// Sep 14 2026 fix - the Diagnostics modal, the failure screenshot
// dialog's title, and both "View exact SAP screen at time of failure
// (...)" links all rendered the raw internal `failed_step` string
// (e.g. "searching") straight to the user - relabeled everywhere it's
// shown.
const FAILED_STEP_LABELS = {
  searching: "creating/locating the SAP Delivery Notification",
  opening_receipt: "opening the Goods Receipt screen",
  entering_quantities: "entering item quantities",
  saving: "saving to SAP",
  unexpected_crash: "an unexpected error",
};
function describeFailedStep(step) {
  return FAILED_STEP_LABELS[step] || step || "unknown step";
}

// Oct 27 2026 fix (real user report: "Line Value" looked wrong next to
// a 2-decimal "PO Price" - e.g. 0.054/EA rounded to "0.05", so
// 25000 x 0.05 (=1250) visibly didn't match the real line value of
// 1350.00). A per-unit rate can legitimately be a sub-cent fraction
// (SAP quotes some lines "per 100 units"), unlike the actual Line
// Value amount next to it, which stays at 2 decimals.
function fmtUnitPrice(amount) {
  if (amount == null) return null;
  return Math.abs(amount) < 1 ? amount.toFixed(4) : amount.toFixed(2);
}

// Oct 2026, user's explicit ask ("IDENTICAL to the Inbound STO progress
// modal - it has NO vertical timeline, it's a simple Dialog with a
// horizontal green Progress bar") - matches ReceiptDetailModal.jsx's
// exact pattern (`progressPct`/`STEP_LABELS`/"Step X of Y — ...") 1:1
// instead of StockTransferPage's vertical timeline (wrong reference,
// corrected same session). Every phase string this job can ever report
// (including the older Playwright automation's dynamic "step:po:count"
// sub-phases, e.g. "searching:29817:1") collapses onto ONE fixed
// sequence of real steps per job `kind`.
const GRN_PROGRESS_STEPS = {
  full_auto_grn: [
    { key: "checking_po_items", label: "Checking PO items in SAP", pct: 10 },
    { key: "posting_goods_receipt", label: "Posting Goods Receipt in SAP", pct: 25 },
    { key: "confirming_put_away", label: "Confirming Put Away in SAP", pct: 55 },
    { key: "moving_stock", label: "Moving stock to {warehouse}", pct: 80 },
  ],
  supplier_grn: [
    { key: "checking_po_items", label: "Checking PO items in SAP", pct: 15 },
    { key: "posting_goods_receipt", label: "Posting Goods Receipt in SAP", pct: 40 },
    { key: "moving_stock", label: "Moving stock to {warehouse}", pct: 75 },
  ],
  manual_grn_notification: [
    { key: "checking_po_items", label: "Checking PO items in SAP", pct: 25 },
    { key: "creating_notifications", label: "Creating SAP Notification", pct: 60 },
  ],
};
function normalizeGrnPhase(phase, stepKeys) {
  if (stepKeys.includes(phase)) return phase;
  if (["queued", "logging_in", "retrying"].includes(phase)) return stepKeys.includes("posting_goods_receipt") ? "posting_goods_receipt" : stepKeys[stepKeys.length - 1];
  return stepKeys.includes("posting_goods_receipt") ? "posting_goods_receipt" : stepKeys[stepKeys.length - 1]; // any dynamic Playwright sub-phase (searching/opening_receipt/entering_quantities/saving:PO:n)
}
function computeGrnProgress(jobProgress, lastRunningPhase, shipment, doneDoc) {
  const steps = GRN_PROGRESS_STEPS[jobProgress?.kind] || GRN_PROGRESS_STEPS.full_auto_grn;
  const stepKeys = steps.map((s) => s.key);
  const techFailed = jobProgress?.status === "failed";
  const techDone = jobProgress?.status === "done";
  // Oct 2026 fix - a "done" job (the automation script ran to
  // completion, no exception) is NOT the same as SAP actually accepting
  // the posting - `sap_sync_status` can still come back "skipped"/
  // "failed"/"pending" (e.g. SAP rejected the reference). The Dialog's
  // green "posted" banner must reflect that real outcome, not just the
  // technical job status, or a rejected PO would show a false success.
  const businessOk = techDone && ["posted", "awaiting_manual_gr"].includes(doneDoc?.sap_sync_status);
  const failed = techFailed || (techDone && !businessOk);
  const done = businessOk;
  const rawPhase = failed ? (lastRunningPhase || stepKeys[0]) : (jobProgress?.phase || stepKeys[0]);
  const idx = Math.max(0, stepKeys.indexOf(normalizeGrnPhase(rawPhase, stepKeys)));
  const warehouseLabel = shipment?.site_id ? `${shipment.site_id}-HOLD` : "the warehouse";
  const stepLabel = steps[idx].label.replace("{warehouse}", warehouseLabel);
  return {
    pct: done ? 100 : steps[idx].pct,
    label: done ? "Complete" : `Step ${idx + 1} of ${steps.length} — ${stepLabel}${failed ? " failed" : ""}`,
    runningLabel: `${stepLabel}...`,
    failed, done,
  };
}

// Sep 12 2026 (user's ask - the raw `JSON.stringify(sap_gr_result)` dump
// in the toast description was unreadable AND the toast disappears
// before anyone can read it anyway). Short, human sentence for the
// toast; the FULL per-PO detail always goes into the persistent
// Diagnostics modal instead (see openDiagnosticsIfFailed below).
function summarizeGrResult(grResult) {
  const perPo = grResult?.per_po || [];
  if (!perPo.length) return "No SAP posting was attempted.";
  const posted = perPo.filter((p) => p.status === "posted");
  const notPosted = perPo.filter((p) => p.status !== "posted");
  if (!notPosted.length) return `All ${posted.length} PO(s) posted to SAP.`;
  const details = notPosted.map((p) => `PO ${p.po_number}: ${p.error || "failed - see Diagnostics"}`).join(" | ");
  return `${posted.length} of ${perPo.length} PO(s) posted. ${details}`;
}

// Sep 22 2026, user's explicit ask ("Goods Receipt posted to SAP should
// also tell posted and stock moved to whichever warehouse") - the
// warehouse move now happens silently in the background (see
// _auto_finish_full_auto_grn in server.py, after Put Away Task confirms),
// so the main "posted" banner needs its own suffix instead of relying on
// the separate, easy-to-miss movement badge elsewhere on this page.
function movementStatusSuffix(shipment) {
  if (!shipment.sap_movement_status || shipment.sap_movement_status === "not_applicable") return null;
  if (shipment.sap_movement_status === "posted") {
    return ` + stock moved to ${shipment.site_id}/${shipment.warehouse_id}`;
  }
  return " - warehouse movement in progress (moves automatically once SAP confirms Put Away)";
}

function summarizeMovementResult(movementResult) {
  const perItem = movementResult?.per_item || [];
  const failed = perItem.filter((i) => !i.ok);
  if (!failed.length) return "Stock movement is still pending.";
  return failed.map((i) => `${i.product_id || `item ${i.item_number}`}: ${i.error || "failed"}`).join(" | ");
}

// Sep 18 2026, user's explicit ask - the Diagnostics modal used to only
// ever look at the Goods Receipt result (sap_gr_result.per_po), so a PO
// that posted cleanly (step 1) but whose warehouse movement (step 2)
// then failed showed the same hardcoded "item(s) below were dropped...
// likely crashed immediately" text as a genuinely dropped/crashed PO -
// misleading for GRN S000001 (Site P3, IRON-SCR/13INTIEBELT: GR posted
// fine, only the warehouse move itself failed). Attaches this PO's own
// movement errors (matched by po_number, same shape _post_goods_
// movement_for_items already returns) so the modal can tell "clean
// success" apart from "GR fine, movement failed" apart from "items
// genuinely dropped".
function poDiagnostics(doc, poResult) {
  const movementItems = (doc?.sap_movement_result?.per_item || []).filter((m) => m.po_number === poResult.po_number);
  return {
    ...poResult,
    movement_issues: movementItems.filter((m) => m.error),
    // Oct 2026, user's explicit ask ("click and open diagnostics, it
    // should show details of the Inbound ID and Goods Movement ID") -
    // `inbound_delivery_id` already lives on poResult itself; the Goods
    // Movement ID is `external_id` on this PO's own successful movement
    // line(s) (goods_movement_batch/goods_movement's own GAC id).
    movement_ids: [...new Set(movementItems.filter((m) => m.ok && m.external_id).map((m) => m.external_id))],
  };
}

export default function GrnApprovalPage() {
  const { user, refresh: refreshAuth } = useAuth();
  const [code, setCode] = useState("");
  const [shipment, setShipment] = useState(null);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [pending, setPending] = useState([]);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [cancelGrnOpen, setCancelGrnOpen] = useState(false);
  // Oct 27 2026, real incident - S001640's "Reversed In SAP" tag only
  // ever showed up in the "Confirmed GRNs" tab's row-click detail
  // dialog (`confirmedDetail`), which never had a Cancel GRN button at
  // all - only the OTHER rendering path (looking the shipment up via
  // the top Lookup box, which populates `shipment`) had one. Tracks
  // which doc code the Cancel dialog is currently acting on, since the
  // two source dialogs populate two different state variables.
  const [cancelGrnTargetCode, setCancelGrnTargetCode] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [screenshotModal, setScreenshotModal] = useState(null);
  // Oct 2026, real incident S001327 - the "Resync from SAP" dry-run's
  // partial-mismatch report, kept client-side only (not persisted on
  // the shipment doc) so an "Accept SAP's Quantity" follow-up button
  // can appear right after the first click, for THIS shipment only.
  const [resyncMismatch, setResyncMismatch] = useState(null);
  // Sep 20 2026, "put_away_failed" fix - user's explicit ask for "a
  // human readable message needed that user can click and close on
  // error" - auto-opens once whenever a looked-up shipment's Put Away
  // permanently failed in SAP, dismissible via the Dialog's own close.
  const [putAwayAlertOpen, setPutAwayAlertOpen] = useState(false);
  const releaseStuckPoNumber = shipment?.sap_gr_result?.per_po?.find((p) => p.release_stuck)?.po_number;
  // Feb 2026, user's explicit ask ("check carefully... diagnose the
  // issue") - real incident (shipment S000029/PO 27480): SAP's Release
  // step silently never completed for one PO's delivery while every
  // sibling PO on the same shipment genuinely finished, yet the overall
  // `sap_sync_status` never flipped to "put_away_failed" (the per-PO
  // `release_stuck` flag - set directly off SAP's own confirmed-delivery
  // report on each retry's final attempt - was already true, it just
  // wasn't being surfaced because every banner/button below only ever
  // checked the top-level status). `effectivePutAwayFailed` makes the
  // banner/alert/button trust this per-PO signal on its own, regardless
  // of whether the top-level status ever caught up - it's a zero-false-
  // positive signal (SAP's own report said "not Finished" on a genuine
  // final attempt), so no outcome where "posted" is actually correct
  // can ever set it.
  const effectivePutAwayFailed = shipment?.sap_sync_status === "put_away_failed" || !!releaseStuckPoNumber;
  useEffect(() => {
    if (effectivePutAwayFailed) setPutAwayAlertOpen(true);
  }, [shipment?._id, shipment?.sap_sync_status, shipment?.grn_alert_message, releaseStuckPoNumber]);

  // Sep 17 2026, Manual GRN (No-Playwright) feature - user's explicit ask
  // ("keep the choice for users") to self-toggle between the existing
  // Playwright automation and manually posting the Goods Receipt in SAP.
  const [prefBusy, setPrefBusy] = useState(false);
  const toggleManualGrnPreference = async (checked) => {
    setPrefBusy(true);
    try {
      await axios.put(`${API}/admin/grn/my-preference`, { manual_grn_preference: checked });
      await refreshAuth();
      toast.success(checked ? "Manual GRN mode enabled - future approvals only create the SAP Notification, you post the Goods Receipt yourself in SAP" : "Auto (Playwright) GRN mode enabled");
    } catch (err) {
      toast.error("Could not save preference", { description: err?.response?.data?.detail || err.message });
    } finally {
      setPrefBusy(false);
    }
  };

  // Admin-only "Blocked Users" override panel (Sep 17 2026).
  const [blockedUsersOpen, setBlockedUsersOpen] = useState(false);
  const [blockedUsers, setBlockedUsers] = useState([]);
  const [overrideReasons, setOverrideReasons] = useState({});
  const [overrideBusyId, setOverrideBusyId] = useState(null);
  const isGrnAdmin = user && (user.role === "super_admin" || user.role === "admin");
  const loadBlockedUsers = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/grn/blocked-users`);
      setBlockedUsers(data.users || []);
    } catch (err) {
      toast.error("Could not load blocked users", { description: err?.response?.data?.detail || err.message });
    }
  };
  const openBlockedUsers = () => {
    setBlockedUsersOpen(true);
    loadBlockedUsers();
  };
  const overrideBlock = async (userId) => {
    const reason = (overrideReasons[userId] || "").trim();
    if (!reason) {
      toast.error("An override reason is required");
      return;
    }
    setOverrideBusyId(userId);
    try {
      await axios.post(`${API}/admin/grn/override-block/${userId}`, { reason });
      toast.success("User unblocked");
      loadBlockedUsers();
      refreshAuth();
    } catch (err) {
      toast.error("Override failed", { description: err?.response?.data?.detail || err.message });
    } finally {
      setOverrideBusyId(null);
    }
  };

  const [sites, setSites] = useState([]);
  const [supplierDocNum, setSupplierDocNum] = useState("");
  const [dupInvoiceWarning, setDupInvoiceWarning] = useState(null);
  const [siteId, setSiteId] = useState("");
  // Sep 20 2026, same-day follow-up: `warehouseId` is permanently "" now -
  // the Warehouse dropdown + its auto-fetch/auto-default-to-RM effect were
  // both removed (user's explicit ask: no warehouse concept in this flow
  // at all). Kept as a literal empty-string constant, NOT state, purely so
  // the existing `warehouse_id: warehouseId` field on the approve payload
  // below doesn't need touching - sending "" keeps the backend's goods-
  // movement gate (`if ... doc.get("warehouse_id")`) permanently falsy,
  // same as skip_movement already does for the Goods Receipt itself.
  const warehouseId = "";
  const [billDate, setBillDate] = useState("");
  // Oct 2026 fix (real incident S001330/S001294, user's final
  // correction) - a separate "GRN Date (Physical)" the user explicitly
  // asked for: the real date goods physically arrived, typed by staff
  // here. This - NOT billDate (Supplier Invoice Date, a different
  // real-world event) and NOT a system timestamp - is what the backend
  // now sends to SAP as the Actual Delivery Date.
  const [grnDatePhysical, setGrnDatePhysical] = useState("");
  // Oct 13 2026, user's explicit ask: a mandatory Supplier Invoice copy
  // upload before a GRN can be approved - { path, filename, size,
  // uploading, error }. Images are compressed client-side (canvas,
  // iterative JPEG quality) toward a <250KB target before upload since
  // phone-camera photos routinely come in at several MB; PDFs/Office
  // docs aren't easily re-compressed client-side so those just get a
  // hard 400KB ceiling check (same ceiling re-enforced server-side).
  const [invoiceAttachment, setInvoiceAttachment] = useState(null);
  // Feb 2026, user's explicit ask (AI Invoice Match) - {status, issues,
  // extracted_invoice_number/date, user_decision, ...} or null if not
  // run yet for this shipment. Inline preview of the uploaded invoice
  // (object URL for an <img>/<iframe>) shown alongside it, same "see
  // the GRN details and invoice copy in parallel" reference the user
  // shared, and shown as soon as a shipment with an attachment is
  // looked up (not just during a fresh review).
  const [aiMatch, setAiMatch] = useState(null);
  const [aiMatchRunning, setAiMatchRunning] = useState(false);
  // Feb 2026, user's explicit ask ("what is the expected time... add a
  // progress bar or seconds counter") - live-measured against the real
  // Gemini 2.5 Flash call (grn_invoice_ai_match_service.py): 4 real
  // runs on a live invoice took 12s/36s/12s/24s - no fixed duration,
  // so a determinate "will finish in Xs" bar would be dishonest. Shows
  // an actually-accurate elapsed-seconds counter plus a "soft" progress
  // bar that eases toward (but never reaches) ~92% using the measured
  // ~20s typical/40s slow range as its curve, then snaps to 100% the
  // instant the real response lands - never implies a fixed ETA.
  const [aiMatchElapsedSec, setAiMatchElapsedSec] = useState(0);
  const aiMatchTimerRef = useRef(null);
  const aiMatchSoftProgressPct = Math.min(92, Math.round(100 * (1 - Math.exp(-aiMatchElapsedSec / 18))));
  useEffect(() => () => clearInterval(aiMatchTimerRef.current), []);
  // Feb 2026, user's explicit ask ("add two min wait in frontend but
  // check that GRN in the SAP backend and show result to the user") -
  // direct follow-up to the duplicate-Inbound-Delivery incident above.
  // SAP's own Inbound Delivery fulfillment/status fields can still be
  // settling for a short while right after a post - checking "Verify
  // with SAP" the instant it's clicked risks exactly the kind of false
  // negative that caused this (verify_and_correct_gr_status flips a
  // genuinely-posted PO to "failed" because it looked too early).
  // Rather than silently delaying (confusing - looks like nothing
  // happened), shows a visible 2-minute countdown, THEN fires the
  // existing verify-sap-status call automatically and shows its real
  // result exactly as before.
  const VERIFY_SAP_WAIT_SEC = 120;
  const [verifySapCountdown, setVerifySapCountdown] = useState(null); // seconds left, or null when idle
  const verifySapTimerRef = useRef(null);
  useEffect(() => () => clearInterval(verifySapTimerRef.current), []);
  const [aiMatchErrorMsg, setAiMatchErrorMsg] = useState(null);
  const [actualQtys, setActualQtys] = useState({});
  const [jobProgress, setJobProgress] = useState(null);
  // Oct 2026, user's explicit ask ("modal after completion closed
  // itself, should stay open and show Goods Movement ID") - remembers
  // the last real (non-terminal) phase seen while polling, so a
  // "failed" job (whose own `phase` becomes the literal string
  // "failed", losing which real step it was on) can still mark the
  // correct timeline step as failed instead of always the first one.
  const [lastRunningPhase, setLastRunningPhase] = useState(null);
  const closeGrnProgress = () => { setBusy(false); setJobProgress(null); setLastRunningPhase(null); };

  // Oct 2026, user's explicit ask - replaces the old "Mark Discrepancy"
  // (park-and-wait-for-vendor) state above with a single remark popup
  // that unlocks Validate/Post directly (see submitVarianceRemark
  // below and grn_variance_log_service.py).
  const [varianceRemarkOpen, setVarianceRemarkOpen] = useState(false);
  const [varianceRemarkText, setVarianceRemarkText] = useState("");
  const [varianceRemarkBusy, setVarianceRemarkBusy] = useState(false);

  // Sep 10 2026, user's explicit ask: a browsable list of already-Received
  // GRNs, with a popup showing the full trail (supplier code, bill number,
  // SAP inbound delivery number, item-level qty/unit/warehouse) - so staff
  // don't need to remember/re-type a shipment code just to confirm a GRN
  // already went through.
  const [confirmed, setConfirmed] = useState([]);
  const [confirmedDetail, setConfirmedDetail] = useState(null);
  const [confirmedRetryBusy, setConfirmedRetryBusy] = useState(false);
  // Sep 12 2026, user's explicit ask: "diagnostics" popup - which SAP
  // screen the automation failed on, the SAP error itself, and every
  // milestone it successfully got through beforehand (see `events` on
  // each per_po entry, built up live by sap_playwright_supplier_pgr_
  // service.py's _post_one_po).
  const [diagnosticsModal, setDiagnosticsModal] = useState(null);
  // Sep 12 2026 (user's ask - "put them in diagnostics report, currently
  // toast disappears"): auto-open the modal on any failure/partial
  // success, instead of making the user hunt for a "View Diagnostics"
  // button under a toast that's already gone.
  const openDiagnosticsIfFailed = (grResult) => {
    const failing = grResult?.per_po?.find((p) => p.status !== "posted" && p.events?.length);
    if (failing) setDiagnosticsModal(failing);
  };

  // Sep 10 2026, user's explicit ask: don't let anyone hit Retry while the
  // background job might still legitimately be running - the whole flow
  // (3 attempts x Playwright login/navigate/save + 8s pauses) can
  // genuinely take a few minutes, so gate Retry behind a 5 min cooldown
  // from approval and show "In Process" until then.
  const RETRY_COOLDOWN_MIN = 5;
  const [nowTick, setNowTick] = useState(Date.now());
  useEffect(() => {
    const tick = setInterval(() => setNowTick(Date.now()), 15000);
    return () => clearInterval(tick);
  }, []);
  const minutesSince = (dateStr) => dateStr ? (nowTick - new Date(dateStr).getTime()) / 60000 : Infinity;
  const retryUnlockInMin = (dateStr) => Math.max(0, Math.ceil(RETRY_COOLDOWN_MIN - minutesSince(dateStr)));

  const retryConfirmedGoodsReceipt = async (doc) => {
    // Sep 12 2026 bug fix (user's explicit report: "retry does not
    // work... nothing shows diagnostics", "retry history shows
    // nowhere, still shows original date/time"): this used to fire the
    // retry job and just hope a single 3-second-later list reload
    // would pick up the result. A real SAP GRN attempt takes 1-2+
    // minutes, so that reload almost always ran while the job was
    // still in progress - and it only ever refreshed the closed LIST,
    // never the currently OPEN detail dialog (`confirmedDetail`), which
    // kept showing the pre-retry sap_gr_result/screenshot/events
    // forever, looking exactly like "retry did nothing". Now polls the
    // job the same way the main Approve/Retry flow already does
    // (pollGrnJob) and pushes the real, final result straight into the
    // open dialog + the list once it's actually done.
    setConfirmedRetryBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/grn/${doc._id}/retry-goods-receipt`);
      toast.success("Retry started - checking SAP now, this can take a couple of minutes");
      const result = await pollGrnJob(data.job_id);
      if (confirmedDetail?._id === doc._id) setConfirmedDetail(result);
      if (result.sap_sync_status === "posted") {
        toast.success("Goods Receipt posted to SAP");
      } else {
        toast.warning("Still not posted", { description: summarizeGrResult(result.sap_gr_result), duration: 8000 });
        openDiagnosticsIfFailed(result.sap_gr_result);
      }
      loadConfirmed();
    } catch (err) {
      toast.error("Retry failed", { description: err?.response?.data?.detail || err.message });
    } finally {
      setConfirmedRetryBusy(false);
    }
  };

  // Sep 13 2026, user's explicit ask ("align it and choose best for
  // users interest") - the top-level lookup detail panel already hid
  // "Retry" behind "Reset Retry" once sap_sync_status hits "failed"
  // (MAX_GR_RETRIES_BEFORE_FAILED reached, same unresolved SAP error
  // reproducing every time), but this Confirmed-detail dialog still
  // showed a plain Retry button in that exact same state - letting
  // staff keep hammering a known-broken SAP-side issue instead of
  // waiting on SAP Admin confirmation first. Mirrors resetRetry, but
  // targets confirmedDetail/loadConfirmed instead of shipment.
  const resetConfirmedRetry = async (doc) => {
    if (!window.confirm("Only do this after your SAP Admin has confirmed the underlying SAP error is fixed. Reset retry count for this shipment?")) return;
    setConfirmedRetryBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/grn/${doc._id}/reset-retry`);
      if (confirmedDetail?._id === doc._id) setConfirmedDetail(data);
      toast.success("Retry count reset - you can Retry the Goods Receipt again");
      loadConfirmed();
    } catch (err) {
      toast.error("Reset failed", { description: err?.response?.data?.detail || err.message });
    } finally {
      setConfirmedRetryBusy(false);
    }
  };

  // Sep 18 2026, user's explicit ask (GRN S000001, Site P3) - the
  // warehouse-move Retry button already existed for the Pending
  // Shipments modal (retryMovement above) but was never wired into this
  // Confirmed GRNs detail modal, so a GRN whose Goods Receipt posted
  // fine but whose warehouse move failed had NO way to retry it once it
  // showed up here - only the (unrelated) "Retry Goods Receipt" flow.
  const retryConfirmedMovement = async (doc) => {
    setConfirmedRetryBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/grn/${doc._id}/retry-movement`);
      if (confirmedDetail?._id === doc._id) setConfirmedDetail(data);
      if (data.sap_movement_status === "posted") {
        toast.success(`Stock moved to ${data.site_id}/${data.warehouse_id}`);
      } else {
        toast.warning("Movement still pending", { description: summarizeMovementResult(data.sap_movement_result), duration: 8000 });
      }
      loadConfirmed();
    } catch (err) {
      toast.error("Retry failed", { description: err?.response?.data?.detail || err.message });
    } finally {
      setConfirmedRetryBusy(false);
    }
  };

  // Sep 20 2026, "put_away_failed" fix - Confirmed GRNs detail dialog
  // counterpart to retryPutAway above.
  const retryConfirmedPutAway = async (doc) => {
    setConfirmedRetryBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/grn/${doc._id}/retry-put-away`);
      if (confirmedDetail?._id === doc._id) setConfirmedDetail(data);
      toast.success("Retrying Put Away confirmation in SAP - this can take up to a couple of minutes");
      loadConfirmed();
    } catch (err) {
      toast.error("Retry failed", { description: err?.response?.data?.detail || err.message });
    } finally {
      setConfirmedRetryBusy(false);
    }
  };


  const loadPending = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/grn/shipments`, { params: { status: "in_transit", search: pendingFilter || undefined } });
      setPending(data.shipments || []);
    } catch (err) {
      toast.error("Could not load pending shipments", { description: err?.response?.data?.detail || err.message });
    }
  };

  const loadConfirmed = async (overrides = {}) => {
    try {
      const { data } = await axios.get(`${API}/admin/grn/shipments`, { params: {
        status: "approved",
        po_number: ("po_number" in overrides ? overrides.po_number : confirmedPoFilter) || undefined,
        shipment_code: ("shipment_code" in overrides ? overrides.shipment_code : confirmedShipmentFilter) || undefined,
        pr_number: ("pr_number" in overrides ? overrides.pr_number : confirmedPrFilter) || undefined,
        supplier_name: ("supplier_name" in overrides ? overrides.supplier_name : confirmedSupplierFilter) || undefined,
        site_id: (() => { const v = "site_id" in overrides ? overrides.site_id : confirmedSiteFilter; return v && v !== "all" ? v : undefined; })(),
        item_query: ("item_query" in overrides ? overrides.item_query : confirmedItemFilter) || undefined,
        date_from: ("date_from" in overrides ? overrides.date_from : confirmedDateFrom) || undefined,
        date_to: ("date_to" in overrides ? overrides.date_to : confirmedDateTo) || undefined,
      } });
      setConfirmed(data.shipments || []);
      setConfirmedTotalCount(data.total_count ?? (data.shipments || []).length);
    } catch (err) {
      toast.error("Could not load confirmed GRNs", { description: err?.response?.data?.detail || err.message });
    }
  };

  const inboundDeliveryIds = (s) => [...new Set((s.sap_gr_result?.per_po || []).map((p) => p.inbound_delivery_id).filter(Boolean))];
  // Oct 13 2026, user's explicit ask: SAP lets a user reverse a GRN
  // AFTER it's already posted (real example: Inbound Delivery 55606,
  // reversed due to a bill mismatch) - flagged by the background sweep
  // (see check_grn_reversal_status, server.py) via SAP's own
  // CancellationStatusCode.
  const hasReversedInboundDelivery = (s) => (s.sap_gr_result?.per_po || []).some((p) => p.reversed_in_sap);

  // Sep 11 2026, user's explicit ask: a few live GRNs actually posted in SAP
  // but our own Playwright automation never captured the real Inbound
  // Delivery ID (blank forever otherwise) - lets staff pull it straight
  // from SAP on demand, mirroring OpenPurchaseOrdersPage's "Refresh from
  // SAP" job-poll pattern (a single live query here takes 30-100s+).
  const [fetchingInboundFor, setFetchingInboundFor] = useState(null);
  const fetchInboundDelivery = async (s) => {
    setFetchingInboundFor(s._id);
    try {
      const { data } = await axios.post(`${API}/admin/grn/${s._id}/fetch-inbound-delivery`);
      const jobId = data.job_id;
      for (let i = 0; i < 60; i++) {
        await new Promise((r) => setTimeout(r, 3000));
        const { data: poll } = await axios.get(`${API}/admin/grn/fetch-inbound-delivery/poll/${jobId}`);
        if (poll.status === "done") {
          const ids = inboundDeliveryIds(poll.result.shipment);
          toast.success(`Fetched from SAP: Inbound Delivery # ${ids.join(", ")}`);
          loadConfirmed();
          if (confirmedDetail?._id === s._id) setConfirmedDetail(poll.result.shipment);
          return;
        }
        if (poll.status === "failed") {
          toast.error("Could not fetch from SAP", { description: poll.error || "Unknown error" });
          return;
        }
      }
      toast.error("This is taking longer than expected - try again shortly");
    } catch (err) {
      toast.error("Could not start SAP fetch", { description: err?.response?.data?.detail || err.message });
    } finally {
      setFetchingInboundFor(null);
    }
  };

  // Sep 10 2026, user's explicit ask: tabbed Pending/Confirmed instead of
  // stacked tables, each with its own "PO number or Vendor code" filter.
  const [activeTab, setActiveTab] = useState("pending");
  const [pendingFilter, setPendingFilter] = useState("");
  // Feb 2026 fix (real report, PO 30732/S001494 "no result" even though
  // the shipment genuinely existed) - this tab only ever holds the
  // most recent 200 rows (see server.py's /admin/grn/shipments
  // docstring), so this box used to just filter whichever page was
  // already loaded, never the whole collection. Debounced same as
  // every other live-search box in this app - re-runs loadPending
  // with the typed term as a real server-side search, falling back to
  // the normal capped "recent" list once cleared.
  const pendingSearchDebounce = useRef(null);
  useEffect(() => {
    clearTimeout(pendingSearchDebounce.current);
    pendingSearchDebounce.current = setTimeout(() => loadPending(), 400);
    return () => clearTimeout(pendingSearchDebounce.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingFilter]);
  // Nov 2026, user's explicit ask ("GRN Approval - Advanced Filter
  // Section") - the Confirmed GRNs tab's old single free-text box
  // ("Search Shipment Code, PO Number, Bill Number...") is replaced by
  // a dedicated filter panel: PO Number, Shipment Code, PR Number,
  // Supplier Name, Site, Item Code/Name, and a date range (defaulting
  // to the last 7 days against `approved_at`, the same field this tab
  // already sorts by). Deliberately NOT live/debounced like the box it
  // replaces - the user explicitly asked for Search/Clear Filters
  // buttons instead, since this tab's history only ever grows and a
  // fetch-on-every-keystroke doesn't fit a 6-field form the same way.
  const isoDaysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().slice(0, 10); };
  const confirmedFilterDefaults = { po_number: "", shipment_code: "", pr_number: "", supplier_name: "", site_id: "all", item_query: "", date_from: isoDaysAgo(7), date_to: isoDaysAgo(0) };
  const [confirmedPoFilter, setConfirmedPoFilter] = useState(confirmedFilterDefaults.po_number);
  const [confirmedShipmentFilter, setConfirmedShipmentFilter] = useState(confirmedFilterDefaults.shipment_code);
  const [confirmedPrFilter, setConfirmedPrFilter] = useState(confirmedFilterDefaults.pr_number);
  const [confirmedSupplierFilter, setConfirmedSupplierFilter] = useState(confirmedFilterDefaults.supplier_name);
  const [confirmedSiteFilter, setConfirmedSiteFilter] = useState(confirmedFilterDefaults.site_id);
  const [confirmedItemFilter, setConfirmedItemFilter] = useState(confirmedFilterDefaults.item_query);
  const [confirmedDateFrom, setConfirmedDateFrom] = useState(confirmedFilterDefaults.date_from);
  const [confirmedDateTo, setConfirmedDateTo] = useState(confirmedFilterDefaults.date_to);
  const [confirmedTotalCount, setConfirmedTotalCount] = useState(0);
  const clearConfirmedFilters = () => {
    setConfirmedPoFilter(confirmedFilterDefaults.po_number);
    setConfirmedShipmentFilter(confirmedFilterDefaults.shipment_code);
    setConfirmedPrFilter(confirmedFilterDefaults.pr_number);
    setConfirmedSupplierFilter(confirmedFilterDefaults.supplier_name);
    setConfirmedSiteFilter(confirmedFilterDefaults.site_id);
    setConfirmedItemFilter(confirmedFilterDefaults.item_query);
    setConfirmedDateFrom(confirmedFilterDefaults.date_from);
    setConfirmedDateTo(confirmedFilterDefaults.date_to);
    loadConfirmed(confirmedFilterDefaults);
  };
  // Oct 2026, user's explicit ask: also search by Supplier Invoice
  // (bill) number and item/product code, not just PO number/vendor -
  // easier to find a shipment when that's the only reference on hand.
  // (Pending tab only now - see the filter panel above for Confirmed.)
  const matchesPoOrVendor = (s, term) => {
    if (!term.trim()) return true;
    const needle = term.trim().toLowerCase();
    const haystack = [
      s._id, s.vendor_code, s.company_name, s.supplier_doc_num,
      ...s.items.map((it) => it.po_number),
      ...s.items.map((it) => it.sap_po_number),
      ...s.items.map((it) => it.product_id),
    ].filter(Boolean).join(" ").toLowerCase();
    return haystack.includes(needle);
  };
  const filteredPending = pending.filter((s) => matchesPoOrVendor(s, pendingFilter));
  // Confirmed GRNs is now filtered entirely server-side (see
  // loadConfirmed/the filter panel) - `confirmed` already IS the
  // filtered set, kept as its own name only to minimize the render
  // code below.
  const filteredConfirmed = confirmed;

  const loadSites = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/grn/sites`);
      setSites(data.sites || []);
      if ((data.sites || []).length === 1) setSiteId(data.sites[0]);
    } catch (err) {
      toast.error("Could not load sites", { description: err?.response?.data?.detail || err.message });
    }
  };

  useEffect(() => {
    loadPending();
    loadConfirmed();
    loadSites();
  }, []);

  // Sep 19 2026, real user report (S000010/S000011/S000012, 3 times in a
  // row): the backend's own background job (Put Away confirm + SAP
  // Inbound Delivery # fetch + Goods Movement retry, all fire-and-forget
  // after a full-auto GRN posts) was actually completing fine within a
  // couple minutes each time - the user just had this page open from
  // before it finished, and it never refreshed itself, so it kept
  // showing "Fetch from SAP"/"Warehouse move pending" long after the
  // backend had already resolved both. Re-fetches the Confirmed GRNs
  // list every 30s (never a full page reload) WHILE any shipment
  // approved in the last 3 minutes is still missing its Delivery ID or
  // still shows movement pending - stops polling on its own once
  // nothing is still finalizing. Rows are keyed by `s._id`, so React
  // only actually re-renders whichever specific row(s) changed, not the
  // whole table.
  // Sep 22 2026, user's explicit ask ("any better way to fetch it
  // instantly") - tightened from 30s to 8s while still finalizing (the
  // backend's own Put Away/Delivery-ID/Movement retry loop now checks as
  // often as every 5s - polling our own DB slower than that just adds
  // pure UI lag on top of SAP's real latency for no reason). Also widened
  // the window to 4 minutes (matches the backend loop's ~170s ceiling +
  // a buffer) and now also keeps polling until movement is genuinely
  // "posted" (not just "pending"), not only while it's missing entirely -
  // covers the Sep 22 2026 policy reversal where movement starts at
  // "not_applicable" immediately after GR posts and only flips once the
  // background loop's first cycle runs.
  useEffect(() => {
    const stillFinalizing = confirmed.some((s) => {
      const approvedAt = s.approved_at ? new Date(s.approved_at).getTime() : 0;
      if (Date.now() - approvedAt > 4 * 60 * 1000) return false;
      return inboundDeliveryIds(s).length === 0 || s.sap_movement_status !== "posted";
    });
    if (!stillFinalizing) return;
    const poll = setInterval(loadConfirmed, 8000);
    return () => clearInterval(poll);
  }, [confirmed]);

  // Sep 20 2026, same-day follow-up (real user report: "why movement
  // message exists now") - this used to auto-fetch this site's warehouses
  // and silently default `warehouseId` to its RM location EVEN AFTER the
  // Warehouse dropdown itself was removed from the UI. That non-empty
  // `warehouseId` was then sent on every approve payload, which re-armed
  // the backend's goods-movement check (`manually_confirm_inbound_
  // delivery`'s own `if ... doc.get("warehouse_id")` gate) - surfacing an
  // unwanted "Moved to P8-RM" note on shipments the user explicitly wants
  // to stay GRN-only, no warehouse concept at all. Removed entirely -
  // `warehouseId` is now a permanent "" (see its declaration above).

  // "RI and RT Site should be non-editable and pre-fixed based on shipment
  // code" (Sep 2026): the Site dropdown only ever offers sites that belong
  // to THIS shipment's buying entity (RI/RT), returned by the lookup as
  // `allowed_site_ids` - auto-locks (disabled) whenever that narrows to
  // exactly one, same as the old single-site-binding auto-lock. A LEGACY
  // shipment (no buyer_code at all) falls back to every known site since
  // its entity is unknown - but once the entity IS known, an empty
  // `allowed_site_ids` means "you're not bound to any of its sites", which
  // must NOT silently fall back to showing every site (testing_agent,
  // iteration_140 - the old `.length ? filter : sites` did exactly that).
  const computeEligibleSites = (shipmentDoc, sitesList) => {
    if (!shipmentDoc || !shipmentDoc.buyer_code) return sitesList;
    return sitesList.filter((s) => (shipmentDoc.allowed_site_ids || []).includes(s));
  };
  const eligibleSites = computeEligibleSites(shipment, sites);
  const siteAccessBlocked = !!shipment?.site_access_blocked;

  // Recomputed whenever the looked-up shipment OR the site list itself
  // changes (fixes a race where a lookup could resolve before loadSites()'s
  // own async call landed, iteration_140) rather than only inside lookup().
  useEffect(() => {
    if (!shipment) return;
    const eligible = computeEligibleSites(shipment, sites);
    setSiteId(eligible.length === 1 ? eligible[0] : "");
    setInvoiceAttachment(shipment.invoice_attachment || null);
    setAiMatch(shipment.ai_invoice_match || null);
    setAiMatchErrorMsg(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shipment, sites]);

  // Oct 29 2026, user's explicit ask ("duplicate inv warning") - checks
  // whichever invoice number is currently relevant: the live-typed value
  // while this shipment is still being approved, or the already-saved
  // value if looking a confirmed shipment back up. Debounced so it
  // doesn't fire on every keystroke; never blocks, only warns.
  useEffect(() => {
    const effectiveDocNum = (supplierDocNum || shipment?.supplier_doc_num || "").trim();
    if (!shipment || !shipment.vendor_code || !effectiveDocNum) {
      setDupInvoiceWarning(null);
      return;
    }
    const timer = setTimeout(() => {
      axios.get(`${API}/admin/grn/check-duplicate-invoice`, {
        params: { vendor_code: shipment.vendor_code, supplier_doc_num: effectiveDocNum, exclude_doc_code: shipment._id },
      })
        .then((r) => setDupInvoiceWarning(r.data?.duplicate || null))
        .catch(() => setDupInvoiceWarning(null));
    }, 500);
    return () => clearTimeout(timer);
  }, [shipment, supplierDocNum]);

  const resetApprovalForm = () => {
    setSupplierDocNum("");
    setBillDate("");
    setGrnDatePhysical("");
    setJobProgress(null);
    setInvoiceAttachment(null);
    setAiMatch(null);
    setAiMatchErrorMsg(null);
    setDupInvoiceWarning(null);
  };

  const GRN_INVOICE_TARGET_BYTES = 250 * 1024;
  const GRN_INVOICE_MAX_BYTES = 400 * 1024;

  // Canvas-based client-side compression for phone-camera photos (these
  // routinely come in at several MB) - iteratively lowers JPEG quality
  // (and caps the longest dimension at 1600px) until under the 250KB
  // target, or gives up at quality 0.3 (the backend's 400KB hard ceiling
  // is the final safety net either way).
  const compressInvoiceImage = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        const maxDim = 1600;
        if (width > maxDim || height > maxDim) {
          const scale = maxDim / Math.max(width, height);
          width = Math.round(width * scale);
          height = Math.round(height * scale);
        }
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        canvas.getContext("2d").drawImage(img, 0, 0, width, height);
        let quality = 0.85;
        const tryCompress = () => {
          canvas.toBlob((blob) => {
            if (!blob) { reject(new Error("Compression failed")); return; }
            if (blob.size <= GRN_INVOICE_TARGET_BYTES || quality <= 0.3) {
              resolve(new File([blob], file.name.replace(/\.[^.]+$/, "") + ".jpg", { type: "image/jpeg" }));
            } else {
              quality -= 0.15;
              tryCompress();
            }
          }, "image/jpeg", quality);
        };
        tryCompress();
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const onInvoiceFileSelect = async (file) => {
    if (!file) return;
    let uploadFile = file;
    if (file.type.startsWith("image/")) {
      try {
        uploadFile = await compressInvoiceImage(file);
      } catch {
        uploadFile = file; // fall back to the original if compression itself fails
      }
    }
    if (uploadFile.size > GRN_INVOICE_MAX_BYTES) {
      setInvoiceAttachment({ filename: file.name, uploading: false, path: null, error: "Still too large after compression - maximum is 400 KB. Try a lower-resolution photo or a smaller file." });
      return;
    }
    setInvoiceAttachment({ filename: file.name, uploading: true, path: null, error: null });
    const formData = new FormData();
    formData.append("file", uploadFile, uploadFile.name);
    try {
      const { data } = await axios.post(`${API}/admin/grn/${shipment._id}/upload-invoice`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setInvoiceAttachment({ filename: data.attachment_filename, path: data.attachment_path, size: data.size, uploading: false, error: null });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setInvoiceAttachment({ filename: file.name, uploading: false, path: null, error: typeof detail === "string" ? detail : "Upload failed" });
    }
  };

  const viewInvoiceAttachment = async (docCode) => {
    try {
      const resp = await axios.get(`${API}/admin/grn/${docCode}/invoice-attachment`, { responseType: "blob" });
      const url = window.URL.createObjectURL(resp.data);
      window.open(url, "_blank");
    } catch {
      toast.error("Could not open the supplier invoice copy");
    }
  };

  // Feb 2026, user's explicit ask - AI-assisted cross-check between the
  // uploaded invoice copy and what's been keyed into this GRN, before
  // "Validate GRN" can be clicked. Sends the SAME live, on-screen
  // actual_qty/invoice_qty values the item table shows right now (not
  // whatever was last saved), so correcting a qty and re-running always
  // checks against the latest entry.
  const runAiMatch = async () => {
    if (!shipment) return;
    setAiMatchRunning(true);
    setAiMatchErrorMsg(null);
    setAiMatchElapsedSec(0);
    clearInterval(aiMatchTimerRef.current);
    aiMatchTimerRef.current = setInterval(() => setAiMatchElapsedSec((s) => s + 1), 1000);
    try {
      const item_actual_qtys = shipment.items.map((it) => {
        const key = `${it.po_number}::${it.item_number}`;
        return {
          po_number: it.po_number, item_number: it.item_number,
          actual_qty: Number(actualQtys[key] ?? it.actual_qty ?? it.ship_qty ?? 0),
          invoice_qty: invoiceQtys[key] != null && invoiceQtys[key] !== "" ? Number(invoiceQtys[key]) : null,
        };
      });
      const { data } = await axios.post(`${API}/admin/grn/${shipment._id}/ai-invoice-match`, {
        supplier_doc_num: supplierDocNum, bill_date: billDate, site_id: siteId || "", warehouse_id: "", item_actual_qtys,
      });
      setAiMatch(data);
    } catch (e) {
      setAiMatchErrorMsg(e?.response?.data?.detail || "AI Match failed");
    } finally {
      clearInterval(aiMatchTimerRef.current);
      setAiMatchRunning(false);
    }
  };

  const acceptAiMatchIssues = async () => {
    try {
      const { data } = await axios.post(`${API}/admin/grn/${shipment._id}/ai-invoice-match/accept`);
      setAiMatch(data);
      toast.success("AI Match issues accepted - you can now Validate GRN");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not accept AI Match issues");
    }
  };

  // Must be declared before liveAiMatchSignature() is evaluated during render (TDZ crash otherwise)
  const [invoiceQtys, setInvoiceQtys] = useState({});
  // Feb 2026, HIGH bug fix (testing_agent iteration_293) - aiMatchCleared
  // must also confirm the current on-screen invoice number/date/qty
  // values are EXACTLY what this match result was actually checked
  // against (same signature logic as the server's own authoritative
  // inputs_still_match in grn_invoice_ai_match_service.py) - otherwise
  // editing a qty/number/date after a clean or accepted run silently
  // kept Validate GRN unlocked against data the AI never saw.
  const liveAiMatchSignature = () => {
    if (!shipment) return null;
    const qtyParts = shipment.items.map((it) => {
      const key = `${it.po_number}::${it.item_number}`;
      const actual = Number(actualQtys[key] ?? it.actual_qty ?? it.ship_qty ?? 0);
      const invoiceQty = invoiceQtys[key] != null && invoiceQtys[key] !== "" ? Number(invoiceQtys[key]) : actual;
      return `${key}=${actual}|${invoiceQty}`;
    }).sort().join(",");
    return `${supplierDocNum.trim()}|${billDate}|${qtyParts}`;
  };
  const checkedAiMatchSignature = (m) => {
    if (!m?.checked_inputs) return null;
    const qtyParts = Object.entries(m.checked_inputs.item_qtys || {})
      .map(([k, v]) => `${k}=${Number(v.actual_qty)}|${Number(v.invoice_qty)}`).sort().join(",");
    return `${(m.checked_inputs.supplier_doc_num || "").trim()}|${m.checked_inputs.bill_date || ""}|${qtyParts}`;
  };
  const aiMatchStale = !!aiMatch && checkedAiMatchSignature(aiMatch) !== liveAiMatchSignature();
  // Oct 2026, user's explicit ask (GRN Variance Log feature) - a
  // rate_mismatch issue (price variance) no longer needs the generic
  // "Accept as is & continue" path; it's handled by the separate
  // Variance Remark popup instead (see hasLiveVariance/varianceAcknowledged
  // below), so it's excluded here - AI Match is "cleared" once every
  // OTHER issue type is resolved, regardless of whether a rate_mismatch
  // is still outstanding. A rate_mismatch issue from BEFORE this
  // feature existed has no numeric ai_rate/po_rate (testing_agent
  // iter341 finding) and can't be shown/logged by the new popup, so it
  // stays on this list (falls back to "Accept as is") instead of
  // silently vanishing.
  const aiMatchNonPriceIssues = (aiMatch?.issues || []).filter((iss) => !(iss.type === "rate_mismatch" && iss.ai_rate != null && iss.po_rate != null));
  const aiMatchCleared = !!aiMatch && !aiMatchStale && (aiMatchNonPriceIssues.length === 0 || aiMatch.user_decision === "accepted");
  const aiMatchUsableAttachment = !!invoiceAttachment?.path && /\.(pdf|jpe?g|png)$/i.test(invoiceAttachment.filename || "");

  const initActualQtys = (items) => {
    const next = {};
    (items || []).forEach((it) => { next[`${it.po_number}::${it.item_number}`] = it.actual_qty ?? it.ship_qty; });
    setActualQtys(next);
  };

  // Oct 13 2026, user's explicit ask (GRN Invoice-Variance Hold feature)
  // (invoiceQtys useState moved above liveAiMatchSignature - testing_agent iter294 TDZ crash fix)
  const [invoiceHolds, setInvoiceHolds] = useState([]);
  const [checkingHoldKey, setCheckingHoldKey] = useState(null);

  const initInvoiceQtys = (items) => {
    const next = {};
    (items || []).forEach((it) => { next[`${it.po_number}::${it.item_number}`] = it.actual_invoice_qty ?? it.actual_qty ?? it.ship_qty; });
    setInvoiceQtys(next);
  };

  const fetchInvoiceHolds = async (docCode) => {
    try {
      const { data } = await axios.get(`${API}/admin/grn/${docCode}/invoice-holds`);
      setInvoiceHolds(data.holds || []);
    } catch {
      setInvoiceHolds([]);
    }
  };

  const checkInvoiceException = async (poNumber, itemNumber) => {
    const key = `${poNumber}::${itemNumber}`;
    setCheckingHoldKey(key);
    try {
      const { data } = await axios.post(`${API}/admin/grn/invoice-hold/check`, { po_number: poNumber, item_number: itemNumber });
      setInvoiceHolds((prev) => prev.map((h) => (h._id === data._id ? data : h)));
      if (data.status === "approved") {
        toast.success(`Invoice Exception Approved for PO ${poNumber} Item ${itemNumber} (Invoice ${data.invoice_number}, Credit Memo ${data.credit_memo_number})`);
      } else if (data.last_check_outcome === "access_error") {
        // Oct 13 2026, user's explicit ask: don't say "not resolved yet"
        // when we couldn't even reach SAP to check - report the access
        // gap explicitly so it's not confused with a genuine "no
        // invoice yet" outcome.
        toast.error("Could not reach SAP to verify - exception check is still awaiting SAP access. Contact IT/SAP team.");
      } else {
        toast.info("Checked SAP - Invoice/Credit Memo not found yet. Check again later.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not check Invoice Exception status");
    } finally {
      setCheckingHoldKey(null);
    }
  };

  const lookup = async (targetCode) => {
    const value = (targetCode || code).trim();
    if (!value) return;
    setSearching(true);
    setSearchError("");
    setShipment(null);
    setResyncMismatch(null);
    try {
      const { data } = await axios.get(`${API}/admin/grn/lookup/${value}`);
      setShipment(data);
      setCode(value);
      resetApprovalForm();
      initActualQtys(data.items);
      initInvoiceQtys(data.items);
      fetchInvoiceHolds(value);
    } catch (err) {
      setSearchError(err?.response?.data?.detail || "No shipment found for this code");
    } finally {
      setSearching(false);
    }
  };

  const pollGrnJob = async (jobId) => {
    const deadline = Date.now() + 6 * 60 * 1000; // multi-line, multi-PO GRNs can take a couple of minutes
    while (Date.now() < deadline) {
      const { data: job } = await axios.get(`${API}/admin/grn/receipt-status/${jobId}`);
      setJobProgress(job);
      if (job.status === "running") setLastRunningPhase(job.phase);
      if (job.status === "done") return job.result;
      if (job.status === "failed") throw new Error(job.error || "Goods Receipt posting failed");
      await new Promise((r) => setTimeout(r, 2500));
    }
    throw new Error("This is taking longer than expected - check back shortly, it may still complete in the background.");
  };

  const approve = async (mode = "normal", overridePoDate = false) => {
    if (!siteId) {
      toast.error("Select a Site before approving");
      return;
    }
    if (!supplierDocNum.trim() || !billDate) {
      toast.error("Supplier Invoice Number and Supplier Invoice Date are required before approving");
      return;
    }
    if (!grnDatePhysical) {
      toast.error("GRN Date (Physical) is required before approving");
      return;
    }
    setBusy(true);
    setJobProgress(null);
    setLastRunningPhase(null);
    let jobStarted = false;
    try {
      const item_actual_qtys = shipment.items.map((it) => ({
        po_number: it.po_number, item_number: it.item_number,
        actual_qty: Number(actualQtys[`${it.po_number}::${it.item_number}`] ?? it.ship_qty),
        invoice_qty: Number(invoiceQtys[`${it.po_number}::${it.item_number}`] ?? actualQtys[`${it.po_number}::${it.item_number}`] ?? it.ship_qty),
      }));
      const endpoint = mode === "full_auto" ? "approve-full-auto" : mode === "full_auto_merge" ? "approve-full-auto-merge" : "approve";
      const { data } = await axios.post(`${API}/admin/grn/${shipment._id}/${endpoint}`, {
        supplier_doc_num: supplierDocNum, bill_date: billDate, grn_date_physical: grnDatePhysical, site_id: siteId, warehouse_id: warehouseId, item_actual_qtys,
        override_po_date_check: overridePoDate,
      });
      setShipment(data.shipment);
      if (data.shipment?.po_date_warning) {
        // Feb 2026, user's explicit ask - staggered rollout: a non-
        // blocking warning until Oct 26 2026, then the real hard block
        // (ShipmentValidationError, surfaced as the usual toast.error
        // in the catch block below) from that date on.
        toast.warning("PO date later than Invoice date", { description: data.shipment.po_date_warning, duration: 10000 });
      }
      jobStarted = true;
      const result = await pollGrnJob(data.job_id);
      setShipment(result);
      if (result.sap_sync_status === "posted" && result.sap_movement_status === "posted") {
        toast.success(mode === "full_auto_merge" ? `Merged GRN posted (ONE combined SAP Inbound Delivery) + stock moved to ${result.site_id}/${result.warehouse_id}` : mode === "full_auto" ? `Full Automated GRN posted + stock moved to ${result.site_id}/${result.warehouse_id} - no manual SAP step needed` : `Goods Receipt posted + stock moved to ${result.site_id}/${result.warehouse_id}`);
      } else if (result.sap_sync_status === "posted" && result.sap_movement_status === "not_applicable") {
        // Sep 20 2026, user's explicit ask ("we aren't moving any stock
        // now so we do not need this") - "not_applicable" is the
        // PERMANENT, EXPECTED state for every GRN now (skip_movement is
        // always on) - it must never fall into the "still pending"
        // warning branch below, which was designed for the OLD flow
        // where movement genuinely ran right after the GR and could
        // fail/lag.
        toast.success(mode === "full_auto_merge" ? "Merged GRN posted in SAP (ONE combined Inbound Delivery)" : mode === "full_auto" ? "Full Automated GRN posted in SAP" : "Goods Receipt posted in SAP");
      } else if (result.sap_sync_status === "posted") {
        toast.warning("Goods Receipt posted - warehouse movement still pending", { description: summarizeMovementResult(result.sap_movement_result), duration: 8000 });
      } else if (result.sap_sync_status === "awaiting_manual_gr") {
        toast.success("SAP Notification created - complete the GR in SAP, then click 'Re-check SAP'", { duration: 8000 });
      } else {
        // Oct 2026 fix - openDiagnosticsIfFailed used to auto-open the
        // per-PO Diagnostics Dialog here, which now visually stacks on
        // top of the progress Dialog above (which already surfaces
        // jobProgress.error) and blocks its Close button. The per-PO
        // "View Diagnostics" link in the item table below still opens
        // it manually.
        toast.error("Approved internally - SAP posting failed, use Retry Goods Receipt below", { description: summarizeGrResult(result.sap_gr_result), duration: 8000 });
      }
      loadPending();
      loadConfirmed();
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message;
      // Oct 27 2026, user's explicit ask ("Add Admin bypass for the PO
      // date is later the Invoice date in the GRN") - only offered to
      // admin/super_admin, and only for this exact block (never a
      // generic "retry anyway" for every failure) - re-sends the SAME
      // approval with override_po_date_check: true, which the backend
      // only ever honours for an admin/super_admin approver.
      if (isGrnAdmin && typeof detail === "string" && detail.includes("PO date is later than the Supplier Invoice date")) {
        toast.error("PO date later than Invoice date", {
          description: detail, duration: 15000,
          action: { label: "Admin Override & Approve Anyway", onClick: () => approve(mode, true) },
        });
      } else {
        toast.error("Approval failed", { description: detail });
      }
      lookup(shipment?._id);
      // Oct 2026, user's explicit ask ("modal should stay open, closable
      // any time") - a job that actually started keeps its progress
      // panel open (showing the real failed step) until the user
      // explicitly clicks Close; only a request that never even got a
      // job_id (nothing to show) clears `busy` here.
      if (!jobStarted) setBusy(false);
    }
  };

  // Sep 18 2026, user's explicit ask: BOTH the existing GRN process and
  // the new "Test Full Automated GRN" button now require one extra
  // confirmation step - a popup showing exactly the Invoice Number and
  // Bill Date about to be sent to SAP, before actually posting.
  const [confirmMode, setConfirmMode] = useState(null); // "normal" | "full_auto" | "full_auto_merge" | null
  const openApprovalConfirm = (mode) => {
    if (!siteId) {
      toast.error("Select a Site before approving");
      return;
    }
    if (!supplierDocNum.trim() || !billDate) {
      toast.error("Supplier Invoice Number and Supplier Invoice Date are required before approving");
      return;
    }
    if (!grnDatePhysical) {
      toast.error("GRN Date (Physical) is required before approving");
      return;
    }
    setConfirmMode(mode);
  };
  const confirmApproval = () => {
    const mode = confirmMode;
    setConfirmMode(null);
    approve(mode);
  };

  // Feb 2026, user's explicit ask - replaces the Oct 27 2026 "please
  // confirm you updated SAP yourself" trust-based dialog (this app
  // couldn't read/write SAP's own exchange rate yet at the time) with
  // the real thing: a foreign-currency (Import Purchase) shipment's
  // SAP exchange rate, live, as of the GRN Date (Physical) - see
  // grn_exchange_rate_service.py/sap_exchange_rate_client.py. Resets
  // (new fetch + re-confirmation required) whenever the shipment or
  // GRN Date (Physical) changes - mirrors the staleness pattern
  // already used for AI Match/variance remarks on this page.
  const fxCurrencyCode = (shipment?.items || []).map((it) => (it.currency || "").trim().toUpperCase()).find((c) => c && c !== "INR") || null;
  const [fxRateInfo, setFxRateInfo] = useState(null);
  const [fxRateLoading, setFxRateLoading] = useState(false);
  const [fxRateError, setFxRateError] = useState("");
  const [fxDecision, setFxDecision] = useState(null); // null | "not_matched"
  const [fxCorrectedRate, setFxCorrectedRate] = useState("");
  const [fxConfirmed, setFxConfirmed] = useState(false);
  const [fxConfirming, setFxConfirming] = useState(false);

  useEffect(() => {
    setFxDecision(null);
    setFxCorrectedRate("");
    setFxConfirmed(false);
    setFxRateInfo(null);
    setFxRateError("");
    if (!shipment?._id || !fxCurrencyCode || !grnDatePhysical) return;
    let cancelled = false;
    setFxRateLoading(true);
    axios.get(`${API}/admin/grn/${shipment._id}/exchange-rate`, { params: { grn_date_physical: grnDatePhysical } })
      .then(({ data }) => {
        if (cancelled) return;
        setFxRateInfo(data);
        setFxConfirmed(!!data.already_confirmed);
      })
      .catch((err) => { if (!cancelled) setFxRateError(err?.response?.data?.detail || "Could not fetch the SAP exchange rate"); })
      .finally(() => { if (!cancelled) setFxRateLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shipment?._id, fxCurrencyCode, grnDatePhysical]);

  const confirmFxMatched = async () => {
    setFxConfirming(true);
    try {
      await axios.post(`${API}/admin/grn/${shipment._id}/exchange-rate/confirm`, { grn_date_physical: grnDatePhysical, matched: true });
      setFxConfirmed(true);
      toast.success("Exchange rate confirmed");
    } catch (err) {
      toast.error("Could not confirm the exchange rate", { description: err?.response?.data?.detail || err.message });
    } finally {
      setFxConfirming(false);
    }
  };

  const submitFxCorrection = async () => {
    const rate = parseFloat(fxCorrectedRate);
    if (!rate || rate <= 0) {
      toast.error("Enter the exchange rate shown on the supplier's invoice");
      return;
    }
    setFxConfirming(true);
    try {
      await axios.post(`${API}/admin/grn/${shipment._id}/exchange-rate/confirm`, { grn_date_physical: grnDatePhysical, matched: false, corrected_rate: rate });
      setFxConfirmed(true);
      toast.success(`SAP exchange rate updated to ${rate}`);
    } catch (err) {
      toast.error("Could not update the SAP exchange rate", { description: err?.response?.data?.detail || err.message });
    } finally {
      setFxConfirming(false);
    }
  };

  const requestPostGrn = (mode) => {
    if (fxCurrencyCode && !fxConfirmed) {
      toast.error("Confirm the SAP exchange rate before posting this GRN");
      return;
    }
    openApprovalConfirm(mode);
  };

  const retryGoodsReceipt = async () => {
    setBusy(true);
    setJobProgress(null);
    setLastRunningPhase(null);
    let jobStarted = false;
    try {
      const { data } = await axios.post(`${API}/admin/grn/${shipment._id}/retry-goods-receipt`);
      jobStarted = true;
      const result = await pollGrnJob(data.job_id);
      setShipment(result);
      if (result.sap_sync_status === "posted") {
        toast.success("Goods Receipt posted to SAP");
      } else {
        // Oct 2026 fix - same stacked-Dialog conflict as approve() above.
        toast.warning("Still pending", { description: summarizeGrResult(result.sap_gr_result), duration: 8000 });
      }
    } catch (err) {
      toast.error("Retry failed", { description: err?.response?.data?.detail || err.message });
      if (!jobStarted) setBusy(false);
    }
  };

  const resetRetry = async () => {
    if (!window.confirm("Only do this after your SAP Admin has confirmed the underlying SAP error is fixed. Reset retry count for this shipment?")) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/grn/${shipment._id}/reset-retry`);
      setShipment(data);
      toast.success("Retry count reset - you can Retry the Goods Receipt again");
    } catch (err) {
      toast.error("Reset failed", { description: err?.response?.data?.detail || err.message });
    } finally {
      setBusy(false);
    }
  };

  // Sep 20 2026, "put_away_failed" fix - only re-runs Put Away
  // confirmation (never re-creates the SAP Inbound Delivery
  // Notification, which already exists) once the SAP admin confirms
  // the underlying business error (e.g. missing Financials/Purchasing
  // setup for a material) is fixed.
  const retryPutAway = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/grn/${shipment._id}/retry-put-away`);
      setShipment(data);
      toast.success("Retrying Put Away confirmation in SAP - this can take up to a couple of minutes");
    } catch (err) {
      toast.error("Retry failed", { description: err?.response?.data?.detail || err.message });
    } finally {
      setBusy(false);
    }
  };

  // Oct 2026, companion to retryPutAway - for the "SAP Release never
  // completed at all" case (_confirm_put_away_task's release_stuck
  // flag), re-polling the same stuck delivery can never resolve it;
  // this creates a brand new delivery instead, but ONLY makes sense
  // after staff have manually cancelled the stuck one in SAP first
  // (this app has no API to do that cancellation itself).
  const resendDelivery = async (poNumber) => {
    if (!window.confirm(`Only do this AFTER your SAP Admin has manually CANCELLED the stuck Inbound Delivery for PO ${poNumber} in SAP. Resend a fresh delivery for this PO now?`)) return;
    setBusy(true);
    setJobProgress(null);
    setLastRunningPhase(null);
    try {
      const { data } = await axios.post(`${API}/admin/grn/${shipment._id}/resend-delivery/${poNumber}`);
      toast.success(`Resending delivery for PO ${poNumber} to SAP - this can take up to a couple of minutes`);
      const result = await pollGrnJob(data.job_id);
      setShipment(result);
      toast.success(`PO ${poNumber} resend finished`);
    } catch (err) {
      toast.error("Resend failed", { description: err?.response?.data?.detail || err.message });
    } finally {
      setBusy(false);
      setJobProgress(null);
    }
  };

  const verifySapStatus = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/grn/${shipment._id}/verify-sap-status`);
      const fixed = (data.gr_verification_result || []).filter((c) => c.corrected_to);
      setShipment(data);
      if (fixed.length > 0) {
        toast.success(`SAP disagreed with ${fixed.length} PO(s) shown as Posted - corrected to Failed, Retry is now available`);
      } else {
        toast.success("SAP confirms every PO on this shipment is genuinely posted");
      }
    } catch (err) {
      toast.error("Could not verify against SAP", { description: err?.response?.data?.detail || err.message });
    } finally {
      setBusy(false);
    }
  };

  // Starts the visible 2-minute countdown, then runs the real
  // verifySapStatus check above once it reaches zero.
  const startVerifySapCountdown = () => {
    clearInterval(verifySapTimerRef.current);
    setVerifySapCountdown(VERIFY_SAP_WAIT_SEC);
    verifySapTimerRef.current = setInterval(() => {
      setVerifySapCountdown((s) => {
        if (s <= 1) {
          clearInterval(verifySapTimerRef.current);
          verifySapStatus();
          return null;
        }
        return s - 1;
      });
    }, 1000);
  };

  // Sep 17 2026, Manual GRN feature - "Re-check SAP" button, used once
  // staff have posted the actual Goods Receipt themselves in SAP.
  const recheckManualGr = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/grn/${shipment._id}/recheck-manual-gr`);
      setShipment(data);
      if (data.recheck_result === "matched") {
        toast.success("Quantities matched - Goods Receipt confirmed" + (data.sap_movement_status === "posted" ? ` + stock moved to ${data.site_id}/${data.warehouse_id}` : ""));
      } else if (data.recheck_result === "mismatch") {
        toast.error(`Quantity mismatch found on ${(data.manual_gr_mismatch_items || []).length} line(s) - you're blocked from approving new GRNs until this is resolved`, { duration: 10000 });
      }
      loadPending();
      loadConfirmed();
    } catch (err) {
      toast.error("Re-check failed", { description: err?.response?.data?.detail || err.message });
    } finally {
      setBusy(false);
    }
  };

  const retryMovement = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/grn/${shipment._id}/retry-movement`);
      setShipment(data);
      if (data.sap_movement_status === "posted") {
        toast.success(`Stock moved to ${data.site_id}/${data.warehouse_id}`);
      } else {
        toast.warning("Movement still pending", { description: summarizeMovementResult(data.sap_movement_result), duration: 8000 });
      }
    } catch (err) {
      toast.error("Retry failed", { description: err?.response?.data?.detail || err.message });
    } finally {
      setBusy(false);
    }
  };

  // Oct 2026, real incident shipment S001327 - "Resync from SAP" button
  // for a shipment stuck pending/failed/partial where SAP's OWN
  // confirmed-delivery data may already show more than this shipment's
  // own `sap_gr_result` currently reflects. First click is a safe
  // dry-run; if it reports a genuine partial mismatch, a second
  // "Accept SAP's quantity" click calls it again with accept_partial=true.
  const resyncFromSap = async (acceptPartial = false) => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/grn/${shipment._id}/resync-from-sap`, { accept_partial: acceptPartial });
      setShipment(data);
      const report = data.resync_report || [];
      const reconciled = report.filter((r) => r.outcome === "reconciled_posted");
      const mismatched = report.filter((r) => r.outcome === "partial_mismatch");
      const notReceived = report.filter((r) => r.outcome === "not_received_yet");
      // Oct 2026 testing-agent fix - an over-receipt or a PO with 2+
      // lines sharing the same product can never be auto-accepted, so
      // they never get the "Accept SAP's Quantity" follow-up button -
      // only shown separately, informational-only.
      const overReceived = report.filter((r) => r.outcome === "over_receipt_mismatch");
      const ambiguous = report.filter((r) => r.outcome === "ambiguous_multi_line");
      setResyncMismatch(mismatched.length > 0 ? { doc_code: shipment._id, items: mismatched } : null);
      if (reconciled.length > 0) {
        toast.success(`Reconciled ${reconciled.length} PO(s) from SAP's real confirmed delivery` + (data.sap_movement_status === "posted" ? ` + stock moved to ${data.site_id}/${data.warehouse_id}` : ""));
      }
      if (mismatched.length > 0) {
        toast.warning(`${mismatched.length} PO(s) only partially confirmed in SAP so far`, {
          description: mismatched.map((m) => `PO ${m.po_number}: ${Object.entries(m.mismatched_products || {}).map(([p, v]) => `${p} - expected ${v.expected}, SAP has ${v.sap_confirmed}`).join("; ")}`).join(" | "),
          duration: 12000,
        });
      }
      if (overReceived.length > 0) {
        toast.warning(`${overReceived.length} PO(s) show SAP confirming MORE than requested - needs manual review, can't be auto-accepted`, {
          description: overReceived.map((m) => `PO ${m.po_number}: ${Object.entries(m.mismatched_products || {}).map(([p, v]) => `${p} - expected ${v.expected}, SAP has ${v.sap_confirmed}`).join("; ")}`).join(" | "),
          duration: 12000,
        });
      }
      if (ambiguous.length > 0) {
        toast.warning(`${ambiguous.length} PO(s) have multiple lines for the same product - can't be safely auto-split, needs manual correction`, { duration: 10000 });
      }
      if (notReceived.length > 0 && reconciled.length === 0 && mismatched.length === 0 && overReceived.length === 0 && ambiguous.length === 0) {
        toast.info(`SAP has no confirmed delivery yet for ${notReceived.map((r) => r.po_number).join(", ")}`);
      }
      loadPending();
      loadConfirmed();
    } catch (err) {
      toast.error("Resync failed", { description: err?.response?.data?.detail || err.message });
    } finally {
      setBusy(false);
    }
  };

  // Feb 2026, user's explicit ask (real incident: shipment S001609,
  // PO 30870, Inbound Delivery 57443 cancelled directly in SAP by an
  // admin) - Admin/Super Admin only (isGrnAdmin, enforced again
  // server-side). The backend live-verifies with SAP before acting -
  // see /admin/grn/{doc_code}/cancel's own docstring - so this can
  // come back 400 ("still shows ... as Cancelled") even after the
  // click, which is expected/safe, not a bug.
  const cancelGrn = async () => {
    const targetCode = cancelGrnTargetCode;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/grn/${targetCode}/cancel`);
      if (shipment?._id === targetCode) setShipment(data);
      if (confirmedDetail?._id === targetCode) setConfirmedDetail(data);
      setCancelGrnOpen(false);
      toast.success(`${targetCode} cancelled - SAP confirmed its Inbound Delivery was Cancelled`);
      loadPending();
      loadConfirmed();
    } catch (err) {
      toast.error("Could not cancel this GRN", { description: err?.response?.data?.detail || err.message, duration: 10000 });
    } finally {
      setBusy(false);
    }
  };

  // Oct 28 2026, user's explicit ask ("Admin can pick Shipment & allow
  // that for bypass the PO data block") - admin-only grant/revoke of a
  // standing, shipment-specific PO-date bypass that ANY approver can
  // then use (see backend's prepare_approval `shipment_bypass_granted`).
  const [poDateBypassBusy, setPoDateBypassBusy] = useState(false);
  const togglePoDateBypass = async (allow) => {
    setPoDateBypassBusy(true);
    try {
      await axios.post(`${API}/admin/grn/${shipment._id}/po-date-bypass`, { allow });
      toast.success(allow ? `PO Date bypass allowed for ${shipment._id}` : `PO Date bypass revoked for ${shipment._id}`);
      lookup(shipment._id);
    } catch (err) {
      toast.error("Could not update the PO Date bypass", { description: err?.response?.data?.detail || err.message });
    } finally {
      setPoDateBypassBusy(false);
    }
  };

  const reject = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/grn/${shipment._id}/reject`, { reason: rejectReason });
      setShipment(data);
      setRejectOpen(false);
      setRejectReason("");
      toast.success("Shipment rejected");
      loadPending();
    } catch (err) {
      toast.error("Rejection failed", { description: err?.response?.data?.detail || err.message });
    } finally {
      setBusy(false);
    }
  };

  const isActionable = shipment && (shipment.status === "in_transit" || shipment.status === "discrepancy");

  // Oct 2026, user's explicit ask ("Remove: Mark Discrepancy button
  // (allow user to GRN short Qty against the Invoice full Qty and
  // price variance too. Add: pop window for enter the remark and
  // build a log for the all varience case Qty & Price with full
  // details") - replaces the old hasQtyDiscrepancy hard block (real
  // case: S001723, physical qty 8650 vs shipped 12250, supplier debit
  // note for the shortfall) AND the AI Match `rate_mismatch` issue's
  // generic "Accept as is" path (see aiMatchCleared below, which now
  // excludes rate_mismatch from that count on purpose). Both a qty
  // mismatch (Actual Received Qty vs Ship Qty, no AI/OCR dependency)
  // and a price mismatch (AI-detected invoice rate vs PO rate, user's
  // explicit choice - no manual "Actual Invoice Price" field) now go
  // straight through to Validate/Post once ONE remark is submitted via
  // the "Record Variance & Continue" popup - see submitVarianceRemark
  // and grn_variance_log_service.py's matching server-side check in
  // prepare_approval.
  const qtyVarianceItems = isActionable ? (shipment.items || []).filter((it) => {
    const key = `${it.po_number}::${it.item_number}`;
    const effectiveQty = Number(actualQtys[key] ?? it.actual_qty ?? it.ship_qty ?? 0);
    return Math.abs(effectiveQty - Number(it.ship_qty)) > 1e-6;
  }) : [];
  const priceVarianceIssues = (isActionable && aiMatch && !aiMatchStale) ? (aiMatch.issues || []).filter((iss) => iss.type === "rate_mismatch" && iss.ai_rate != null && iss.po_rate != null) : [];
  const hasLiveVariance = qtyVarianceItems.length > 0 || priceVarianceIssues.length > 0;

  // Byte-matches grn_variance_log_service.py's variance_signature
  // (same fixed 4-decimal formatting, same sort order) so a shipment
  // that already has a matching `variance_ack` from THIS exact live
  // state (e.g. right after a fresh lookup of an already-acknowledged
  // shipment) is recognised as acknowledged without a round trip - the
  // real enforcement is still the server-side copy in prepare_approval.
  const liveVarianceSignature = () => {
    if (!hasLiveVariance) return null;
    const qtyPart = qtyVarianceItems.map((it) => {
      const key = `${it.po_number}::${it.item_number}`;
      const effectiveQty = Number(actualQtys[key] ?? it.actual_qty ?? it.ship_qty ?? 0);
      return `${it.po_number}/${it.item_number}=${effectiveQty.toFixed(4)}`;
    }).sort().join(",");
    const pricePart = priceVarianceIssues.map((iss) => {
      const it = shipment.items[iss.grn_item_index];
      return `${it.po_number}/${it.item_number}=${Number(iss.ai_rate).toFixed(4)}`;
    }).sort().join(",");
    return `${qtyPart}|${pricePart}`;
  };
  const varianceAcknowledged = !hasLiveVariance || (!!shipment.variance_ack && shipment.variance_ack.signature === liveVarianceSignature());

  const openVarianceRemark = () => {
    setVarianceRemarkText("");
    setVarianceRemarkOpen(true);
  };

  const submitVarianceRemark = async () => {
    if (!varianceRemarkText.trim()) {
      toast.error("A remark is required");
      return;
    }
    setVarianceRemarkBusy(true);
    try {
      const item_actual_qtys = shipment.items.map((it) => ({
        po_number: it.po_number, item_number: it.item_number,
        // Oct 2026, testing_agent iter341 code review comment - must
        // match qtyVarianceItems/liveVarianceSignature's exact fallback
        // (it.actual_qty ?? it.ship_qty), not just it.ship_qty, so the
        // signature submitted here always matches what was just shown
        // in the popup (harmless today only because initActualQtys
        // happens to fill every key already, per the same comment).
        actual_qty: Number(actualQtys[`${it.po_number}::${it.item_number}`] ?? it.actual_qty ?? it.ship_qty),
      }));
      const { data } = await axios.post(`${API}/admin/grn/${shipment._id}/variance-remark`, { remark: varianceRemarkText, item_actual_qtys });
      setShipment(data);
      setVarianceRemarkOpen(false);
      toast.success("Variance logged - Validate GRN/Post GRN to SAP are now unlocked");
    } catch (err) {
      toast.error("Could not log variance", { description: err?.response?.data?.detail || err.message });
    } finally {
      setVarianceRemarkBusy(false);
    }
  };


  // Sep 21 2026, user's explicit ask - split "Post GRN in SAP" into 2
  // explicit steps ("Validate GRN" -> "Post GRN in SAP"), same pattern
  // already shipped for STO (StockTransferPage.js). Zero side effects
  // (see supplier_shipment_service.validate_grn's docstring) - catches
  // stale PO items, cancelled items, insufficient open qty, missing
  // site activation/valuation BEFORE the real, irreversible SAP post.
  const [validatingGrn, setValidatingGrn] = useState(false);
  const [grnValidated, setGrnValidated] = useState(false);
  const [grnValidationIssues, setGrnValidationIssues] = useState([]);
  const [showGrnValidationErrorBox, setShowGrnValidationErrorBox] = useState(false);
  // Feb 2026, user's explicit ask - separate "Validate Merge"/"Post GRN
  // Merge" test path (combined multi-PO SAP posting, see
  // sap_combined_grn_service.py) added side-by-side with the existing
  // buttons above for testing in preview. Kept as entirely separate
  // state so trying one path never disables/enables the other - per
  // user's explicit instruction, "Validate Merge" runs the EXACT SAME
  // validate_grn() checks as "Validate GRN" (same endpoint), only
  // "Post GRN Merge" posts differently.
  const [validatingGrnMerge, setValidatingGrnMerge] = useState(false);
  const [grnMergeValidated, setGrnMergeValidated] = useState(false);
  const [grnMergeValidationIssues, setGrnMergeValidationIssues] = useState([]);
  const [showGrnMergeValidationErrorBox, setShowGrnMergeValidationErrorBox] = useState(false);

  useEffect(() => {
    setGrnValidated(false);
    setGrnMergeValidated(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shipment?._id, siteId, actualQtys, invoiceQtys]);

  const runGrnValidation = async () => {
    setValidatingGrn(true);
    try {
      const item_actual_qtys = shipment.items.map((it) => ({
        po_number: it.po_number, item_number: it.item_number,
        actual_qty: Number(actualQtys[`${it.po_number}::${it.item_number}`] ?? it.ship_qty),
        invoice_qty: Number(invoiceQtys[`${it.po_number}::${it.item_number}`] ?? actualQtys[`${it.po_number}::${it.item_number}`] ?? it.ship_qty),
      }));
      const { data: jobStart } = await axios.post(`${API}/admin/grn/${shipment._id}/validate`, {
        supplier_doc_num: supplierDocNum, bill_date: billDate, site_id: siteId, warehouse_id: warehouseId, item_actual_qtys,
      });
      // Oct 2026 fix (production latency real incident - "GRN
      // Validation taking 90s+") - this endpoint now returns a job_id
      // instantly; poll for the real result instead of awaiting one
      // long HTTP request (same pattern pollGrnJob already uses).
      const deadline = Date.now() + 3 * 60 * 1000;
      let data = null;
      while (Date.now() < deadline) {
        const { data: job } = await axios.get(`${API}/admin/grn/validate/status/${jobStart.job_id}`);
        if (job.status === "done") { data = job.result; break; }
        if (job.status === "failed") throw new Error(job.error || "Could not validate this GRN.");
        await new Promise((r) => setTimeout(r, 2000));
      }
      if (!data) throw new Error("This is taking longer than expected - check back shortly.");
      if (data.ok) {
        setGrnValidated(true);
        toast.success("Validation passed - PO items, open quantity, activation and valuation all look good.");
      } else {
        setGrnValidated(false);
        setGrnValidationIssues(data.issues || []);
        setShowGrnValidationErrorBox(true);
      }
    } catch (err) {
      setGrnValidated(false);
      setGrnValidationIssues([{ po_number: null, item_number: null, field: "sap", level: "error", message: err?.response?.data?.detail || err?.message || "Could not validate this GRN." }]);
      setShowGrnValidationErrorBox(true);
    } finally {
      setValidatingGrn(false);
    }
  };

  // "Validate Merge" - deliberately identical logic to runGrnValidation
  // above (same /validate endpoint, same validate_grn() checks) per
  // user's explicit instruction - only the state it writes to differs,
  // so it can be removed as a standalone block if this test path is
  // dropped.
  const runGrnMergeValidation = async () => {
    setValidatingGrnMerge(true);
    try {
      const item_actual_qtys = shipment.items.map((it) => ({
        po_number: it.po_number, item_number: it.item_number,
        actual_qty: Number(actualQtys[`${it.po_number}::${it.item_number}`] ?? it.ship_qty),
        invoice_qty: Number(invoiceQtys[`${it.po_number}::${it.item_number}`] ?? actualQtys[`${it.po_number}::${it.item_number}`] ?? it.ship_qty),
      }));
      const { data: jobStart } = await axios.post(`${API}/admin/grn/${shipment._id}/validate`, {
        supplier_doc_num: supplierDocNum, bill_date: billDate, site_id: siteId, warehouse_id: warehouseId, item_actual_qtys,
      });
      const deadline = Date.now() + 3 * 60 * 1000;
      let data = null;
      while (Date.now() < deadline) {
        const { data: job } = await axios.get(`${API}/admin/grn/validate/status/${jobStart.job_id}`);
        if (job.status === "done") { data = job.result; break; }
        if (job.status === "failed") throw new Error(job.error || "Could not validate this GRN.");
        await new Promise((r) => setTimeout(r, 2000));
      }
      if (!data) throw new Error("This is taking longer than expected - check back shortly.");
      if (data.ok) {
        setGrnMergeValidated(true);
        toast.success("Validation passed - PO items, open quantity, activation and valuation all look good.");
      } else {
        setGrnMergeValidated(false);
        setGrnMergeValidationIssues(data.issues || []);
        setShowGrnMergeValidationErrorBox(true);
      }
    } catch (err) {
      setGrnMergeValidated(false);
      setGrnMergeValidationIssues([{ po_number: null, item_number: null, field: "sap", level: "error", message: err?.response?.data?.detail || err?.message || "Could not validate this GRN." }]);
      setShowGrnMergeValidationErrorBox(true);
    } finally {
      setValidatingGrnMerge(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F2F4F7] font-sans" data-testid="grn-approval-page">
      <header className="min-h-16 bg-[#0E7C86] shadow-[0_1px_3px_0_rgba(16,24,40,0.15)] flex items-center justify-between px-3 sm:px-5 shrink-0 z-10 gap-2 sm:gap-4">
        <div className="flex items-center gap-3 shrink-0" data-testid="app-title">
          <div className="w-8 h-8 rounded-lg bg-white/15 flex items-center justify-center shrink-0">
            <Shield size={18} weight="fill" className="text-white" />
          </div>
          <div className="flex flex-col leading-tight">
            <span className="font-heading text-[16px] font-bold text-white tracking-tight">Materials Hub</span>
            <span className="font-sans text-[12px] text-white/70 hidden sm:inline">GRN Approval</span>
          </div>
        </div>
        <div className="w-px h-7 bg-white/25 shrink-0" />
        <div className="flex items-center gap-3 flex-1 justify-start min-w-0">
          <NavTabs />
        </div>
        <SapConnectionStatus />
        <ErpConnectionStatus />
      </header>
      <div className="flex-1 overflow-auto max-w-[1400px] w-full mx-auto p-4 md:p-6 space-y-4">
        <div className="flex items-center gap-2 mb-1">
          <Truck size={18} weight="fill" className="text-[#004B87]" />
          <h1 className="font-heading text-xl font-bold text-[#1D2939]">GRN Approval</h1>
        </div>
        <p className="text-sm text-[#475467]">Enter the shipment code from the delivery paperwork, physically match the goods and supplier invoice, then approve.</p>

        {/* Sep 17 2026, Manual GRN feature - self-service toggle between
            the Playwright-automated Goods Receipt and posting it manually
            in SAP, plus the admin "Blocked Users" override entry point.
            Sep 16 2026, user's explicit ask - Manual GRN is now the ONLY
            path for Supplier GRN; toggle kept visible but disabled (not
            removed) in case Playwright is ever wanted back. */}
        <div className="bg-white border border-[#D0D5DD] rounded-sm p-3 flex flex-wrap items-center justify-between gap-3 shadow-[0_1px_2px_0_rgba(16,24,40,0.05)]">
          <div className="flex items-center gap-3">
            <Switch
              checked={true}
              disabled={true}
              data-testid="grn-manual-mode-toggle"
            />
            <div className="leading-tight">
              <div className="text-sm font-semibold text-[#1D2939]">Manual GRN mode</div>
              <div className="text-xs text-[#667085]">Approvals only create the SAP Notification - you post the Goods Receipt yourself in SAP</div>
            </div>
          </div>
          {isGrnAdmin && (
            <Button size="sm" variant="outline" onClick={openBlockedUsers} className="h-8 rounded-sm text-xs" data-testid="grn-open-blocked-users-button">
              <LockKey size={14} className="mr-1" /> Blocked Users
            </Button>
          )}
        </div>

        {user?.grn_blocked_shipment && (
          <div className="text-sm px-3 py-2.5 rounded-sm border border-[#E02424]/30 bg-[#E02424]/5 text-[#B91C1C] flex items-center gap-2" data-testid="grn-user-blocked-banner">
            <WarningCircle size={16} weight="fill" />
            <span>You are blocked from approving new GRNs - shipment <strong>{user.grn_blocked_shipment}</strong> has an unresolved quantity mismatch. {user.grn_blocked_reason}</span>
          </div>
        )}

        <div className="bg-white border border-[#D0D5DD] rounded-sm p-3 flex items-center gap-3 shadow-[0_1px_2px_0_rgba(16,24,40,0.05)]">
          <Input
            placeholder="e.g. S000003"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            onKeyDown={(e) => e.key === "Enter" && lookup()}
            className="h-8 font-data uppercase w-64 text-[13px] rounded-sm border-[#D0D5DD] focus-visible:border-[#004B87] focus-visible:ring-1 focus-visible:ring-[#004B87]"
            maxLength={10}
            data-testid="grn-code-input"
          />
          <Button onClick={() => lookup()} disabled={searching} className="h-8 rounded-sm bg-[#004B87] hover:bg-[#003A6A] active:bg-[#00294D] text-[13px] font-bold transition-colors" data-testid="grn-code-search-button">
            <MagnifyingGlass size={14} className="mr-1" /> {searching ? "Searching..." : "Lookup"}
          </Button>
        </div>
        {searchError && <div className="text-sm text-[#B91C1C] mt-2" data-testid="grn-search-error">{searchError}</div>}

        {shipment && (
          <div className="bg-white border border-[#D0D5DD] rounded-sm shadow-[0_1px_2px_0_rgba(16,24,40,0.05)] p-5" data-testid="grn-shipment-detail">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-lg font-data font-bold text-[#004B87]">{shipment._id}</div>
                <div className="text-sm text-[#475467]">{shipment.company_name} ({shipment.vendor_code}) · {[...new Set(shipment.items.map((it) => it.po_number))].map((p) => `PO ${p}`).join(", ")}</div>
                {shipment.created_on_behalf_by && (
                  <div className="text-xs text-[#B54708] mt-0.5" data-testid="grn-created-on-behalf-flag">Created on behalf by {shipment.created_on_behalf_by}</div>
                )}
              </div>
              <StatusText tone={resolveStatusBadge(shipment).tone} testId="grn-status-badge" className="text-sm">{resolveStatusBadge(shipment).label}</StatusText>
            </div>

            {shipment.buyer_entity_name && (
              <div className="mt-2 text-xs text-[#475467]" data-testid="grn-buyer-entity">
                Buyer entity: <span className="font-semibold text-[#1D2939]">{shipment.buyer_entity_name}</span>
                {eligibleSites.length === 1 && <span> · Site locked to {eligibleSites[0]} for this entity</span>}
              </div>
            )}

            {/* Oct 28 2026, user's explicit ask - the Supplier Invoice
                Number/Date typed in at approval time wasn't visible again
                here once a shipment was looked back up post-confirmation,
                making it easy to miss a staff member re-using the same
                invoice number across two different shipments (real
                incident: S000569/S001545, both typed as 262709-001681). */}
            {shipment.supplier_doc_num && (
              <div className="mt-2 text-xs text-[#475467]" data-testid="grn-shipment-invoice-number">
                Supplier Invoice No: <span className="font-data font-semibold text-[#1D2939]">{shipment.supplier_doc_num}</span>
                {shipment.bill_date && <span> · Supplier Invoice Date: <span className="font-data font-semibold text-[#1D2939]">{shipment.bill_date}</span></span>}
              </div>
            )}

            {/* Oct 29 2026, user's explicit ask ("duplicate inv warning") -
                same check/state as the live approval-form banner below,
                shown here too since an ALREADY-approved shipment (no
                approval form visible, isActionable is false) is exactly
                the case that caught the real S000569/S001545 incident. */}
            {!isActionable && dupInvoiceWarning && (
              <div className="mt-2 flex items-start gap-2 bg-[#FFFAEB] border border-[#FEDF89] rounded-sm px-3 py-2 text-xs text-[#B54708]" data-testid="grn-duplicate-invoice-warning-confirmed">
                <WarningCircle size={15} className="shrink-0 mt-0.5" />
                <span>
                  Invoice No. <strong className="font-data">{shipment.supplier_doc_num}</strong> is also recorded on shipment{" "}
                  <strong className="font-data">{dupInvoiceWarning.doc_code}</strong>
                  {dupInvoiceWarning.po_numbers?.length > 0 && <> (PO {dupInvoiceWarning.po_numbers.join(", ")})</>}
                  {dupInvoiceWarning.bill_date && <>, dated {dupInvoiceWarning.bill_date}</>} for this same supplier - likely a re-typed/copy-pasted number. Worth a Finance double-check before this invoice is processed.
                </span>
              </div>
            )}

            {/* Oct 28 2026, user's explicit ask ("Admin can pick Shipment
                & allow that for bypass the PO data block") - admin grants
                it per-shipment here; any approver (not just admins) who
                then approves THIS exact shipment can proceed past the
                "PO date later than Invoice date" block. Everyone viewing
                the shipment sees the badge once granted, so a regular
                user knows why it won't block them. */}
            {isGrnAdmin && shipment.status === "in_transit" && (
              shipment.po_date_bypass_allowed ? (
                <div className="mt-2 flex items-center gap-2 flex-wrap" data-testid="grn-po-date-bypass-granted-banner">
                  <StatusText tone="warning" testId="grn-po-date-bypass-granted-badge" className="text-xs">
                    PO Date Bypass Allowed for this shipment (granted by {shipment.po_date_bypass_granted_by})
                  </StatusText>
                  <Button size="sm" variant="outline" disabled={poDateBypassBusy} onClick={() => togglePoDateBypass(false)} className="h-6 px-2 text-[11px] rounded-sm" data-testid="grn-po-date-bypass-revoke-button">
                    Revoke
                  </Button>
                </div>
              ) : (
                <Button size="sm" variant="outline" disabled={poDateBypassBusy} onClick={() => togglePoDateBypass(true)} className="mt-2 h-7 text-xs rounded-sm border-[#B54708] text-[#B54708]" data-testid="grn-po-date-bypass-grant-button" title="Lets ANY user approve this specific shipment past the 'PO date later than Invoice date' block">
                  Allow PO Date Bypass for this shipment
                </Button>
              )
            )}
            {!isGrnAdmin && shipment.po_date_bypass_allowed && shipment.status === "in_transit" && (
              <StatusText tone="warning" testId="grn-po-date-bypass-granted-badge" className="text-xs mt-2 inline-block">
                An admin has allowed a PO Date bypass for this shipment
              </StatusText>
            )}

            {siteAccessBlocked && (
              <div className="mt-3 text-sm px-3 py-2.5 rounded-sm border border-[#E02424]/30 bg-[#E02424]/5 text-[#B91C1C]" data-testid="grn-site-access-blocked-banner">
                <div className="flex items-center gap-2 font-semibold"><WarningCircle size={16} weight="fill" /> You're not bound to any site for {shipment.buyer_entity_name}</div>
                <div className="mt-1 text-xs">Ask a Super Admin to grant you Store Assignment access to one of this entity's sites before this shipment can be approved.</div>
              </div>
            )}

            {shipment.status === "discrepancy" && (
              <div className="mt-4 text-sm px-3 py-2.5 rounded-sm border border-[#E02424]/30 bg-[#E02424]/5 text-[#B91C1C]" data-testid="grn-discrepancy-banner">
                <div className="flex items-center gap-2 font-semibold"><WarningCircle size={16} weight="fill" /> Discrepancy logged by {shipment.discrepancy_marked_by}</div>
                <div className="mt-1">{shipment.discrepancy_reason}</div>
                <div className="mt-1 text-xs">
                  Flagged items: {(shipment.discrepancy_items || []).map((it) => `${it.po_number}/${it.item_number}`).join(", ")}
                </div>
                <div className="mt-1 text-xs text-[#475467]">Ask the vendor to edit this shipment - saving their edit will automatically bring it back to "In Transit" for re-review.</div>
                <div className="mt-1 text-xs text-[#475467]">Already certain this is the real received quantity (e.g. a debit note is being raised for the shortfall)? Use the "Record Variance &amp; Continue" button below instead of waiting for the vendor.</div>
              </div>
            )}

            {/* Oct 13 2026, user's explicit ask (GRN Invoice-Variance Hold
               feature) - any PO+Item on this shipment that's currently on
               hold (short supply detected on an EARLIER GRN) shows here,
               with the 3-state "Request/Check Invoice Exception Approval"
               button, BEFORE the user even tries to Validate/Approve. */}
            {invoiceHolds.filter((h) => h.status === "pending").map((h) => {
              // Nov 2026, user's explicit ask - a hold is keyed by PO+Item,
              // not by GRN, so it still shows up when re-opening the very
              // shipment that CREATED it (triggered_by_doc_code === this
              // shipment's own _id). That shipment already completed/
              // approved fine (raise_if_invoice_held never blocks the
              // trigger itself) - only FUTURE GRNs on this PO/Item are
              // blocked. Shown as a calmer informational note here instead
              // of the red "blocked" banner, which was confusingly
              // self-referential ("blocked by shipment S000071" while
              // viewing S000071 itself).
              const isSelfTriggered = h.triggered_by_doc_code === shipment._id;
              return (
                <div
                  key={h._id}
                  className={`mt-4 text-sm px-3 py-2.5 rounded-sm border ${isSelfTriggered ? "border-[#B2DDFF] bg-[#EFF8FF] text-[#1D3A6E]" : "border-[#E02424]/30 bg-[#E02424]/5 text-[#B91C1C]"}`}
                  data-testid={`grn-invoice-hold-banner-${h._id}`}
                >
                  <div className="flex items-center gap-2 font-semibold">
                    {isSelfTriggered ? <Info size={16} weight="fill" /> : <WarningCircle size={16} weight="fill" />}
                    Invoice Exception Hold - PO {h.po_number} Item {h.item_number}
                  </div>
                  <div className="mt-1 text-xs">
                    {isSelfTriggered ? (
                      <>
                        This shipment ({h.triggered_by_doc_code}) over-invoiced this item (invoiced {h.invoice_qty}, received {h.received_qty}),
                        placing PO {h.po_number} Item {h.item_number} on hold. Any NEW GRN for this PO/Item will be blocked until SAP confirms
                        the matching Invoice + Credit Memo are Posted.
                      </>
                    ) : (
                      <>
                        Short supply detected on shipment {h.triggered_by_doc_code} (invoiced {h.invoice_qty}, received {h.received_qty}).
                        No GRN can be approved for this PO/Item until resolved.
                      </>
                    )}
                  </div>
                  {h.last_check_outcome === "access_error" && (
                    <div className="mt-1 text-xs font-semibold" data-testid={`grn-invoice-hold-access-error-${h._id}`}>
                      Last check could not reach SAP - exception still awaiting SAP access. Contact IT/SAP team.
                    </div>
                  )}
                  {h.last_check_outcome === "not_found" && (
                    <div className="mt-1 text-xs text-[#475467]" data-testid={`grn-invoice-hold-not-found-${h._id}`}>
                      SAP checked successfully - Invoice/Credit Memo not found yet.
                    </div>
                  )}
                  <Button
                    size="sm"
                    disabled={checkingHoldKey === h._id}
                    onClick={() => checkInvoiceException(h.po_number, h.item_number)}
                    className={`mt-2 rounded-sm text-white transition-colors duration-150 ${isSelfTriggered ? "bg-[#175CD3] hover:bg-[#1849A9]" : "bg-[#B91C1C] hover:bg-[#991B1B]"}`}
                    data-testid={`grn-invoice-hold-check-button-${h._id}`}
                  >
                    {checkingHoldKey === h._id ? "Checking SAP\u2026" : h.last_checked_at ? "Check Exception Approval Status" : "Request Invoice Exception Approval"}
                  </Button>
                </div>
              );
            })}

            {isActionable && (
              <div className="flex items-start gap-2 bg-[#EFF8FF] border border-[#B2DDFF] rounded-sm px-3 py-2 mt-4 mb-1" data-testid="grn-actual-qty-guidance-banner">
                <Info size={18} weight="fill" className="text-[#175CD3] shrink-0 mt-0.5" />
                <div className="text-xs text-[#1D3A6E]">
                  <span className="font-bold">Actual Received Qty - enter what you physically received, not the Ship Qty or Invoice Qty.</span>{" "}
                  Open the package/shipment and count it yourself. If less (or more) arrived than what the supplier shipped or billed, enter that real count here - this is exactly what gets posted as the Goods Receipt in SAP.
                </div>
              </div>
            )}

            <table className="border-collapse w-full text-xs mt-2 border border-[#D0D5DD] rounded-sm overflow-hidden">
              <thead className="bg-[#EAECF0] text-[#344054] text-xs font-bold font-heading tracking-wide">
                <tr>
                  <th className="border border-[#D0D5DD] p-1.5 text-left">PO Number</th>
                  <th className="border border-[#D0D5DD] p-1.5 text-left">Item</th>
                  <th className="border border-[#D0D5DD] p-1.5 text-left">Item Code</th>
                  <th className="border border-[#D0D5DD] p-1.5 text-left">Description</th>
                  <th className="border border-[#D0D5DD] p-1.5 text-right">Ship Qty</th>
                  <th className="border border-[#D0D5DD] p-1.5 text-right">Open PO Qty</th>
                  <th className="border border-[#D0D5DD] p-1.5 text-right">Actual Received Qty</th>
                  <th className="border border-[#D0D5DD] p-1.5 text-right">Actual Invoice Qty</th>
                  <th className="border border-[#D0D5DD] p-1.5 text-right">PO Price</th>
                  <th className="border border-[#D0D5DD] p-1.5 text-right">Line Value</th>
                  {shipment.sap_gr_result?.per_po && (
                    <th className="border border-[#D0D5DD] p-1.5 text-left">SAP Inbound Delivery #</th>
                  )}
                  {shipment.sap_gr_result?.per_po && (
                    <th className="border border-[#D0D5DD] p-1.5 text-left">PO Status</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {shipment.items.map((it, i) => {
                  const key = `${it.po_number}::${it.item_number}`;
                  const effectiveQty = Number(actualQtys[key] ?? it.actual_qty ?? it.ship_qty ?? 0);
                  const lineValue = it.unit_price != null ? effectiveQty * it.unit_price : null;
                  // Sep 14 2026 fix (user's real report: "diagnostics not
                  // showing full details" - the single "View Diagnostics"
                  // button only ever surfaced whichever ONE PO had an
                  // issue, with zero way to check any OTHER PO's own
                  // status on the same multi-PO shipment) - resolve each
                  // line's own PO's result directly, per-row.
                  const poResult = shipment.sap_gr_result?.per_po?.find((p) => p.po_number === it.po_number);
                  return (
                    <tr key={i} className="bg-white odd:bg-[#F9FAFB]">
                      <td className="border border-[#D0D5DD] px-2 py-1 font-data">
                        {it.po_number}
                        {it.sap_po_number && (
                          <div className="text-[10px] text-[#475467] font-sans" data-testid={`grn-item-printed-po-number-${i}`}>Printed PO #: {it.sap_po_number}</div>
                        )}
                      </td>
                      <td className="border border-[#D0D5DD] px-2 py-1 font-data">{it.item_number}</td>
                      <td className="border border-[#D0D5DD] px-2 py-1 font-data font-semibold" data-testid={`grn-item-code-${i}`}>{it.product_id || "\u2014"}</td>
                      <td className="border border-[#D0D5DD] px-2 py-1">{it.description}</td>
                      <td className="border border-[#D0D5DD] px-2 py-1 text-right font-data font-semibold">{it.ship_qty} {it.unit_of_measure}</td>
                      <td className="border border-[#D0D5DD] px-2 py-1 text-right font-data text-[#475467]" data-testid={`grn-item-open-po-qty-${i}`}>
                        {it.open_po_qty ?? it.po_qty}
                        {it.over_delivered && (
                          <div className="text-[10px] text-[#B42318] font-sans font-semibold whitespace-nowrap" data-testid={`grn-item-over-delivered-badge-${i}`} title="SAP shows this item already delivered past its ordered quantity - Open PO Qty is shown as 0, not negative.">
                            Over-delivered in SAP
                          </div>
                        )}
                        {it.extra_tolerance_pct > 0 && (
                          <div className="text-[10px] text-[#027A48] font-sans font-semibold whitespace-nowrap" data-testid={`grn-item-tolerance-badge-${i}`} title="This PO's source PR was approved with an Extra % over-delivery allowance - receiving above Open PO Qty is allowed up to this limit.">
                            +{it.extra_tolerance_pct}% extra OK (PR)
                          </div>
                        )}
                      </td>
                      <td className="border border-[#D0D5DD] px-2 py-1 text-right">
                        {isActionable ? (
                          <Input
                            type="number"
                            value={actualQtys[key] ?? ""}
                            onChange={(e) => setActualQtys((prev) => ({ ...prev, [key]: e.target.value }))}
                            className="w-24 h-7 text-right font-data rounded-sm border-[#D0D5DD] ml-auto"
                            data-testid={`grn-actual-qty-input-${key}`}
                          />
                        ) : (
                          <span className="font-data font-semibold" data-testid={`grn-actual-qty-value-${key}`}>{it.actual_qty ?? it.ship_qty} {it.unit_of_measure}</span>
                        )}
                      </td>
                      <td className="border border-[#D0D5DD] px-2 py-1 text-right">
                        {isActionable ? (
                          <Input
                            type="number"
                            value={invoiceQtys[key] ?? ""}
                            onChange={(e) => setInvoiceQtys((prev) => ({ ...prev, [key]: e.target.value }))}
                            className="w-24 h-7 text-right font-data rounded-sm border-[#D0D5DD] ml-auto"
                            data-testid={`grn-invoice-qty-input-${key}`}
                          />
                        ) : (
                          <span className="font-data font-semibold" data-testid={`grn-invoice-qty-value-${key}`}>{it.actual_invoice_qty ?? it.actual_qty ?? it.ship_qty} {it.unit_of_measure}</span>
                        )}
                        {aiMatch && !aiMatchStale && (() => {
                          const rowIssues = aiMatch.issues.filter((iss) => iss.grn_item_index === i);
                          // Nov 2026, user's explicit ask (current-price
                          // invoicing suppliers) - a row whose ONLY issue
                          // is an expected, named price variance isn't
                          // really an "AI found a problem" situation, so
                          // it gets a neutral badge instead of the amber
                          // "issue" one (a remark is still mandatory via
                          // the Variance banner/button below regardless -
                          // this is cosmetic only).
                          const onlyKnownVariablePrice = rowIssues.length > 0 && rowIssues.every((r) => r.type === "rate_mismatch" && r.known_variable_price_supplier);
                          if (rowIssues.length === 0) {
                            return <div className="text-[10px] text-[#027A48] font-sans mt-0.5" data-testid={`grn-ai-match-row-badge-${i}`}>AI: Matched</div>;
                          }
                          if (onlyKnownVariablePrice) {
                            return <div className="text-[10px] text-[#475467] font-sans mt-0.5" data-testid={`grn-ai-match-row-badge-${i}`} title={rowIssues.map((r) => r.message).join(" | ")}>AI: Current-price supplier</div>;
                          }
                          return <div className="text-[10px] text-[#B54708] font-sans mt-0.5" data-testid={`grn-ai-match-row-badge-${i}`} title={rowIssues.map((r) => r.message).join(" | ")}>AI: {rowIssues.length} issue{rowIssues.length > 1 ? "s" : ""}</div>;
                        })()}
                      </td>
                      <td className="border border-[#D0D5DD] px-2 py-1 text-right font-data text-[#475467]" data-testid={`grn-item-unit-price-${i}`}>
                        {it.unit_price != null ? `${it.currency || ""} ${fmtUnitPrice(it.unit_price)}` : "-"}
                      </td>
                      <td className="border border-[#D0D5DD] px-2 py-1 text-right font-data font-semibold text-[#1D2939]" data-testid={`grn-item-line-value-${i}`}>
                        {lineValue != null ? `${it.currency || ""} ${lineValue.toFixed(2)}` : "-"}
                      </td>
                      {shipment.sap_gr_result?.per_po && (
                        <td className="border border-[#D0D5DD] px-2 py-1 font-data" data-testid={`grn-item-inbound-delivery-id-${i}`}>
                          {poResult?.inbound_delivery_id || "-"}
                        </td>
                      )}
                      {shipment.sap_gr_result?.per_po && (
                        <td className="border border-[#D0D5DD] px-2 py-1 whitespace-nowrap" data-testid={`grn-item-po-status-${i}`}>
                          {poResult?.status === "notification_created" ? (
                            <span className="text-[11px] font-semibold text-[#3538CD]">Notification Created</span>
                          ) : poResult ? (
                            <button
                              type="button"
                              className={`text-[11px] font-semibold hover:underline ${poResult.release_stuck ? "text-[#B42318]" : poResult.status !== "posted" ? (poResult.status === "skipped" ? "text-[#B54708]" : "text-[#B42318]") : poDiagnostics(shipment, poResult).movement_issues.length > 0 ? "text-[#B54708]" : "text-[#027A48]"}`}
                              onClick={() => setDiagnosticsModal(poDiagnostics(shipment, poResult))}
                              data-testid={`grn-item-diagnostics-link-${i}`}
                            >
                              {poResult.release_stuck ? "Receipt Incomplete" : poResult.status === "posted" ? (poDiagnostics(shipment, poResult).movement_issues.length > 0 ? "Posted (movement failed)" : "Posted") : poResult.status === "skipped" ? "Skipped" : "Failed"} - Diagnostics
                            </button>
                          ) : (
                            <span className="text-[11px] text-[#98A2B3]">Not attempted yet</span>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
                {isActionable && (
                  <tr><td colSpan={shipment.sap_gr_result?.per_po ? 11 : 9} className="border border-[#D0D5DD] px-2 py-1 text-xs text-[#475467]">Actual Qty defaults to Ship Qty - adjust only if the physical count differs. PO Price/Line Value shown is the actual rate from the Purchase Order in SAP (for reference only).</td></tr>
                )}
                {(() => {
                  const valuedItems = shipment.items.filter((it) => it.unit_price != null);
                  if (!valuedItems.length) return null;
                  const total = valuedItems.reduce((sum, it, idx) => {
                    const key = `${it.po_number}::${it.item_number}`;
                    const effectiveQty = Number(actualQtys[key] ?? it.actual_qty ?? it.ship_qty ?? 0);
                    return sum + effectiveQty * it.unit_price;
                  }, 0);
                  const currency = valuedItems[0].currency || "";
                  return (
                    <tr className="bg-[#F2F4F7]">
                      <td colSpan={9} className="border border-[#D0D5DD] px-2 py-1.5 text-right text-xs font-bold text-[#344054] font-heading">Total (Matches Bill Amount)</td>
                      <td className="border border-[#D0D5DD] px-2 py-1.5 text-right font-data font-bold text-[#004B87]" data-testid="grn-items-total-value">
                        {currency} {total.toFixed(2)}
                      </td>
                      {shipment.sap_gr_result?.per_po && <td className="border border-[#D0D5DD]" colSpan={2}></td>}
                    </tr>
                  );
                })()}
              </tbody>
            </table>

            {/* Oct 2026, user's explicit ask ("IDENTICAL to the Inbound
                STO progress modal - it's a simple Dialog with a
                horizontal green Progress bar, no vertical timeline")
                - mirrors ReceiptDetailModal.jsx's exact structure:
                Dialog -> Progress bar + step label -> spinner while
                running -> red error box on failure -> green banner +
                Goods Movement/Inbound Delivery ID on done -> Close. */}
            <Dialog open={busy && !!jobProgress} onOpenChange={(open) => !open && closeGrnProgress()}>
              <DialogContent className="max-w-lg rounded-sm" data-testid="grn-job-progress">
                {jobProgress && (() => {
                  const doneDoc = jobProgress.result || shipment;
                  const { pct, label, runningLabel, failed, done } = computeGrnProgress(jobProgress, lastRunningPhase, shipment, doneDoc);
                  const inboundIds = [...new Set((doneDoc?.sap_gr_result?.per_po || []).map((p) => p.inbound_delivery_id).filter(Boolean))];
                  const gmIds = [...new Set((doneDoc?.sap_movement_result?.per_item || []).filter((p) => p.ok && p.external_id).map((p) => p.external_id))];
                  return (
                    <>
                      <DialogHeader>
                        <DialogTitle>{shipment?._id} - Posting to SAP</DialogTitle>
                        <DialogDescription>Live status of this Goods Receipt's SAP posting</DialogDescription>
                      </DialogHeader>
                      <div>
                        <Progress value={pct} className="h-1.5" data-testid="grn-job-progress-bar" />
                        <p className="text-xs text-[#667085] mt-1" data-testid="grn-job-progress-step-label">{label}</p>
                      </div>
                      {!done && !failed && (
                        <div className="flex items-center gap-2 text-sm text-[#0B6B74]" data-testid="grn-job-progress-running-indicator">
                          <CircleNotch size={16} className="animate-spin" /> {runningLabel}
                        </div>
                      )}
                      {failed && (
                        <div className="flex items-start gap-2 bg-[#FEE4E2] border border-[#FDA29B] rounded-sm px-3 py-2.5 text-sm text-[#B42318]" data-testid="grn-job-progress-error">
                          <WarningCircle size={16} className="shrink-0 mt-0.5" /> {jobProgress.error || summarizeGrResult(doneDoc?.sap_gr_result)}
                        </div>
                      )}
                      {done && (() => {
                        const warehouseLabel = doneDoc?.site_id && doneDoc?.warehouse_id ? `${doneDoc.site_id}/${doneDoc.warehouse_id}` : (doneDoc?.site_id ? `${doneDoc.site_id}-HOLD` : "the warehouse");
                        const movementLines = (doneDoc?.sap_movement_result?.per_item || []);
                        return (
                          <div className="space-y-3">
                            <div className="flex items-center gap-2 bg-[#ECFDF3] border border-[#ABEFC6] rounded-sm px-3 py-2.5 text-sm text-[#027A48] font-medium" data-testid="grn-job-progress-done-banner">
                              <CheckCircle size={16} weight="fill" />
                              {movementLines.length > 0
                                ? <>Moved to {warehouseLabel}{gmIds.length > 0 ? ` · GM ${gmIds.join(", ")}` : ""}</>
                                : "Goods Receipt posted to SAP"}
                            </div>
                            {inboundIds.length > 0 && (
                              <p className="text-xs text-[#667085]" data-testid="grn-progress-inbound-ids">Inbound Delivery ID{inboundIds.length > 1 ? "s" : ""}: {inboundIds.join(", ")}</p>
                            )}
                            {movementLines.length > 0 && (
                              <table className="w-full text-xs" data-testid="grn-progress-movement-lines">
                                <thead>
                                  <tr className="text-[#667085] text-xs">
                                    <th className="text-left py-1">Product</th>
                                    <th className="text-right py-1">Result</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {movementLines.map((l, i) => (
                                    <tr key={`${l.product_id}-${i}`} className="border-t border-[#EAECF0]">
                                      <td className="py-1.5 font-mono text-xs text-[#344054]">{l.product_id}</td>
                                      <td className="py-1.5 text-right font-mono text-xs">
                                        {l.ok ? <span className="text-[#027A48]" title={l.external_id}>GM {shortMovementId(l.external_id)}</span> : <span className="text-[#B42318]">{l.error}</span>}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            )}
                          </div>
                        );
                      })()}
                      <div className="flex justify-end pt-1">
                        <Button size="sm" variant="outline" onClick={closeGrnProgress} data-testid="grn-progress-close-button">Close</Button>
                      </div>
                    </>
                  );
                })()}
              </DialogContent>
            </Dialog>

            {isActionable && (
              <div className="mt-5 border-t border-[#D0D5DD] pt-4 space-y-3">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div>
                    <Label className="text-xs text-[#475467]">Supplier Invoice Number <span className="text-[#B42318]">*</span></Label>
                    <Input
                      placeholder="e.g. INV-4521"
                      value={supplierDocNum}
                      onChange={(e) => setSupplierDocNum(e.target.value.slice(0, 20))}
                      maxLength={20}
                      className="rounded-sm border-[#D0D5DD] mt-1"
                      data-testid="grn-supplier-doc-num-input"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-[#475467]">Supplier Invoice Date <span className="text-[#B42318]">*</span></Label>
                    <DateField value={billDate} onChange={setBillDate} testId="grn-bill-date-input" />
                  </div>
                  <div>
                    <Label className="text-xs text-[#475467]">GRN Date (Physical) <span className="text-[#B42318]">*</span></Label>
                    <DateField value={grnDatePhysical} onChange={setGrnDatePhysical} testId="grn-date-physical-input" />
                  </div>
                  <div>
                    <Label className="text-xs text-[#475467]">Site</Label>
                    <Select value={siteId} onValueChange={setSiteId} disabled={siteAccessBlocked || eligibleSites.length === 1}>
                      <SelectTrigger className="rounded-sm border-[#D0D5DD] mt-1" data-testid="grn-site-select">
                        <SelectValue placeholder="Select site" />
                      </SelectTrigger>
                      <SelectContent>
                        {eligibleSites.map((s) => (<SelectItem key={s} value={s} data-testid={`grn-site-option-${s}`}>{s}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {fxCurrencyCode && (
                  <div className="bg-[#FFFAEB] border border-[#FEDF89] rounded-sm p-4 space-y-3" data-testid="grn-exchange-rate-card">
                    <div className="flex items-center gap-2 text-[#B54708] font-heading font-semibold text-sm">
                      <WarningCircle size={18} weight="fill" /> Import Purchase ({fxCurrencyCode}) — SAP Exchange Rate Check
                    </div>
                    {!grnDatePhysical ? (
                      <div className="text-sm text-[#475467]">Select the GRN Date (Physical) above to check the SAP exchange rate.</div>
                    ) : fxRateLoading ? (
                      <div className="flex items-center gap-2 text-sm text-[#475467]" data-testid="grn-exchange-rate-loading"><CircleNotch size={16} className="animate-spin" /> Fetching the current SAP exchange rate...</div>
                    ) : fxRateError ? (
                      <div className="text-sm text-[#B42318]" data-testid="grn-exchange-rate-error">{fxRateError}</div>
                    ) : fxRateInfo ? (
                      <>
                        {fxRateInfo.rate != null ? (
                          <div>
                            <div className="text-xs text-[#475467]">SAP exchange rate in effect for {format(parseISO(grnDatePhysical), "dd-MM-yyyy")}:</div>
                            <div className="text-3xl sm:text-4xl font-heading font-bold text-[#B54708] mt-1 tracking-tight" data-testid="grn-exchange-rate-value">
                              1 {fxCurrencyCode} = {fxRateInfo.base_currency || "INR"} {Number(fxRateInfo.rate).toFixed(4)}
                            </div>
                            {fxRateInfo.valid_from && (
                              <div className="text-[11px] text-[#98A2B3] mt-0.5">SAP record valid from {format(parseISO(fxRateInfo.valid_from), "dd-MM-yyyy")}</div>
                            )}
                          </div>
                        ) : (
                          <div className="text-sm text-[#B42318]" data-testid="grn-exchange-rate-not-found">SAP has no {fxCurrencyCode}→{fxRateInfo.base_currency || "INR"} rate on file on or before this date - enter the invoice's rate below.</div>
                        )}
                        {fxConfirmed ? (
                          <div className="flex items-center gap-2 text-sm text-[#027A48] font-semibold" data-testid="grn-exchange-rate-confirmed-badge">
                            <CheckCircle size={16} weight="fill" /> Exchange rate confirmed
                          </div>
                        ) : (
                          <>
                            {fxRateInfo.rate != null && (
                              <>
                                <p className="text-sm text-[#475467]">Does this match the exchange rate on the supplier's invoice?</p>
                                <div className="flex gap-2">
                                  <Button onClick={confirmFxMatched} disabled={fxConfirming} className="rounded-sm bg-[#027A48] hover:bg-[#05603A] text-white" data-testid="grn-exchange-rate-matched-button">
                                    {fxConfirming && fxDecision !== "not_matched" ? <CircleNotch size={16} className="animate-spin" /> : "Matched"}
                                  </Button>
                                  <Button
                                    variant="outline"
                                    onClick={() => setFxDecision(fxDecision === "not_matched" ? null : "not_matched")}
                                    disabled={fxConfirming}
                                    className="rounded-sm border-[#B42318] text-[#B42318] hover:bg-[#FEF3F2]"
                                    data-testid="grn-exchange-rate-not-matched-button"
                                  >
                                    Doesn't Match
                                  </Button>
                                </div>
                              </>
                            )}
                            {(fxRateInfo.rate == null || fxDecision === "not_matched") && (
                              <div className="flex items-end gap-2 pt-1">
                                <div className="flex-1">
                                  <Label className="text-xs text-[#475467]">Exchange rate from the supplier's invoice</Label>
                                  <Input
                                    type="number"
                                    step="0.0001"
                                    value={fxCorrectedRate}
                                    onChange={(e) => setFxCorrectedRate(e.target.value)}
                                    placeholder={`1 ${fxCurrencyCode} = ? ${fxRateInfo.base_currency || "INR"}`}
                                    className="rounded-sm border-[#D0D5DD] mt-1"
                                    data-testid="grn-exchange-rate-corrected-input"
                                  />
                                </div>
                                <Button onClick={submitFxCorrection} disabled={fxConfirming} className="rounded-sm bg-[#B54708] hover:bg-[#93370D] text-white" data-testid="grn-exchange-rate-save-corrected-button">
                                  {fxConfirming ? <CircleNotch size={16} className="animate-spin" /> : "Save & Update SAP"}
                                </Button>
                              </div>
                            )}
                          </>
                        )}
                      </>
                    ) : null}
                  </div>
                )}

                {dupInvoiceWarning && (
                  <div className="flex items-start gap-2 bg-[#FFFAEB] border border-[#FEDF89] rounded-sm px-3 py-2.5 text-xs text-[#B54708]" data-testid="grn-duplicate-invoice-warning">
                    <WarningCircle size={15} className="shrink-0 mt-0.5" />
                    <span>
                      Invoice No. <strong className="font-data">{(supplierDocNum || shipment?.supplier_doc_num || "").trim()}</strong> is already recorded on shipment{" "}
                      <strong className="font-data">{dupInvoiceWarning.doc_code}</strong>
                      {dupInvoiceWarning.po_numbers?.length > 0 && <> (PO {dupInvoiceWarning.po_numbers.join(", ")})</>}
                      {dupInvoiceWarning.bill_date && <>, dated {dupInvoiceWarning.bill_date}</>} for this same supplier. Double-check this isn't a re-typed or copy-pasted number before continuing.
                    </span>
                  </div>
                )}

                <div>
                  <Label className="text-xs text-[#475467]">Supplier Invoice Copy <span className="text-[#B42318]">*</span></Label>
                  {!invoiceAttachment ? (
                    <label className="flex items-center justify-center gap-2 h-14 border-2 border-dashed border-[#D0D5DD] rounded-sm cursor-pointer hover:border-[#004B87] hover:bg-[#F9FAFB] transition-colors mt-1" data-testid="grn-invoice-attachment-dropzone">
                      <Paperclip size={15} className="text-[#98A2B3]" />
                      <span className="text-xs text-[#667085]">Click to upload or take a photo of the supplier's invoice (PDF/JPG/PNG/DOC/XLS, max 400 KB)...</span>
                      <input
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx,application/pdf,image/*,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                        className="hidden"
                        onChange={(e) => onInvoiceFileSelect(e.target.files?.[0])}
                        data-testid="grn-invoice-attachment-input"
                      />
                    </label>
                  ) : (
                    <div className="flex items-center justify-between gap-3 h-11 px-3 border border-[#D0D5DD] rounded-sm bg-[#F9FAFB] mt-1" data-testid="grn-invoice-attachment-chip">
                      <span className="flex items-center gap-2 text-xs text-[#344054] min-w-0 truncate">
                        {invoiceAttachment.uploading ? <CircleNotch size={14} className="animate-spin shrink-0" /> : invoiceAttachment.error ? <WarningCircle size={14} className="text-[#B42318] shrink-0" /> : <CheckCircle size={14} className="text-[#027A48] shrink-0" weight="fill" />}
                        <span className="truncate">{invoiceAttachment.filename}</span>
                        {invoiceAttachment.size != null && <span className="text-[#98A2B3] shrink-0">({Math.round(invoiceAttachment.size / 1024)} KB)</span>}
                      </span>
                      <div className="flex items-center gap-2 shrink-0">
                        {invoiceAttachment.error && <span className="text-[11px] text-[#B42318]" data-testid="grn-invoice-attachment-error">{invoiceAttachment.error}</span>}
                        {invoiceAttachment.path && (
                          <Button type="button" variant="ghost" size="sm" className="h-6 px-2 text-xs rounded-sm" onClick={() => viewInvoiceAttachment(shipment._id)} data-testid="grn-invoice-attachment-view-button">View</Button>
                        )}
                        <Button type="button" variant="ghost" size="sm" className="h-6 px-2 text-xs rounded-sm" onClick={() => { setInvoiceAttachment(null); setAiMatch(null); setAiMatchErrorMsg(null); }} data-testid="grn-invoice-attachment-remove-button">Replace</Button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Feb 2026, user's explicit ask - AI Invoice Match: reads the
                    uploaded invoice (vision model) and cross-checks invoice
                    number/date + every item's qty/rate against what's been
                    keyed into this GRN, shown side-by-side with the invoice
                    image itself (same "see GRN details and invoice copy in
                    parallel" layout the user referenced). Pure app-level
                    review - never touches SAP. Mandatory gate: "Validate GRN"
                    below stays disabled until this is clean or explicitly
                    accepted (re-enforced server-side in prepare_approval). */}
                {invoiceAttachment?.path && (
                  <div className="grid lg:grid-cols-2 gap-4 items-start" data-testid="grn-ai-match-section">
                    <div className="border border-[#D0D5DD] rounded-sm p-3 bg-white space-y-3">
                      <div className="flex items-center justify-between gap-2">
                        <Label className="text-xs text-[#475467] font-semibold">AI Invoice Match</Label>
                        <Button
                          type="button"
                          onClick={runAiMatch}
                          disabled={aiMatchRunning || !aiMatchUsableAttachment || !supplierDocNum.trim() || !billDate}
                          title={!aiMatchUsableAttachment ? "Re-upload the invoice as PDF, JPG or PNG to use AI Match" : (!supplierDocNum.trim() || !billDate) ? "Enter the Supplier Invoice Number and Date first" : undefined}
                          variant="outline"
                          className="h-7 rounded-sm px-3 text-xs font-bold"
                          data-testid="grn-ai-match-run-button"
                        >
                          {aiMatchRunning ? <CircleNotch size={13} className="mr-1 animate-spin" /> : <Sparkle size={13} className="mr-1" />}
                          {aiMatchRunning ? `Reading invoice... ${aiMatchElapsedSec}s` : aiMatch ? "Re-run AI Match" : "Run AI Match"}
                        </Button>
                      </div>
                      {aiMatchRunning && (
                        <div className="space-y-1" data-testid="grn-ai-match-progress">
                          <Progress value={aiMatchSoftProgressPct} className="h-1.5" />
                          <p className="text-[10px] text-[#98A2B3]">Usually takes 15-30s (can run to ~45s on a busy invoice) - reading and cross-checking every line against this GRN.</p>
                        </div>
                      )}
                      {!aiMatchUsableAttachment && (
                        <p className="text-xs text-[#B54708]" data-testid="grn-ai-match-unsupported-file">AI Match needs a PDF, JPG or PNG invoice copy - this file ({invoiceAttachment.filename}) isn't readable by the vision model. Click Replace above to upload a photo/PDF instead.</p>
                      )}
                      {aiMatchErrorMsg && (
                        <div className="flex items-start gap-2 bg-[#FEE4E2] border border-[#FDA29B] rounded-sm px-3 py-2 text-xs text-[#B42318]" data-testid="grn-ai-match-error">
                          <WarningCircle size={14} className="shrink-0 mt-0.5" /> {aiMatchErrorMsg}
                        </div>
                      )}
                      {aiMatch && !aiMatchRunning && (
                        <div className="space-y-2">
                          {aiMatchStale && (
                            <div className="flex items-center gap-2 bg-[#FEF0C7] border border-[#FEC84B] rounded-sm px-3 py-2 text-xs text-[#93370D] font-medium" data-testid="grn-ai-match-stale-banner">
                              <WarningCircle size={14} className="shrink-0" /> Invoice number, date or an item quantity changed since this was checked - re-run AI Match before validating.
                            </div>
                          )}
                          <div className="flex flex-wrap items-center gap-3 text-xs text-[#475467]">
                            <span>Invoice # read: <span className="font-data font-semibold text-[#1D2939]">{aiMatch.extracted_invoice_number || "unreadable"}</span> {aiMatch.invoice_number_match ? <CheckCircle size={12} weight="fill" className="inline text-[#027A48] ml-0.5" /> : <WarningCircle size={12} className="inline text-[#B54708] ml-0.5" />}</span>
                            <span>Date read: <span className="font-data font-semibold text-[#1D2939]">{aiMatch.extracted_invoice_date || "unreadable"}</span> {aiMatch.invoice_date_match ? <CheckCircle size={12} weight="fill" className="inline text-[#027A48] ml-0.5" /> : <WarningCircle size={12} className="inline text-[#B54708] ml-0.5" />}</span>
                            <span data-testid="grn-ai-match-supplier-name-read">Supplier read: <span className="font-data font-semibold text-[#1D2939]">{aiMatch.extracted_supplier_name || "unreadable"}</span> {aiMatch.supplier_name_match ? <CheckCircle size={12} weight="fill" className="inline text-[#027A48] ml-0.5" /> : <WarningCircle size={12} className="inline text-[#B42318] ml-0.5" />}</span>
                          </div>
                          {aiMatchNonPriceIssues.length === 0 ? (
                            <div className={`flex items-center gap-2 bg-[#ECFDF3] border border-[#ABEFC6] rounded-sm px-3 py-2 text-xs text-[#027A48] font-medium ${aiMatchStale ? "opacity-40" : ""}`} data-testid="grn-ai-match-clean-banner">
                              <CheckCircle size={14} weight="fill" /> {priceVarianceIssues.length > 0 ? "Invoice matches the GRN entries - no issues found other than the rate, below." : "Invoice matches the GRN entries - no issues found."}
                            </div>
                          ) : (
                            <div className={`space-y-2 ${aiMatchStale ? "opacity-40" : ""}`} data-testid="grn-ai-match-issues-list">
                              {aiMatchNonPriceIssues.map((issue, i) => {
                                const isSupplierNameIssue = issue.type === "supplier_name_mismatch" || issue.type === "supplier_name_unreadable";
                                return (
                                  <div
                                    key={i}
                                    className={isSupplierNameIssue
                                      ? "flex items-start gap-2 bg-[#FEE4E2] border border-[#FDA29B] rounded-sm px-3 py-2 text-xs text-[#B42318] font-medium"
                                      : "flex items-start gap-2 bg-[#FFFAEB] border border-[#FEDF89] rounded-sm px-3 py-2 text-xs text-[#93370D]"}
                                    data-testid={`grn-ai-match-issue-${i}`}
                                  >
                                    <WarningCircle size={14} className="shrink-0 mt-0.5" /> {issue.message}
                                  </div>
                                );
                              })}
                              {!aiMatchStale && (aiMatch.user_decision === "accepted" ? (
                                <div className="flex items-center gap-2 text-xs text-[#027A48] font-medium" data-testid="grn-ai-match-accepted-banner">
                                  <CheckCircle size={13} weight="fill" /> Accepted by {aiMatch.decided_by} - Validate GRN is unlocked.
                                </div>
                              ) : (
                                <Button type="button" onClick={acceptAiMatchIssues} variant="outline" className="h-7 rounded-sm px-3 text-xs font-bold border-[#B54708] text-[#B54708] hover:bg-[#FFFAEB]" data-testid="grn-ai-match-accept-button">
                                  Accept as is &amp; continue
                                </Button>
                              ))}
                            </div>
                          )}
                          {priceVarianceIssues.length > 0 && (() => {
                            const allKnownSupplier = priceVarianceIssues.every((iss) => iss.known_variable_price_supplier);
                            return (
                              <div className="flex items-start gap-2 bg-[#FFFAEB] border border-[#FEDF89] rounded-sm px-3 py-2 text-xs text-[#93370D] font-medium" data-testid="grn-ai-match-price-variance-note">
                                <WarningCircle size={14} className="shrink-0 mt-0.5" />
                                {allKnownSupplier
                                  ? `${shipment.company_name} invoices at its current market price, so the PO rate won't match by design on ${priceVarianceIssues.length} item${priceVarianceIssues.length > 1 ? "s" : ""} - a reason is still required: click "Record Variance & Continue" below.`
                                  : `Invoice rate differs from PO rate on ${priceVarianceIssues.length} item${priceVarianceIssues.length > 1 ? "s" : ""} - click "Record Variance & Continue" below to proceed.`}
                              </div>
                            );
                          })()}
                        </div>
                      )}
                      {!aiMatch && !aiMatchRunning && !aiMatchErrorMsg && aiMatchUsableAttachment && (
                        <p className="text-xs text-[#667085]" data-testid="grn-ai-match-placeholder">Run AI Match to verify the invoice number, date and item quantities/rates against what you've entered above, before Validate GRN unlocks.</p>
                      )}
                    </div>
                    <InvoicePreviewPanel docCode={shipment._id} attachment={invoiceAttachment} />
                  </div>
                )}

                {/* Feb 2026, user's explicit ask - this combined multi-PO
                    SAP posting path (one combined Inbound Delivery per
                    shipment instead of one per PO) is now the standard
                    GRN posting flow - the old per-PO "Validate GRN"/
                    "Post GRN in SAP" buttons were removed from the UI
                    after side-by-side preview testing. */}
                <div className="border border-[#ABEFC6] rounded-sm p-3 bg-[#ECFDF3] space-y-2" data-testid="grn-merge-path-box">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 flex-wrap">
                    <div className="flex items-center gap-2">
                      <span className="flex items-center justify-center w-5 h-5 rounded-full bg-[#067647] text-white text-xs font-bold shrink-0">1</span>
                      <Button
                        onClick={runGrnMergeValidation}
                        disabled={validatingGrnMerge || busy || siteAccessBlocked || !varianceAcknowledged || !!user?.grn_blocked_shipment || !siteId || !aiMatchCleared}
                        variant={grnMergeValidated ? "outline" : "default"}
                        title={!aiMatchCleared ? (aiMatchStale ? "Invoice details changed since AI Match was run - re-run it first" : "Run AI Invoice Match first (and accept/resolve any issues it finds)") : !siteId ? "Select a Site first" : !varianceAcknowledged ? "Qty or Price variance detected - click 'Record Variance & Continue' below first" : undefined}
                        className="h-8 rounded-sm px-4 text-[13px] font-bold transition-colors bg-[#067647] hover:bg-[#05603A] text-white"
                        data-testid="grn-validate-merge-button"
                      >
                        <ArrowsClockwise size={14} className="mr-1" /> {validatingGrnMerge ? "Validating..." : grnMergeValidated ? "Validated \u2713" : "Validate GRN"}
                      </Button>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`flex items-center justify-center w-5 h-5 rounded-full text-white text-xs font-bold shrink-0 ${grnMergeValidated ? "bg-[#067647]" : "bg-[#D0D5DD]"}`}>2</span>
                      <Button
                        onClick={() => requestPostGrn("full_auto_merge")}
                        disabled={busy || !grnMergeValidated || !supplierDocNum.trim() || !billDate || !grnDatePhysical || !invoiceAttachment?.path}
                        title={!grnMergeValidated ? "Run Validate GRN first" : ((!supplierDocNum.trim() || !billDate) ? "Enter the Supplier Invoice Number and Supplier Invoice Date first" : !grnDatePhysical ? "Enter the GRN Date (Physical) first" : !invoiceAttachment?.path ? "Upload the Supplier Invoice Copy first" : undefined)}
                        className="h-8 rounded-sm bg-[#079455] hover:bg-[#067647] text-white px-4 text-[13px] font-bold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        data-testid="grn-approve-full-auto-merge-button"
                      >
                        <ArrowsClockwise size={14} className="mr-1" /> {busy ? "Posting..." : "Post GRN to SAP"}
                      </Button>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 flex-wrap">
                  <Button onClick={() => setRejectOpen(true)} disabled={busy} className="h-8 rounded-sm bg-[#B42318] hover:bg-[#912018] text-white px-4 text-[13px] font-bold transition-colors" data-testid="grn-reject-button">
                    <XCircle size={14} className="mr-1" /> Reject
                  </Button>
                  {hasLiveVariance && !varianceAcknowledged && (
                    <Button onClick={openVarianceRemark} disabled={busy} className="h-8 rounded-sm bg-[#B54708] hover:bg-[#93370D] text-white px-4 text-[13px] font-bold transition-colors" data-testid="grn-record-variance-button">
                      <WarningCircle size={14} className="mr-1" /> Record Variance &amp; Continue
                    </Button>
                  )}
                </div>
                {hasLiveVariance && !varianceAcknowledged && (
                  <div className="text-xs text-[#B54708] bg-[#FFFAEB] border border-[#FEDF89] rounded-sm px-3 py-2 flex items-center gap-1.5" data-testid="grn-qty-mismatch-warning">
                    <WarningCircle size={14} />
                    {qtyVarianceItems.length > 0 && priceVarianceIssues.length > 0
                      ? "Quantity and Price variance detected against the Ship Qty/PO rate."
                      : qtyVarianceItems.length > 0
                        ? "Quantity mismatch detected: Actual Qty differs from Ship Qty on one or more items."
                        : "Price variance detected: Invoice rate differs from PO rate on one or more items."}
                    {" "}Click "Record Variance &amp; Continue" to log it and unlock posting.
                  </div>
                )}
                {hasLiveVariance && varianceAcknowledged && (
                  <div className="text-xs text-[#027A48] bg-[#ECFDF3] border border-[#ABEFC6] rounded-sm px-3 py-2 flex items-center gap-1.5" data-testid="grn-variance-acknowledged-banner">
                    <CheckCircle size={14} weight="fill" /> Variance recorded by {shipment.variance_ack?.by} - Validate GRN/Post GRN to SAP are unlocked.
                  </div>
                )}

                <Dialog open={showGrnMergeValidationErrorBox} onOpenChange={setShowGrnMergeValidationErrorBox}>
                  <DialogContent className="max-w-lg rounded-sm" data-testid="grn-merge-validation-error-box">
                    <DialogHeader>
                      <DialogTitle className="flex items-center gap-2 text-[#912018]"><WarningCircle size={18} /> Validation found {grnMergeValidationIssues.length} issue{grnMergeValidationIssues.length === 1 ? "" : "s"}</DialogTitle>
                      <DialogDescription>Fix these before posting the GRN in SAP - SAP would reject it otherwise.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-2 max-h-80 overflow-y-auto">
                      {grnMergeValidationIssues.map((issue, idx) => (
                        <div key={idx} className="bg-[#FEF3F2] border border-[#FDA29B] rounded-sm p-2.5 text-sm text-[#912018]" data-testid={`grn-merge-validation-issue-${idx}`}>
                          {issue.message}
                        </div>
                      ))}
                    </div>
                    <Button onClick={() => setShowGrnMergeValidationErrorBox(false)} className="w-full rounded-sm" data-testid="grn-merge-validation-error-ok-button">OK</Button>
                  </DialogContent>
                </Dialog>

                <Dialog open={showGrnValidationErrorBox} onOpenChange={setShowGrnValidationErrorBox}>
                  <DialogContent className="max-w-lg rounded-sm" data-testid="grn-validation-error-box">
                    <DialogHeader>
                      <DialogTitle className="flex items-center gap-2 text-[#912018]"><WarningCircle size={18} /> Validation found {grnValidationIssues.length} issue{grnValidationIssues.length === 1 ? "" : "s"}</DialogTitle>
                      <DialogDescription>Fix these before posting the GRN in SAP - SAP would reject it otherwise.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-2 max-h-80 overflow-y-auto">
                      {grnValidationIssues.map((issue, idx) => (
                        <div key={idx} className="bg-[#FEF3F2] border border-[#FDA29B] rounded-sm p-2.5 text-sm text-[#912018]" data-testid={`grn-validation-issue-${idx}`}>
                          {issue.message}
                        </div>
                      ))}
                    </div>
                    <Button onClick={() => setShowGrnValidationErrorBox(false)} className="w-full rounded-sm" data-testid="grn-validation-error-ok-button">OK</Button>
                  </DialogContent>
                </Dialog>
              </div>
            )}

            <Dialog open={!!confirmMode} onOpenChange={(o) => !o && setConfirmMode(null)}>
              <DialogContent className="rounded-sm" data-testid="grn-approval-confirm-dialog">
                <DialogHeader>
                  <DialogTitle className="font-heading">{confirmMode === "full_auto_merge" ? "Confirm Post GRN to SAP" : "Confirm Post GRN in SAP"}</DialogTitle>
                  <DialogDescription>
                    {confirmMode === "full_auto_merge"
                      ? "This posts a REAL, irreversible Goods Receipt directly to SAP as ONE combined Inbound Delivery covering every PO/item on this shipment, then moves the received stock. Please confirm the details below."
                      : "This posts a REAL, irreversible Goods Receipt directly to SAP - no manual SAP step afterward. Warehouse movement is not performed; only the Goods Receipt is posted. Please confirm the details below."}
                  </DialogDescription>
                </DialogHeader>
                <div className="bg-[#F9FAFB] border border-[#E5E7EB] rounded-sm p-3 text-sm space-y-1">
                  <div className="flex justify-between"><span className="text-[#475467]">Supplier Invoice Number</span><span className="font-data font-semibold" data-testid="grn-confirm-invoice-number">{supplierDocNum}</span></div>
                  <div className="flex justify-between"><span className="text-[#475467]">Supplier Invoice Date</span><span className="font-data font-semibold" data-testid="grn-confirm-bill-date">{billDate ? format(parseISO(billDate), "dd-MM-yyyy") : "\u2014"}</span></div>
                  <div className="flex justify-between"><span className="text-[#475467]">GRN Date (Physical) - sent to SAP as Actual Delivery Date</span><span className="font-data font-semibold" data-testid="grn-confirm-grn-date-physical">{grnDatePhysical ? format(parseISO(grnDatePhysical), "dd-MM-yyyy") : "\u2014"}</span></div>
                  <div className="flex justify-between"><span className="text-[#475467]">Site</span><span className="font-data font-semibold">{siteId}</span></div>
                </div>
                <div className="flex justify-end gap-2 mt-2">
                  <Button variant="outline" className="rounded-sm" onClick={() => setConfirmMode(null)} data-testid="grn-approval-confirm-cancel-button">Cancel</Button>
                  <Button
                    onClick={confirmApproval}
                    className={`rounded-sm text-white transition-colors ${confirmMode === "full_auto_merge" ? "bg-[#079455] hover:bg-[#067647]" : "bg-[#6941C6] hover:bg-[#53389E]"}`}
                    data-testid="grn-approval-confirm-button"
                  >
                    Yes, Post to SAP Now
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            <Dialog open={putAwayAlertOpen && effectivePutAwayFailed} onOpenChange={setPutAwayAlertOpen}>
              <DialogContent className="rounded-sm" data-testid="grn-put-away-failed-alert-dialog">
                <DialogHeader>
                  <DialogTitle className="font-heading flex items-center gap-2 text-[#B42318]">
                    <WarningCircle size={20} weight="fill" /> Receipt Not Completed in SAP
                  </DialogTitle>
                  <DialogDescription data-testid="grn-put-away-failed-alert-message">
                    {shipment.grn_alert_message || (releaseStuckPoNumber ? `PO ${releaseStuckPoNumber}'s delivery never finished Release in SAP - needs SAP Admin attention.` : "SAP did not finish receiving this Goods Receipt after repeated attempts - needs SAP Admin attention.")}
                  </DialogDescription>
                </DialogHeader>
                <div className="flex justify-end gap-2 mt-2">
                  <Button variant="outline" className="rounded-sm" onClick={() => setPutAwayAlertOpen(false)} data-testid="grn-put-away-failed-alert-close-button">Close</Button>
                </div>
              </DialogContent>
            </Dialog>


            {shipment.status === "approved" && !busy && (
              <div className="mt-4 space-y-2">
                <div className="text-sm px-3 py-2 rounded-sm flex items-center justify-between gap-2 border" data-testid="grn-sap-sync-status"
                     style={(shipment.sap_sync_status === "posted" && !effectivePutAwayFailed) || shipment.sap_sync_status === "partial" ? { color: "#0B7A56", background: "rgba(16,185,129,0.1)", borderColor: "rgba(16,185,129,0.3)" } : shipment.sap_sync_status === "failed" || shipment.sap_sync_status === "manual_mismatch" || effectivePutAwayFailed ? { color: "#B42318", background: "rgba(180,35,24,0.08)", borderColor: "rgba(180,35,24,0.3)" } : shipment.sap_sync_status === "awaiting_manual_gr" ? { color: "#3538CD", background: "rgba(53,56,205,0.08)", borderColor: "rgba(53,56,205,0.3)" } : { color: "#B45309", background: "rgba(227,160,8,0.1)", borderColor: "rgba(227,160,8,0.3)" }}>
                  <span className="flex items-center gap-2">
                    {(shipment.sap_sync_status === "posted" && !effectivePutAwayFailed) || shipment.sap_sync_status === "partial" ? <CheckCircle size={16} /> : shipment.sap_sync_status === "manual_mismatch" || effectivePutAwayFailed ? <WarningCircle size={16} weight="fill" /> : <PlugsConnected size={16} />}
                    {shipment.sap_sync_status === "posted" && !effectivePutAwayFailed
                      ? (
                        <span data-testid="grn-sap-posted-with-movement">
                          Goods Receipt posted to SAP
                          {movementStatusSuffix(shipment)}
                        </span>
                      )
                      : shipment.sap_sync_status === "partial"
                      ? (
                        <span data-testid="grn-sap-partial-badge">
                          <strong>Partially Posted</strong> - {`${(shipment.sap_gr_result?.per_po || []).filter((p) => p.status === "posted").length} of ${(shipment.sap_gr_result?.per_po || []).length} PO(s) posted - the rest were permanently skipped (see per-PO diagnostics below), nothing left to retry`}
                        </span>
                      )
                      : shipment.sap_sync_status === "awaiting_manual_gr"
                      ? (
                        <span data-testid="grn-sap-awaiting-manual-badge">
                          <strong>Awaiting Manual GR In SAP</strong> - Complete the GR in SAP, then click Re-check SAP
                        </span>
                      )
                      : shipment.sap_sync_status === "manual_mismatch"
                      ? (
                        <span data-testid="grn-sap-manual-mismatch-badge">
                          <strong>Qty Mismatch - Approver Blocked</strong> - {`SAP's confirmed quantity does not match ${(shipment.manual_gr_mismatch_items || []).length} line(s) - correct it in SAP and Re-check`}
                        </span>
                      )
                      : effectivePutAwayFailed
                      ? (
                        <span data-testid="grn-sap-put-away-failed-badge">
                          <strong>Receipt Not Completed In SAP</strong> - {shipment.grn_alert_message || (releaseStuckPoNumber ? `PO ${releaseStuckPoNumber}'s delivery never finished Release in SAP - needs SAP Admin attention` : "SAP did not finish receiving this Goods Receipt - needs SAP Admin attention")}
                        </span>
                      )
                      : shipment.sap_sync_status === "skipped"
                      ? (shipment.sap_gr_result?.per_po?.[0]?.error || "PO not found in SAP - check it's released")
                      : shipment.sap_sync_status === "failed"
                      ? (
                        <span data-testid="grn-sap-failed-badge">
                          <strong>SAP Sync Failed</strong> - {shipment.sap_gr_result?.per_po?.find((p) => p.error)?.error || "Repeated attempts failed - needs SAP Admin attention"}
                        </span>
                      )
                      : (
                        <span data-testid="grn-sap-in-process-badge">
                          <strong>In Process</strong> - {shipment.sap_gr_result?.per_po?.find((p) => p.error)?.error || "Posting to SAP - this can take a few minutes"}
                        </span>
                      )}
                  </span>
                  {shipment.sap_sync_status === "partial" ? null : shipment.sap_sync_status === "awaiting_manual_gr" || shipment.sap_sync_status === "manual_mismatch" ? (
                    <Button size="sm" variant="outline" onClick={recheckManualGr} disabled={busy} className="rounded-sm h-7 text-xs" data-testid="grn-recheck-manual-gr-button">
                      <ArrowsClockwise size={12} className="mr-1" /> Re-check SAP
                    </Button>
                  ) : effectivePutAwayFailed ? (
                    releaseStuckPoNumber ? (
                      <Button size="sm" variant="outline" onClick={() => resendDelivery(releaseStuckPoNumber)} disabled={busy} className="rounded-sm h-7 text-xs" data-testid="grn-resend-delivery-button" title="Only use after your SAP Admin has manually cancelled the stuck Inbound Delivery in SAP">
                        <ArrowsClockwise size={12} className="mr-1" /> Resend Delivery
                      </Button>
                    ) : (
                      <Button size="sm" variant="outline" onClick={retryPutAway} disabled={busy} className="rounded-sm h-7 text-xs" data-testid="grn-retry-put-away-button" title="Only use once your SAP Admin confirms the underlying SAP error is fixed">
                        <ArrowsClockwise size={12} className="mr-1" /> Retry Put Away
                      </Button>
                    )
                  ) : shipment.sap_sync_status === "skipped" ? (
                    <Button size="sm" variant="outline" onClick={retryGoodsReceipt} disabled={busy} className="rounded-sm h-7 text-xs" data-testid="grn-retry-goods-receipt-button">
                      <ArrowsClockwise size={12} className="mr-1" /> Retry
                    </Button>
                  ) : shipment.sap_sync_status === "failed" ? (
                    shipment.grn_mode === "manual" ? (
                      <Button size="sm" variant="outline" onClick={retryGoodsReceipt} disabled={busy} className="rounded-sm h-7 text-xs" data-testid="grn-retry-goods-receipt-button">
                        <ArrowsClockwise size={12} className="mr-1" /> Retry
                      </Button>
                    ) : (
                      <Button size="sm" variant="outline" onClick={resetRetry} disabled={busy} className="rounded-sm h-7 text-xs" data-testid="grn-reset-retry-button" title="Only use once your SAP Admin confirms the underlying SAP error is fixed">
                        <ArrowsClockwise size={12} className="mr-1" /> Reset Retry
                      </Button>
                    )
                  ) : shipment.sap_sync_status !== "posted" && (
                    retryUnlockInMin(shipment.approved_at) > 0 ? (
                      <span className="text-xs text-[#98A2B3] shrink-0" data-testid="grn-retry-cooldown">Retry available in {retryUnlockInMin(shipment.approved_at)}m</span>
                    ) : (
                      <Button size="sm" variant="outline" onClick={retryGoodsReceipt} disabled={busy} className="rounded-sm h-7 text-xs" data-testid="grn-retry-goods-receipt-button">
                        <ArrowsClockwise size={12} className="mr-1" /> Retry
                      </Button>
                    )
                  )}
                  {shipment.sap_sync_status === "posted" && (
                    verifySapCountdown != null ? (
                      <span className="text-xs text-[#98A2B3] shrink-0" data-testid="grn-verify-sap-countdown">
                        Checking SAP in {String(Math.floor(verifySapCountdown / 60)).padStart(2, "0")}:{String(verifySapCountdown % 60).padStart(2, "0")}
                      </span>
                    ) : (
                      <Button size="sm" variant="outline" onClick={startVerifySapCountdown} disabled={busy} className="rounded-sm h-7 text-xs" data-testid="grn-verify-sap-status-button" title="Waits 2 minutes for SAP to finish settling, then double-checks that every PO here was genuinely posted">
                        <ArrowsClockwise size={12} className="mr-1" /> Verify with SAP
                      </Button>
                    )
                  )}
                  {/* Oct 2026, real incident S001327 - SAP may already show more
                      than this shipment's own sap_gr_result reflects (e.g. the
                      line-mapping bug this resync corrects for). Only offered
                      for the full_auto/combined-GRN path's own stuck states -
                      Manual GRN already has its own "Re-check SAP" button above. */}
                  {shipment.status === "approved" && ["pending", "failed", "partial"].includes(shipment.sap_sync_status) && shipment.grn_mode !== "manual" && !effectivePutAwayFailed && (
                    <Button size="sm" variant="outline" onClick={() => resyncFromSap(false)} disabled={busy} className="rounded-sm h-7 text-xs" data-testid="grn-resync-from-sap-button" title="Re-check SAP's own confirmed delivery data - may reconcile a PO this shipment still shows as unposted">
                      <ArrowsClockwise size={12} className="mr-1" /> Resync from SAP
                    </Button>
                  )}
                  {resyncMismatch?.doc_code === shipment._id && (
                    <Button size="sm" variant="outline" onClick={() => resyncFromSap(true)} disabled={busy} className="rounded-sm h-7 text-xs border-[#B45309] text-[#B45309]" data-testid="grn-accept-sap-partial-qty-button" title="Accept SAP's lower confirmed quantity as the final received amount for the flagged PO(s)">
                      Accept SAP's Quantity
                    </Button>
                  )}
                  {/* Oct 27 2026, real incident (shipment S001640, PO 30909,
                      Inbound Delivery 57494) - a successfully-posted GRN
                      later reversed directly in SAP (see
                      hasReversedInboundDelivery above) was permanently
                      stuck: sap_sync_status stayed "posted" forever, which
                      hid this button entirely, and the admin had no way to
                      reconcile the local record with SAP's true state. */}
                  {isGrnAdmin && shipment.status === "approved" && (!["posted", "partial"].includes(shipment.sap_sync_status) || hasReversedInboundDelivery(shipment)) && (
                    <Button size="sm" variant="outline" onClick={() => { setCancelGrnTargetCode(shipment._id); setCancelGrnOpen(true); }} disabled={busy} className="rounded-sm h-7 text-xs border-[#B42318] text-[#B42318]" data-testid="grn-cancel-button" title="Only succeeds if SAP confirms this shipment's Inbound Delivery is Cancelled - verified live against SAP before cancelling">
                      <XCircle size={12} className="mr-1" /> Cancel GRN
                    </Button>
                  )}
                </div>
                {resyncMismatch?.doc_code === shipment._id && (
                  <table className="border-collapse w-full text-[11px] border border-[#FEDF89] rounded-sm overflow-hidden" data-testid="grn-resync-mismatch-table">
                    <thead className="bg-[#FFFAEB] text-[#B45309] font-bold uppercase tracking-wide">
                      <tr>
                        <th className="border border-[#FEDF89] p-1.5 text-left">PO Number</th>
                        <th className="border border-[#FEDF89] p-1.5 text-left">Product</th>
                        <th className="border border-[#FEDF89] p-1.5 text-right">Expected Qty</th>
                        <th className="border border-[#FEDF89] p-1.5 text-right">SAP Confirmed Qty</th>
                      </tr>
                    </thead>
                    <tbody>
                      {resyncMismatch.items.flatMap((r) => Object.entries(r.mismatched_products || {}).map(([productId, v]) => (
                        <tr key={`${r.po_number}-${productId}`} className="bg-white odd:bg-[#FFFAEB]/40" data-testid={`grn-resync-mismatch-row-${r.po_number}-${productId}`}>
                          <td className="border border-[#FEDF89] px-2 py-1 font-data">{r.po_number}</td>
                          <td className="border border-[#FEDF89] px-2 py-1">{productId}</td>
                          <td className="border border-[#FEDF89] px-2 py-1 text-right font-data">{v.expected}</td>
                          <td className="border border-[#FEDF89] px-2 py-1 text-right font-data">{v.sap_confirmed}</td>
                        </tr>
                      )))}
                    </tbody>
                  </table>
                )}
                {shipment.sap_sync_status === "manual_mismatch" && (shipment.manual_gr_mismatch_items || []).length > 0 && (
                  <table className="border-collapse w-full text-[11px] border border-[#FECDCA] rounded-sm overflow-hidden" data-testid="grn-manual-mismatch-table">
                    <thead className="bg-[#FEF3F2] text-[#B42318] font-bold uppercase tracking-wide">
                      <tr>
                        <th className="border border-[#FECDCA] p-1.5 text-left">PO Number</th>
                        <th className="border border-[#FECDCA] p-1.5 text-left">Item</th>
                        <th className="border border-[#FECDCA] p-1.5 text-left">Product</th>
                        <th className="border border-[#FECDCA] p-1.5 text-right">Shipped/Confirmed Qty</th>
                        <th className="border border-[#FECDCA] p-1.5 text-right">SAP Confirmed Qty</th>
                      </tr>
                    </thead>
                    <tbody>
                      {shipment.manual_gr_mismatch_items.map((m, i) => (
                        <tr key={i} className="bg-white odd:bg-[#FEF3F2]/40" data-testid={`grn-manual-mismatch-row-${i}`}>
                          <td className="border border-[#FECDCA] px-2 py-1 font-data">{m.po_number}</td>
                          <td className="border border-[#FECDCA] px-2 py-1 font-data">{m.item_number}</td>
                          <td className="border border-[#FECDCA] px-2 py-1">{m.product_id}</td>
                          <td className="border border-[#FECDCA] px-2 py-1 text-right font-data">{m.shipped_qty ?? "\u2014"}</td>
                          <td className="border border-[#FECDCA] px-2 py-1 text-right font-data">{m.sap_confirmed_qty ?? m.reason ?? "\u2014"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                {(shipment.sap_sync_status !== "posted" && shipment.sap_sync_status !== "partial") && shipment.sap_gr_result?.per_po?.some((p) => p.screenshot_path) && (
                  <div className="px-3">
                    <button
                      type="button"
                      className="text-xs text-[#7A1E1E] font-bold hover:underline"
                      data-testid="grn-view-failure-screenshot-button"
                      onClick={() => {
                        const failed = shipment.sap_gr_result.per_po.find((p) => p.screenshot_path);
                        setScreenshotModal(failed);
                      }}
                    >
                      View exact SAP screen at time of failure ({describeFailedStep(shipment.sap_gr_result.per_po.find((p) => p.screenshot_path)?.failed_step)})
                    </button>
                  </div>
                )}
                {shipment.sap_gr_result?.per_po?.some((p) => (p.status !== "posted" && p.status !== "notification_created" && p.events?.length) || (p.put_away_confirmed === false && p.events?.length) || p.skipped_items?.length) && (
                  <div className="px-3">
                    <button
                      type="button"
                      className="text-xs text-[#475467] font-bold hover:underline"
                      data-testid="grn-view-diagnostics-button"
                      onClick={() => setDiagnosticsModal(poDiagnostics(shipment, shipment.sap_gr_result.per_po.find((p) => (p.status !== "posted" && p.status !== "notification_created" && p.events?.length) || (p.put_away_confirmed === false && p.events?.length) || p.skipped_items?.length)))}
                    >
                      View Diagnostics
                    </button>
                  </div>
                )}
                {/* Sep 20 2026, user's explicit ask ("this is not needed as
                    warehouse is not necessary") - Goods Movement is
                    permanently skipped for every GRN now (skip_movement=True,
                    see finalize_goods_receipt), so `sap_movement_status` is
                    always "not_applicable" going forward. Hiding this whole
                    block in that case (same pattern the Confirmed GRN detail
                    dialog below already uses) instead of showing a stale/
                    confusing "movement skipped" banner + a Retry button that
                    has nothing to retry. */}
                {shipment.sap_movement_status !== "not_applicable" && (
                  <div className="text-sm px-3 py-2 rounded-sm flex items-center justify-between gap-2 border" data-testid="grn-sap-movement-status"
                       style={shipment.sap_movement_status === "posted" ? { color: "#0B7A56", background: "rgba(16,185,129,0.1)", borderColor: "rgba(16,185,129,0.3)" } : { color: "#B45309", background: "rgba(227,160,8,0.1)", borderColor: "rgba(227,160,8,0.3)" }}>
                    <span className="flex items-center gap-2">
                      {shipment.sap_movement_status === "posted" ? <CheckCircle size={16} /> : <PlugsConnected size={16} />}
                      {shipment.sap_movement_status === "posted"
                        ? `Stock moved to ${shipment.site_id}/${shipment.warehouse_id}`
                        : `Warehouse movement pending${shipment.warehouse_id ? ` (target ${shipment.site_id}/${shipment.warehouse_id})` : ""}`}
                    </span>
                    {shipment.sap_movement_status !== "posted" && shipment.sap_sync_status === "posted" && (
                      <Button size="sm" variant="outline" onClick={retryMovement} disabled={busy} className="rounded-sm h-7 text-xs" data-testid="grn-retry-movement-button">
                        <ArrowsClockwise size={12} className="mr-1" /> Retry
                      </Button>
                    )}
                  </div>
                )}
                {/* Sep 12 2026, user's explicit ask ("after success grn
                    why an error occurred") - the badge above only ever
                    said "pending", never WHY. The real SAP error per
                    item was already being captured (sap_movement_result.
                    per_item[].error) but only ever surfaced in a toast
                    at the moment Retry was clicked - invisible on a
                    plain page load/lookup like this screenshot.
                    Sep 20 2026 fix (user's explicit ask - "shows a weird
                    looking error to the user"): a raw SAP fault/XML
                    string here during the NORMAL, expected, self-healing
                    retry window (see _post_goods_movement_for_items's
                    retry-on-lag docstring) looked alarming for something
                    that usually fixes itself within ~2 min with zero
                    action needed. Only show the real SAP error text once
                    a line has genuinely escalated (`needs_manual_check`,
                    after repeated real failures) - otherwise show a calm
                    "in progress" message instead. */}
                {shipment.sap_movement_status !== "not_applicable" && shipment.sap_movement_status !== "posted" && shipment.sap_movement_result?.per_item?.some((p) => p.error) && (
                  <div className={`text-xs rounded-sm px-3 py-2 space-y-1 ${shipment.sap_movement_result.per_item.some((p) => p.needs_manual_check) ? "text-[#B54708] bg-[#FFFAEB] border border-[#FEDF89]" : "text-[#175CD3] bg-[#EFF8FF] border border-[#B2DDFF]"}`} data-testid="grn-movement-error-detail">
                    {shipment.sap_movement_result.per_item.filter((p) => p.error).map((p, i) => (
                      <div key={i} data-testid={`grn-movement-error-${i}`}>
                        <strong>{p.product_id}:</strong> {p.needs_manual_check ? p.error : "Warehouse movement in progress - this usually resolves itself within a couple of minutes"}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {shipment.status === "approved" && invoiceAttachment?.path && (
              <div className="mt-4">
                <InvoicePreviewPanel docCode={shipment._id} attachment={invoiceAttachment} />
              </div>
            )}



            {shipment.status === "rejected" && shipment.rejection_reason && (
              <div className="mt-4 text-sm text-[#B91C1C]" data-testid="grn-rejection-reason">Reason: {shipment.rejection_reason}</div>
            )}
          </div>
        )}

        <div className="flex gap-1 bg-white border border-[#D0D5DD] rounded-sm p-1 w-fit mt-6" data-testid="grn-tab-strip">
          <button type="button" onClick={() => setActiveTab("pending")} className={`px-4 py-1.5 text-xs font-bold rounded-sm transition-colors ${activeTab === "pending" ? "bg-[#004B87] text-white" : "text-[#344054] hover:bg-[#F2F4F7]"}`} data-testid="grn-tab-pending">
            Pending Shipments
          </button>
          <button type="button" onClick={() => setActiveTab("confirmed")} className={`px-4 py-1.5 text-xs font-bold rounded-sm transition-colors ${activeTab === "confirmed" ? "bg-[#004B87] text-white" : "text-[#344054] hover:bg-[#F2F4F7]"}`} data-testid="grn-tab-confirmed">
            Confirmed GRNs
          </button>
        </div>

        {activeTab === "pending" && (
          <>
            <div className="flex items-center gap-3 mt-3">
              <Input
                placeholder="Search Shipment Code, PO Number, Vendor Code, or Item Code..."
                value={pendingFilter}
                onChange={(e) => setPendingFilter(e.target.value)}
                className="h-8 w-72 text-[13px] rounded-sm border-[#D0D5DD] focus-visible:border-[#004B87] focus-visible:ring-1 focus-visible:ring-[#004B87]"
                data-testid="grn-pending-filter-input"
              />
            </div>
            <div className="mt-3 bg-white border border-[#D0D5DD] rounded-sm shadow-[0_1px_2px_0_rgba(16,24,40,0.05)] overflow-x-auto">
              <table className="w-full text-xs border-collapse">
                <thead className="bg-[#EAECF0] text-[#344054] text-xs font-bold font-heading uppercase tracking-wide">
                  <tr>
                    <th className="border border-[#D0D5DD] p-1.5 text-left">Code</th>
                    <th className="border border-[#D0D5DD] p-1.5 text-left">Vendor</th>
                    <th className="border border-[#D0D5DD] p-1.5 text-left">PO Numbers</th>
                    <th className="border border-[#D0D5DD] p-1.5 text-left">Printed PO #</th>
                    <th className="border border-[#D0D5DD] p-1.5 text-left">Created</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPending.length === 0 && (
                    <tr><td colSpan={5} className="border border-[#D0D5DD] px-3 py-6 text-center text-[#475467]" data-testid="grn-pending-empty">{pending.length === 0 ? "No shipments awaiting GRN approval." : "No shipments match your search."}</td></tr>
                  )}
                  {filteredPending.map((s) => (
                    <tr key={s._id} className="cursor-pointer bg-white odd:bg-[#F9FAFB] hover:bg-[#F0F4F8] transition-colors duration-150" onClick={() => lookup(s._id)} data-testid={`grn-pending-row-${s._id}`}>
                      <td className="border border-[#D0D5DD] px-2 py-1 font-data font-bold text-[#004B87]">{s._id}</td>
                      <td className="border border-[#D0D5DD] px-2 py-1">{s.company_name}</td>
                      <td className="border border-[#D0D5DD] px-2 py-1 font-data">{[...new Set(s.items.map((it) => it.po_number))].join(", ")}</td>
                      <td className="border border-[#D0D5DD] px-2 py-1 font-data" data-testid={`grn-pending-printed-po-${s._id}`}>{[...new Set(s.items.map((it) => it.sap_po_number).filter(Boolean))].join(", ") || "\u2014"}</td>
                      <td className="border border-[#D0D5DD] px-2 py-1 text-[#475467]">{new Date(s.created_at).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {activeTab === "confirmed" && (
          <>
            <p className="text-xs text-[#475467] mt-3">Click a row for the full receipt trail - supplier bill number, SAP inbound delivery #, and item-level qty/unit/warehouse.</p>

            {/* Nov 2026, user's explicit ask: "GRN Approval - Advanced
            Filter Section ... add a dedicated Filter Panel above the
            results grid" - replaces the old single free-text box. */}
            <div className="mt-3 bg-white border border-[#D0D5DD] rounded-sm p-3" data-testid="grn-confirmed-filter-panel">
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5">
                <div>
                  <Label className="text-[11px] text-[#475467] font-semibold">PO Number</Label>
                  <Input value={confirmedPoFilter} onChange={(e) => setConfirmedPoFilter(e.target.value)} placeholder="e.g. 30361" className="h-8 mt-1 text-[13px] rounded-sm border-[#D0D5DD]" data-testid="grn-confirmed-filter-po-number" />
                </div>
                <div>
                  <Label className="text-[11px] text-[#475467] font-semibold">Shipment Code</Label>
                  <Input value={confirmedShipmentFilter} onChange={(e) => setConfirmedShipmentFilter(e.target.value)} placeholder="e.g. S000114" className="h-8 mt-1 text-[13px] rounded-sm border-[#D0D5DD]" data-testid="grn-confirmed-filter-shipment-code" />
                </div>
                <div>
                  <Label className="text-[11px] text-[#475467] font-semibold">PR Number</Label>
                  <Input value={confirmedPrFilter} onChange={(e) => setConfirmedPrFilter(e.target.value)} placeholder="e.g. 4500012345" className="h-8 mt-1 text-[13px] rounded-sm border-[#D0D5DD]" data-testid="grn-confirmed-filter-pr-number" />
                </div>
                <div>
                  <Label className="text-[11px] text-[#475467] font-semibold">Supplier Name</Label>
                  <Input value={confirmedSupplierFilter} onChange={(e) => setConfirmedSupplierFilter(e.target.value)} placeholder="e.g. Radish Technology" className="h-8 mt-1 text-[13px] rounded-sm border-[#D0D5DD]" data-testid="grn-confirmed-filter-supplier-name" />
                </div>
                <div>
                  <Label className="text-[11px] text-[#475467] font-semibold">Site</Label>
                  <Select value={confirmedSiteFilter} onValueChange={setConfirmedSiteFilter}>
                    <SelectTrigger className="h-8 mt-1 text-[13px] rounded-sm border-[#D0D5DD]" data-testid="grn-confirmed-filter-site">
                      <SelectValue placeholder="All Sites" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Sites</SelectItem>
                      {sites.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[11px] text-[#475467] font-semibold">Item Code / Item Name</Label>
                  <Input value={confirmedItemFilter} onChange={(e) => setConfirmedItemFilter(e.target.value)} placeholder="e.g. G12LW or Lock Washer" className="h-8 mt-1 text-[13px] rounded-sm border-[#D0D5DD]" data-testid="grn-confirmed-filter-item" />
                </div>
                <div>
                  <Label className="text-[11px] text-[#475467] font-semibold">From Date</Label>
                  <DateField value={confirmedDateFrom} onChange={setConfirmedDateFrom} disabledMatcher={(d) => d > parseISO(confirmedDateTo)} testId="grn-confirmed-filter-date-from" className="!h-8 !mt-1 !text-[13px] !border-[#D0D5DD]" />
                </div>
                <div>
                  <Label className="text-[11px] text-[#475467] font-semibold">To Date</Label>
                  <DateField value={confirmedDateTo} onChange={setConfirmedDateTo} disabledMatcher={(d) => d < parseISO(confirmedDateFrom)} testId="grn-confirmed-filter-date-to" className="!h-8 !mt-1 !text-[13px] !border-[#D0D5DD]" />
                </div>
              </div>
              <div className="flex items-center gap-2 mt-3">
                <Button type="button" onClick={() => loadConfirmed()} className="h-8 px-4 text-[13px] bg-[#004B87] hover:bg-[#003a6b]" data-testid="grn-confirmed-filter-search-button">
                  <MagnifyingGlass size={14} className="mr-1.5" weight="bold" /> Search
                </Button>
                <Button type="button" variant="outline" onClick={clearConfirmedFilters} className="h-8 px-4 text-[13px]" data-testid="grn-confirmed-filter-clear-button">
                  Clear Filters
                </Button>
                <span className="text-[13px] text-[#475467] ml-2" data-testid="grn-confirmed-filter-total-count">
                  {confirmedTotalCount} record{confirmedTotalCount === 1 ? "" : "s"} found
                </span>
              </div>
            </div>

            <div className="mt-3 bg-white border border-[#D0D5DD] rounded-sm shadow-[0_1px_2px_0_rgba(16,24,40,0.05)] overflow-x-auto">
              <table className="w-full text-xs border-collapse">
                <thead className="bg-[#EAECF0] text-[#344054] text-xs font-bold font-heading uppercase tracking-wide">
                  <tr>
                    <th className="border border-[#D0D5DD] p-1.5 text-left">Code</th>
                    <th className="border border-[#D0D5DD] p-1.5 text-left">Vendor</th>
                    <th className="border border-[#D0D5DD] p-1.5 text-left">PO Numbers</th>
                    <th className="border border-[#D0D5DD] p-1.5 text-left">Printed PO #</th>
                    <th className="border border-[#D0D5DD] p-1.5 text-left">Supplier Invoice No</th>
                    <th className="border border-[#D0D5DD] p-1.5 text-left whitespace-nowrap">Supplier Invoice Date</th>
                    <th className="border border-[#D0D5DD] p-1.5 text-left">SAP Inbound Delivery #</th>
                    <th className="border border-[#D0D5DD] p-1.5 text-left">SAP Reference</th>
                    <th className="border border-[#D0D5DD] p-1.5 text-left">SAP Status</th>
                    <th className="border border-[#D0D5DD] p-1.5 text-left">Approved</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredConfirmed.length === 0 && (
                    <tr><td colSpan={10} className="border border-[#D0D5DD] px-3 py-6 text-center text-[#475467]" data-testid="grn-confirmed-empty">{confirmedTotalCount === 0 ? "No confirmed GRNs match these filters." : "No confirmed GRNs yet."}</td></tr>
                  )}
                  {filteredConfirmed.map((s) => (
                    <tr key={s._id} className="cursor-pointer bg-white odd:bg-[#F9FAFB] hover:bg-[#F0F4F8] transition-colors duration-150" onClick={() => setConfirmedDetail(s)} data-testid={`grn-confirmed-row-${s._id}`}>
                      <td className="border border-[#D0D5DD] px-2 py-1 font-data font-bold text-[#004B87]">
                        {s._id}
                        {isGrnAdmin && s.sap_over_received_items?.length > 0 && (
                          <div className="mt-1" data-testid={`grn-confirmed-over-received-${s._id}`}>
                            <StatusText
                              tone="danger"
                              className="text-[10px]"
                              title={`Inbound exceeds PO qty in SAP (cache was stale or qty posted outside this app): ${s.sap_over_received_items.map((o) => `${o.product_id || o.item_number} on PO ${o.po_number} - ordered ${o.po_qty}, SAP shows ${o.delivered_qty} delivered (${o.over_qty} over)`).join("; ")}`}
                            >
                              Over-Received in SAP
                            </StatusText>
                          </div>
                        )}
                        {isGrnAdmin && s.sap_fulfillment_variance_items?.length > 0 && (
                          <div className="mt-1" data-testid={`grn-confirmed-fulfillment-variance-${s._id}`}>
                            <StatusText
                              tone="warning"
                              className="text-[10px]"
                              title={`Delivery Notification Qty vs Fulfilled Qty mismatch in SAP - Put Away not yet confirmed: ${s.sap_fulfillment_variance_items.map((v) => `${v.product_id || v.item_number} on PO ${v.po_number} - SAP notification qty ${v.notification_qty}${v.release_stuck ? " (SAP Release stuck)" : ""}`).join("; ")}`}
                            >
                              Fulfillment Pending in SAP
                            </StatusText>
                          </div>
                        )}
                      </td>
                      <td className="border border-[#D0D5DD] px-2 py-1">{s.company_name}</td>
                      <td className="border border-[#D0D5DD] px-2 py-1 font-data">{[...new Set(s.items.map((it) => it.po_number))].join(", ")}</td>
                      <td className="border border-[#D0D5DD] px-2 py-1 font-data" data-testid={`grn-confirmed-printed-po-${s._id}`}>{[...new Set(s.items.map((it) => it.sap_po_number).filter(Boolean))].join(", ") || "\u2014"}</td>
                      <td className="border border-[#D0D5DD] px-2 py-1 font-data">{s.supplier_doc_num || "\u2014"}</td>
                      <td className="border border-[#D0D5DD] px-2 py-1 font-data whitespace-nowrap" data-testid={`grn-confirmed-bill-date-${s._id}`}>{s.bill_date ? format(parseISO(s.bill_date), "dd-MM-yyyy") : "\u2014"}</td>
                      <td className="border border-[#D0D5DD] px-2 py-1 font-data" data-testid={`grn-confirmed-inbound-id-${s._id}`}>
                        {inboundDeliveryIds(s).length > 0 ? (
                          <span className="inline-flex items-center gap-1.5">
                            {inboundDeliveryIds(s).join(", ")}
                            {hasReversedInboundDelivery(s) && (
                              <StatusText tone="danger" testId={`grn-confirmed-reversed-tag-${s._id}`} className="text-[10px]">Reversed In SAP</StatusText>
                            )}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-2">
                            <span>{"\u2014"}</span>
                            <button
                              type="button"
                              className="text-[11px] text-[#004B87] font-bold hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
                              disabled={fetchingInboundFor === s._id}
                              data-testid={`grn-fetch-inbound-delivery-button-${s._id}`}
                              onClick={(e) => { e.stopPropagation(); fetchInboundDelivery(s); }}
                            >
                              {fetchingInboundFor === s._id ? "Fetching from SAP..." : "Fetch from SAP"}
                            </button>
                          </span>
                        )}
                      </td>
                      <td className="border border-[#D0D5DD] px-2 py-1 font-data text-[#475467]" data-testid={`grn-confirmed-sap-reference-${s._id}`}>
                        {Object.keys(s.manual_gr_notification_ids || {}).length > 0
                          ? [...new Set(Object.values(s.manual_gr_notification_ids))].join(", ")
                          : "\u2014"}
                      </td>
                      <td className="border border-[#D0D5DD] px-2 py-1" data-testid={`grn-confirmed-sap-status-${s._id}`}>
                        {(s.sap_gr_result?.per_po || []).some((p) => p.release_stuck) && s.sap_sync_status !== "put_away_failed" ? (
                          <StatusText tone="danger">Receipt Not Completed</StatusText>
                        ) : s.sap_sync_status === "posted" ? (
                          <StatusText tone="success">Posted</StatusText>
                        ) : s.sap_sync_status === "failed" ? (
                          <StatusText tone="danger">Sync Failed</StatusText>
                        ) : s.sap_sync_status === "put_away_failed" ? (
                          <StatusText tone="danger">Receipt Not Completed</StatusText>
                        ) : s.sap_sync_status === "awaiting_manual_gr" ? (
                          <StatusText tone="info">Awaiting Manual GR</StatusText>
                        ) : s.sap_sync_status === "manual_mismatch" ? (
                          <StatusText tone="danger">Qty Mismatch</StatusText>
                        ) : (
                          <StatusText tone="warning">In Process</StatusText>
                        )}
                        {s.sap_movement_result?.per_item?.length > 0 && (
                          <div className="mt-1" data-testid={`grn-confirmed-movement-${s._id}`}>
                            <MovementPopover items={s.sap_movement_result.per_item} warehouseLabel={s.warehouse_id} docCode={s._id}>
                              <span className={`text-[11px] ${s.sap_movement_status === "posted" ? "text-[#027A48]" : "text-[#B54708]"}`}>
                                {s.sap_movement_status === "posted" ? `Moved to ${s.warehouse_id}` : "Warehouse move pending"}
                              </span>
                            </MovementPopover>
                          </div>
                        )}
                      </td>
                      <td className="border border-[#D0D5DD] px-2 py-1 text-[#475467]">{s.approved_at ? new Date(s.approved_at).toLocaleString() : "\u2014"}</td>
                    </tr>
                  ))}
                </tbody>

              </table>
            </div>
          </>
        )}
      </div>

      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent className="rounded-sm" data-testid="grn-reject-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Reject Shipment</DialogTitle>
            <DialogDescription>This reason will be shown to the vendor.</DialogDescription>
          </DialogHeader>
          <Textarea placeholder="Reason" value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} className="rounded-sm border-[#D0D5DD]" data-testid="grn-reject-reason-input" />
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="outline" className="rounded-sm" onClick={() => setRejectOpen(false)}>Cancel</Button>
            <Button onClick={reject} disabled={busy} className="rounded-sm bg-[#E02424] hover:bg-[#B91C1C] transition-colors duration-150" data-testid="grn-reject-confirm-button">Confirm Reject</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={cancelGrnOpen} onOpenChange={setCancelGrnOpen}>
        <DialogContent className="rounded-sm" data-testid="grn-cancel-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Cancel GRN {cancelGrnTargetCode}?</DialogTitle>
            <DialogDescription>
              SAP will be live-verified first - this only succeeds if SAP confirms the matching Inbound Delivery is genuinely Cancelled. The shipment will then be marked Cancelled and the vendor's dashboard will reflect it. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="outline" className="rounded-sm" onClick={() => setCancelGrnOpen(false)} data-testid="grn-cancel-dialog-dismiss-button">Back</Button>
            <Button onClick={cancelGrn} disabled={busy} className="rounded-sm bg-[#B42318] hover:bg-[#912018] transition-colors duration-150" data-testid="grn-cancel-confirm-button">Verify with SAP &amp; Cancel</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={blockedUsersOpen} onOpenChange={setBlockedUsersOpen}>
        <DialogContent className="rounded-sm max-w-xl" data-testid="grn-blocked-users-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Blocked GRN Users</DialogTitle>
            <DialogDescription>Users blocked from approving new GRNs due to an unresolved Manual GRN quantity mismatch. Overriding requires a reason and is logged for audit.</DialogDescription>
          </DialogHeader>
          {blockedUsers.length === 0 ? (
            <div className="text-sm text-[#667085] py-4" data-testid="grn-blocked-users-empty">No users are currently blocked.</div>
          ) : (
            <div className="space-y-3 max-h-[60vh] overflow-auto">
              {blockedUsers.map((u) => (
                <div key={u._id} className="border border-[#D0D5DD] rounded-sm p-3 space-y-2" data-testid={`grn-blocked-user-row-${u._id}`}>
                  <div className="text-sm font-semibold text-[#1D2939]">{u.name || u.email}</div>
                  <div className="text-xs text-[#B42318]">Shipment {u.grn_blocked_shipment}: {u.grn_blocked_reason}</div>
                  <Textarea
                    placeholder="Override reason (required, logged for audit)"
                    value={overrideReasons[u._id] || ""}
                    onChange={(e) => setOverrideReasons((prev) => ({ ...prev, [u._id]: e.target.value }))}
                    className="rounded-sm border-[#D0D5DD] text-sm"
                    data-testid={`grn-override-reason-input-${u._id}`}
                  />
                  <Button
                    size="sm"
                    onClick={() => overrideBlock(u._id)}
                    disabled={overrideBusyId === u._id}
                    className="rounded-sm bg-[#B54708] hover:bg-[#93370D] text-white text-xs"
                    data-testid={`grn-override-block-button-${u._id}`}
                  >
                    {overrideBusyId === u._id ? "Overriding..." : "Override & Unblock User"}
                  </Button>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={varianceRemarkOpen} onOpenChange={setVarianceRemarkOpen}>
        <DialogContent className="rounded-sm max-w-lg" data-testid="grn-variance-remark-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Record Variance &amp; Continue</DialogTitle>
            <DialogDescription>
              Enter a remark explaining this variance - it's logged permanently (GRN Variance Log) and then
              "Validate GRN"/"Post GRN to SAP" unlock with these exact values. No further approval step needed.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-56 overflow-y-auto border border-[#D0D5DD] rounded-sm p-2 text-xs" data-testid="grn-variance-remark-items">
            {qtyVarianceItems.map((it, i) => {
              const key = `${it.po_number}::${it.item_number}`;
              const effectiveQty = Number(actualQtys[key] ?? it.actual_qty ?? it.ship_qty ?? 0);
              const delta = effectiveQty - Number(it.ship_qty);
              return (
                <div key={`qty-${i}`} className="flex items-center justify-between gap-2" data-testid={`grn-variance-remark-qty-item-${i}`}>
                  <span className="font-data">{it.po_number}/{it.item_number} - {it.description}</span>
                  <span className={`font-semibold whitespace-nowrap ${delta < 0 ? "text-[#B42318]" : "text-[#027A48]"}`}>
                    Ship {Number(it.ship_qty).toFixed(2)} &rarr; Actual {effectiveQty.toFixed(2)} ({delta > 0 ? "+" : ""}{delta.toFixed(2)} {it.unit_of_measure})
                  </span>
                </div>
              );
            })}
            {priceVarianceIssues.map((iss, i) => {
              const it = shipment?.items?.[iss.grn_item_index];
              if (!it) return null;
              const delta = iss.ai_rate - iss.po_rate;
              return (
                <div key={`price-${i}`} className="flex items-center justify-between gap-2" data-testid={`grn-variance-remark-price-item-${i}`}>
                  <span className="font-data">{it.po_number}/{it.item_number} - {it.description}</span>
                  <span className={`font-semibold whitespace-nowrap ${delta < 0 ? "text-[#027A48]" : "text-[#B42318]"}`}>
                    PO {Number(iss.po_rate).toFixed(2)} &rarr; Invoice {Number(iss.ai_rate).toFixed(2)} {it.currency || ""} ({delta > 0 ? "+" : ""}{delta.toFixed(2)})
                  </span>
                </div>
              );
            })}
          </div>
          <Textarea
            placeholder="e.g. Physical count 8650 EA vs shipped 12250 EA - debit note raised to supplier for the shortfall"
            value={varianceRemarkText}
            onChange={(e) => setVarianceRemarkText(e.target.value)}
            className="rounded-sm border-[#D0D5DD]"
            data-testid="grn-variance-remark-input"
          />
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="outline" className="rounded-sm" onClick={() => setVarianceRemarkOpen(false)} data-testid="grn-variance-remark-cancel-button">Cancel</Button>
            <Button
              onClick={submitVarianceRemark}
              disabled={varianceRemarkBusy || !varianceRemarkText.trim()}
              className="rounded-sm bg-[#079455] hover:bg-[#067647] text-white transition-colors duration-150"
              data-testid="grn-variance-remark-submit-button"
            >
              {varianceRemarkBusy ? "Saving..." : "Save & Unlock"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={!!confirmedDetail} onOpenChange={(o) => !o && setConfirmedDetail(null)}>
        <DialogContent className="rounded-sm max-w-4xl" data-testid="grn-confirmed-detail-dialog">
          {confirmedDetail && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading font-data text-[#004B87]" data-testid="grn-confirmed-detail-code">{confirmedDetail._id}</DialogTitle>
                <DialogDescription>{confirmedDetail.company_name} ({confirmedDetail.vendor_code})</DialogDescription>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-y-2 gap-x-6 text-sm" data-testid="grn-confirmed-detail-meta">
                <div><span className="text-[#475467]">PO Number(s):</span> <span className="font-data font-semibold">{[...new Set(confirmedDetail.items.map((it) => it.po_number))].join(", ")}</span></div>
                <div><span className="text-[#475467]">GRN Date (Physical):</span> <span className="font-data font-semibold" data-testid="grn-confirmed-detail-grn-date">{confirmedDetail.grn_date_physical ? format(parseISO(confirmedDetail.grn_date_physical), "dd-MM-yyyy") : "\u2014"}</span></div>
                {[...new Set(confirmedDetail.items.map((it) => it.sap_po_number).filter(Boolean))].length > 0 && (
                  <div><span className="text-[#475467]">Printed PO #(s):</span> <span className="font-data font-semibold" data-testid="grn-confirmed-detail-printed-po-number">{[...new Set(confirmedDetail.items.map((it) => it.sap_po_number).filter(Boolean))].join(", ")}</span></div>
                )}
                <div><span className="text-[#475467]">Supplier Invoice No:</span> <span className="font-data font-semibold">{confirmedDetail.supplier_doc_num || "\u2014"}</span></div>
                <div><span className="text-[#475467]">Supplier Invoice Date:</span> <span className="font-data font-semibold">{confirmedDetail.bill_date || "\u2014"}</span></div>
                <div><span className="text-[#475467]">Supplier Invoice Copy:</span>{" "}
                  {confirmedDetail.invoice_attachment ? (
                    <Button type="button" variant="ghost" size="sm" className="h-6 px-2 text-xs rounded-sm font-semibold text-[#004B87]" onClick={() => viewInvoiceAttachment(confirmedDetail._id)} data-testid="grn-confirmed-detail-invoice-attachment-view">
                      <Paperclip size={12} className="mr-1" /> {confirmedDetail.invoice_attachment.filename}
                    </Button>
                  ) : (
                    <span className="font-semibold text-[#98A2B3]">{"\u2014"}</span>
                  )}
                </div>
                {confirmedDetail.sap_gr_result?.sap_username && (
                  <div><span className="text-[#475467]">SAP User Used:</span> <span className="font-data font-semibold" data-testid="grn-confirmed-detail-sap-username">{confirmedDetail.sap_gr_result.sap_username}</span></div>
                )}
                <div><span className="text-[#475467]">SAP Inbound Delivery #:</span>{" "}
                  {confirmedDetail.sap_sync_status === "posted" && !(confirmedDetail.sap_gr_result?.per_po || []).some((p) => p.release_stuck) ? (
                    <span className="font-data font-semibold" data-testid="grn-confirmed-detail-inbound-id">
                      {inboundDeliveryIds(confirmedDetail).join(", ") || "\u2014"}
                      {hasReversedInboundDelivery(confirmedDetail) && (
                        <StatusText tone="danger" testId="grn-confirmed-detail-reversed-tag" className="text-[10px] ml-1.5">Reversed In SAP</StatusText>
                      )}
                      {isGrnAdmin && confirmedDetail.status === "approved" && (!["posted", "partial"].includes(confirmedDetail.sap_sync_status) || hasReversedInboundDelivery(confirmedDetail)) && (
                        <Button size="sm" variant="outline" onClick={() => { setCancelGrnTargetCode(confirmedDetail._id); setCancelGrnOpen(true); }} disabled={busy} className="rounded-sm h-6 px-2 text-[11px] ml-2 border-[#B42318] text-[#B42318]" data-testid="grn-confirmed-detail-cancel-button" title="Only succeeds if SAP confirms this shipment's Inbound Delivery is Cancelled - verified live against SAP before cancelling">
                          <XCircle size={11} className="mr-1" /> Cancel GRN
                        </Button>
                      )}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5">
                      {(confirmedDetail.sap_gr_result?.per_po || []).some((p) => p.release_stuck) && confirmedDetail.sap_sync_status !== "put_away_failed" ? (
                        <StatusText tone="danger" testId="grn-confirmed-detail-release-stuck-badge" title="Open this shipment from the main Lookup page to use Resend Delivery">Receipt Not Completed</StatusText>
                      ) : confirmedDetail.sap_sync_status === "failed" ? (
                        <StatusText tone="danger" testId="grn-confirmed-detail-failed-badge">Sync Failed</StatusText>
                      ) : confirmedDetail.sap_sync_status === "put_away_failed" ? (
                        <StatusText tone="danger" testId="grn-confirmed-detail-put-away-failed-badge" title={confirmedDetail.grn_alert_message}>Receipt Not Completed</StatusText>
                      ) : (
                        <StatusText tone="warning" testId="grn-confirmed-detail-in-process-badge">In Process</StatusText>
                      )}
                      {confirmedRetryBusy ? (
                        <span className="text-xs text-[#B54708] font-semibold" data-testid="grn-confirmed-detail-retrying-label">Retrying - checking SAP now...</span>
                      ) : confirmedDetail.sap_sync_status === "failed" ? (
                        <Button size="sm" variant="outline" className="h-6 px-2 text-[11px]" disabled={confirmedRetryBusy} onClick={() => resetConfirmedRetry(confirmedDetail)} title="Only use once your SAP Admin confirms the underlying SAP error is fixed" data-testid="grn-confirmed-detail-reset-retry-button">
                          <ArrowsClockwise size={11} className="mr-1" /> Reset Retry
                        </Button>
                      ) : confirmedDetail.sap_sync_status === "put_away_failed" ? (
                        <Button size="sm" variant="outline" className="h-6 px-2 text-[11px]" disabled={confirmedRetryBusy} onClick={() => retryConfirmedPutAway(confirmedDetail)} title="Only use once your SAP Admin confirms the underlying SAP error is fixed" data-testid="grn-confirmed-detail-retry-put-away-button">
                          <ArrowsClockwise size={11} className="mr-1" /> Retry Put Away
                        </Button>
                      ) : retryUnlockInMin(confirmedDetail.approved_at) > 0 ? (
                        <span className="text-xs text-[#98A2B3]" data-testid="grn-confirmed-detail-retry-cooldown">Retry available in {retryUnlockInMin(confirmedDetail.approved_at)}m</span>
                      ) : (
                        <Button size="sm" variant="outline" className="h-6 px-2 text-[11px]" disabled={confirmedRetryBusy} onClick={() => retryConfirmedGoodsReceipt(confirmedDetail)} data-testid="grn-confirmed-detail-retry-button">
                          <ArrowsClockwise size={11} className="mr-1" /> Retry
                        </Button>
                      )}
                      {isGrnAdmin && confirmedDetail.status === "approved" && (
                        <Button size="sm" variant="outline" onClick={() => { setCancelGrnTargetCode(confirmedDetail._id); setCancelGrnOpen(true); }} disabled={busy} className="rounded-sm h-6 px-2 text-[11px] ml-1 border-[#B42318] text-[#B42318]" data-testid="grn-confirmed-detail-cancel-button" title="Only succeeds if SAP confirms this shipment's Inbound Delivery is Cancelled - verified live against SAP before cancelling">
                          <XCircle size={11} className="mr-1" /> Cancel GRN
                        </Button>
                      )}
                    </span>
                  )}
                </div>
                <div><span className="text-[#475467]">Site:</span> <span className="font-data font-semibold">{confirmedDetail.site_id}</span></div>
                <div><span className="text-[#475467]">Approved By:</span> <span className="font-semibold">{confirmedDetail.approved_by} · {confirmedDetail.approved_at ? new Date(confirmedDetail.approved_at).toLocaleString() : "\u2014"}</span></div>
              </div>
              {confirmedDetail.invoice_attachment && (
                <InvoicePreviewPanel docCode={confirmedDetail._id} attachment={confirmedDetail.invoice_attachment} />
              )}
              {confirmedDetail.sap_movement_status && confirmedDetail.sap_movement_status !== "not_applicable" && (
                <div className="text-sm px-3 py-2 rounded-sm flex items-center justify-between gap-2 border" data-testid="grn-confirmed-detail-sap-movement-status"
                     style={confirmedDetail.sap_movement_status === "posted" ? { color: "#0B7A56", background: "rgba(16,185,129,0.1)", borderColor: "rgba(16,185,129,0.3)" } : { color: "#B45309", background: "rgba(227,160,8,0.1)", borderColor: "rgba(227,160,8,0.3)" }}>
                  <span className="flex items-center gap-2">
                    {confirmedDetail.sap_movement_status === "posted" ? <CheckCircle size={16} /> : <PlugsConnected size={16} />}
                    {confirmedDetail.sap_movement_status === "posted"
                      ? `Stock moved to ${confirmedDetail.site_id}/${confirmedDetail.warehouse_id}`
                      : `Warehouse movement pending${confirmedDetail.warehouse_id ? ` (target ${confirmedDetail.site_id}/${confirmedDetail.warehouse_id})` : ""}`}
                  </span>
                  {confirmedDetail.sap_movement_status !== "posted" && (
                    confirmedRetryBusy ? (
                      <span className="text-xs text-[#B54708] font-semibold" data-testid="grn-confirmed-detail-movement-retrying-label">Retrying...</span>
                    ) : (
                      <Button size="sm" variant="outline" onClick={() => retryConfirmedMovement(confirmedDetail)} disabled={confirmedRetryBusy} className="rounded-sm h-7 text-xs" data-testid="grn-confirmed-detail-retry-movement-button">
                        <ArrowsClockwise size={12} className="mr-1" /> Retry
                      </Button>
                    )
                  )}
                </div>
              )}
              {confirmedDetail.sap_movement_status && confirmedDetail.sap_movement_status !== "posted" && confirmedDetail.sap_movement_result?.per_item?.some((p) => p.error) && (
                <div className={`text-xs rounded-sm px-3 py-2 space-y-1 ${confirmedDetail.sap_movement_result.per_item.some((p) => p.needs_manual_check) ? "text-[#B54708] bg-[#FFFAEB] border border-[#FEDF89]" : "text-[#175CD3] bg-[#EFF8FF] border border-[#B2DDFF]"}`} data-testid="grn-confirmed-detail-movement-error-detail">
                  {confirmedDetail.sap_movement_result.per_item.filter((p) => p.error).map((p, i) => (
                    <div key={i} data-testid={`grn-confirmed-detail-movement-error-${i}`}>
                      <strong>{p.product_id}:</strong> {p.needs_manual_check ? p.error : "Warehouse movement in progress - this usually resolves itself within a couple of minutes"}
                    </div>
                  ))}
                </div>
              )}
              {confirmedDetail.sap_sync_status !== "posted" && confirmedDetail.sap_gr_result?.per_po?.some((p) => p.screenshot_path) && (
                <div>
                  <button
                    type="button"
                    className="text-xs text-[#7A1E1E] font-bold hover:underline"
                    data-testid="grn-confirmed-detail-view-failure-screenshot-button"
                    onClick={() => {
                      const failed = confirmedDetail.sap_gr_result.per_po.find((p) => p.screenshot_path);
                      setScreenshotModal(failed);
                    }}
                  >
                    View exact SAP screen at time of failure ({describeFailedStep(confirmedDetail.sap_gr_result.per_po.find((p) => p.screenshot_path)?.failed_step)})
                  </button>
                </div>
              )}
              {confirmedDetail.sap_gr_result?.per_po?.some((p) => (p.status !== "posted" && p.events?.length) || p.skipped_items?.length) && (
                <div>
                  <button
                    type="button"
                    className="text-xs text-[#475467] font-bold hover:underline"
                    data-testid="grn-confirmed-detail-view-diagnostics-button"
                    onClick={() => setDiagnosticsModal(poDiagnostics(confirmedDetail, confirmedDetail.sap_gr_result.per_po.find((p) => (p.status !== "posted" && p.events?.length) || p.skipped_items?.length)))}
                  >
                    View Diagnostics
                  </button>
                </div>
              )}
              <table className="border-collapse w-full text-[11px] mt-2 border border-[#D0D5DD] rounded-sm overflow-hidden">
                <thead className="bg-[#EAECF0] text-[#344054] text-[11px] font-bold font-heading tracking-wide">
                  <tr>
                    <th className="border border-[#D0D5DD] p-1.5 text-left whitespace-nowrap">PO Number</th>
                    <th className="border border-[#D0D5DD] p-1.5 text-left whitespace-nowrap">Item Code</th>
                    <th className="border border-[#D0D5DD] p-1.5 text-left">Description</th>
                    <th className="border border-[#D0D5DD] p-1.5 text-right">Qty</th>
                    <th className="border border-[#D0D5DD] p-1.5 text-left">Unit</th>
                    <th className="border border-[#D0D5DD] p-1.5 text-left whitespace-nowrap">Warehouse</th>
                    <th className="border border-[#D0D5DD] p-1.5 text-right whitespace-nowrap">PO Price</th>
                    <th className="border border-[#D0D5DD] p-1.5 text-right whitespace-nowrap">Line Value</th>
                    {confirmedDetail.sap_gr_result?.per_po && (
                      <th className="border border-[#D0D5DD] p-1.5 text-left whitespace-nowrap">PO Status</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {confirmedDetail.items.map((it, i) => {
                    const qty = it.actual_qty ?? it.ship_qty;
                    const lineValue = it.unit_price != null ? qty * it.unit_price : null;
                    const poResult = confirmedDetail.sap_gr_result?.per_po?.find((p) => p.po_number === it.po_number);
                    return (
                      <tr key={i} className="bg-white odd:bg-[#F9FAFB]" data-testid={`grn-confirmed-detail-item-${i}`}>
                        <td className="border border-[#D0D5DD] px-2 py-1 font-data whitespace-nowrap" data-testid={`grn-confirmed-detail-po-number-${i}`}>{it.po_number}</td>
                        <td className="border border-[#D0D5DD] px-2 py-1 font-data font-semibold whitespace-nowrap">{it.product_id || "\u2014"}</td>
                        <td className="border border-[#D0D5DD] px-2 py-1">{it.description}</td>
                        <td className="border border-[#D0D5DD] px-2 py-1 text-right font-data whitespace-nowrap">{qty}</td>
                        <td className="border border-[#D0D5DD] px-2 py-1 font-data whitespace-nowrap">{it.unit_of_measure}</td>
                        <td className="border border-[#D0D5DD] px-2 py-1 font-data whitespace-nowrap">{confirmedDetail.site_id}/{confirmedDetail.warehouse_id}</td>
                        <td className="border border-[#D0D5DD] px-2 py-1 text-right font-data text-[#475467] whitespace-nowrap" data-testid={`grn-confirmed-detail-po-price-${i}`}>
                          {it.unit_price != null ? `${it.currency || ""} ${fmtUnitPrice(it.unit_price)}` : "\u2014"}
                        </td>
                        <td className="border border-[#D0D5DD] px-2 py-1 text-right font-data font-semibold text-[#1D2939] whitespace-nowrap" data-testid={`grn-confirmed-detail-line-value-${i}`}>
                          {lineValue != null ? `${it.currency || ""} ${lineValue.toFixed(2)}` : "\u2014"}
                        </td>
                        {confirmedDetail.sap_gr_result?.per_po && (
                          <td className="border border-[#D0D5DD] px-2 py-1 whitespace-nowrap" data-testid={`grn-confirmed-detail-po-status-${i}`}>
                            {poResult?.status === "notification_created" ? (
                              <span className="text-[11px] font-semibold text-[#3538CD]">Notification Created</span>
                            ) : poResult ? (
                              <button
                                type="button"
                                className={`text-[11px] font-semibold hover:underline ${poResult.release_stuck ? "text-[#B42318]" : poResult.status !== "posted" ? (poResult.status === "skipped" ? "text-[#B54708]" : "text-[#B42318]") : poDiagnostics(confirmedDetail, poResult).movement_issues.length > 0 ? "text-[#B54708]" : "text-[#027A48]"}`}
                                onClick={() => setDiagnosticsModal(poDiagnostics(confirmedDetail, poResult))}
                                data-testid={`grn-confirmed-detail-diagnostics-link-${i}`}
                              >
                                {poResult.release_stuck ? "Receipt Incomplete" : poResult.status === "posted" ? (poDiagnostics(confirmedDetail, poResult).movement_issues.length > 0 ? "Posted (movement failed)" : "Posted") : poResult.status === "skipped" ? "Skipped" : "Failed"} - Diagnostics
                              </button>
                            ) : (
                              <span className="text-[11px] text-[#98A2B3]">Not attempted yet</span>
                            )}
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
                {(() => {
                  const valuedItems = confirmedDetail.items.filter((it) => it.unit_price != null);
                  if (!valuedItems.length) return null;
                  const total = valuedItems.reduce((sum, it) => sum + (it.actual_qty ?? it.ship_qty) * it.unit_price, 0);
                  const currency = valuedItems[0].currency || "";
                  return (
                    <tfoot>
                      <tr className="bg-[#F2F4F7]">
                        <td colSpan={7} className="border border-[#D0D5DD] px-2 py-1.5 text-right text-[11px] font-bold text-[#344054] font-heading whitespace-nowrap">Total (Matches Bill Amount)</td>
                        <td className="border border-[#D0D5DD] px-2 py-1.5 text-right font-data font-bold text-[#004B87] whitespace-nowrap" data-testid="grn-confirmed-detail-total-value">
                          {currency} {total.toFixed(2)}
                        </td>
                        {confirmedDetail.sap_gr_result?.per_po && <td className="border border-[#D0D5DD]"></td>}
                      </tr>
                    </tfoot>
                  );
                })()}
              </table>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!screenshotModal} onOpenChange={(o) => !o && setScreenshotModal(null)}>
        <DialogContent className="rounded-sm max-w-3xl" data-testid="grn-failure-screenshot-dialog">
          {screenshotModal && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-[#7A1E1E]">PO {screenshotModal.po_number} - failed while {describeFailedStep(screenshotModal.failed_step)}</DialogTitle>
                <DialogDescription className="font-data text-xs">{screenshotModal.error}</DialogDescription>
              </DialogHeader>
              {screenshotModal.screenshot_path ? (
                <img
                  src={`${API}/admin/grn/failure-screenshot?path=${encodeURIComponent(screenshotModal.screenshot_path)}`}
                  alt="SAP screen at time of failure"
                  className="w-full border border-[#D0D5DD] rounded-sm"
                  data-testid="grn-failure-screenshot-image"
                />
              ) : (
                <p className="text-sm text-[#667085]">No screenshot could be captured for this attempt.</p>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!diagnosticsModal} onOpenChange={(o) => !o && setDiagnosticsModal(null)}>
        <DialogContent className="rounded-sm max-w-xl" data-testid="grn-diagnostics-dialog">
          {diagnosticsModal && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-[#344054]">PO {diagnosticsModal.po_number} - Diagnostics</DialogTitle>
                <DialogDescription className="font-data text-xs">
                  {diagnosticsModal.status === "skipped" ? (
                    <span className="text-[#B54708] font-semibold">Skipped - never sent to SAP</span>
                  ) : diagnosticsModal.status === "posted" ? (
                    diagnosticsModal.skipped_items?.length > 0 ? (
                      <span className="text-[#B54708] font-semibold">Posted to SAP - but item(s) below were dropped from this PO</span>
                    ) : diagnosticsModal.movement_issues?.length > 0 ? (
                      <span className="text-[#B54708] font-semibold">Posted to SAP - but the warehouse movement into place failed</span>
                    ) : (
                      <span className="text-[#027A48] font-semibold">Posted to SAP successfully - no issues</span>
                    )
                  ) : (
                    <>Failed while: <strong className="text-[#7A1E1E]">{describeFailedStep(diagnosticsModal.failed_step)}</strong></>
                  )}
                </DialogDescription>
              </DialogHeader>
              {/* Oct 2026, user's explicit ask ("click and open
                  diagnostics, it should show the Inbound ID and Goods
                  Movement ID") - the SAP Inbound Delivery ID for this
                  PO (poDiagnostics already carries it from sap_gr_
                  result.per_po) plus any Goods Movement ID(s) (external_
                  id) this PO's own movement lines posted with. */}
              {(diagnosticsModal.inbound_delivery_id || diagnosticsModal.movement_ids?.length > 0) && (
                <div className="text-sm bg-[#F9FAFB] border border-[#EAECF0] rounded-sm p-2 space-y-1" data-testid="grn-diagnostics-sap-ids">
                  {diagnosticsModal.inbound_delivery_id && (
                    <p data-testid="grn-diagnostics-inbound-delivery-id"><strong>Inbound Delivery ID:</strong> {diagnosticsModal.inbound_delivery_id}</p>
                  )}
                  {diagnosticsModal.movement_ids?.length > 0 && (
                    <p data-testid="grn-diagnostics-goods-movement-id" title={diagnosticsModal.movement_ids.join(", ")}><strong>Goods Movement ID{diagnosticsModal.movement_ids.length > 1 ? "s" : ""}:</strong> {diagnosticsModal.movement_ids.map(shortMovementId).join(", ")}</p>
                  )}
                </div>
              )}
              {diagnosticsModal.error && (
                <div className="text-sm text-[#7A1E1E] bg-[#FEF3F2] border border-[#FDA29B] rounded-sm p-2" data-testid="grn-diagnostics-sap-error">
                  <strong>{diagnosticsModal.status === "skipped" ? "Reason:" : "SAP Error:"}</strong> {diagnosticsModal.error}
                </div>
              )}
              {diagnosticsModal.skipped_items?.length > 0 && (
                <div className="text-sm text-[#93370D] bg-[#FFFAEB] border border-[#FEC84B] rounded-sm p-2" data-testid="grn-diagnostics-skipped-items">
                  <strong>Dropped item(s) - not received in SAP:</strong>
                  <ul className="mt-1 space-y-1">
                    {diagnosticsModal.skipped_items.map((s, i) => (
                      <li key={i} data-testid={`grn-diagnostics-skipped-item-${i}`}>{s.item_number}: {s.reason}</li>
                    ))}
                  </ul>
                </div>
              )}
              {diagnosticsModal.movement_issues?.length > 0 && (
                <div className="text-sm text-[#7A1E1E] bg-[#FEF3F2] border border-[#FDA29B] rounded-sm p-2" data-testid="grn-diagnostics-movement-issues">
                  <strong>Goods Receipt posted fine - warehouse movement into place failed:</strong>
                  <ul className="mt-1 space-y-1">
                    {diagnosticsModal.movement_issues.map((m, i) => (
                      <li key={i} data-testid={`grn-diagnostics-movement-issue-${i}`}>{m.product_id}: {m.error}</li>
                    ))}
                  </ul>
                </div>
              )}
              <div>
                <p className="text-xs font-heading font-bold uppercase tracking-wide text-[#667085] mb-1.5">
                  {diagnosticsModal.status === "skipped" ? "What happened before it was skipped" : diagnosticsModal.status === "posted" ? "Events completed for this PO" : "Events completed successfully before the failure"}
                </p>
                {diagnosticsModal.events?.length ? (
                  <ol className="text-sm space-y-1.5" data-testid="grn-diagnostics-events-list">
                    {diagnosticsModal.events.map((e, i) => (
                      <li key={i} className="flex gap-2" data-testid={`grn-diagnostics-event-${i}`}>
                        <span className="text-[#12B76A] font-bold shrink-0">{i + 1}.</span>
                        <span className="text-[#344054]">{e}</span>
                      </li>
                    ))}
                  </ol>
                ) : diagnosticsModal.status === "posted" ? (
                  <p className="text-sm text-[#98A2B3]">No additional diagnostic events were recorded for this PO.</p>
                ) : (
                  <p className="text-sm text-[#98A2B3]">No events were logged before this failure - it likely crashed immediately.</p>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
